import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { authenticatedApiCall } from '../../utils/apiClient';
import { useMsal } from '@azure/msal-react';
import {
  ComposedChart,
  Bar,
  Line,
  LineChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
  LabelList,
  Cell,
  ZIndexLayer,
  DefaultZIndexes,
  usePlotArea,
  useXAxisDomain,
  useYAxisDomain
} from 'recharts';
import BudgetReport from './BudgetReport';
import { canAccessEvmReport } from '../../utils/reportAccess';
import { ListenButton } from '../common/AudioGuide';
import { applyLetterhead } from '../../utils/pdfLetterhead';
import { exportFilename } from '../../utils/nomenclature';
import HelpWithVideoTour from '../Help/HelpWithVideoTour';
import { loadSlicerLayout, saveSlicerLayout, FALLBACK_SLICER } from './EvmSlicerCard';
import FilterDropdown, { AnchoredPopover, FilterCheckList, FilterRadioList, FILTER_CHECK_ROW, pickCountSummary } from './FilterDropdown';
import ActivityWbsTree from './ActivityWbsTree';
import {
  buildWbsOutline,
  filterCollapsedRows,
  collectWbsPaths,
  stripSharedWbsRoot
} from '../../utils/scheduleWbsOutline';
import {
  BUILTIN_SLICERS,
  FALLBACK_ACTUALS_SLICERS,
  EMPTY_ACTUALS_FILTERS,
  DEFAULT_ACTUALS_ON,
  DEFAULT_TOOLBAR_ON,
  SETTINGS_SLICER_IDS,
  FILTER_LAYOUT_VERSION,
  SCOPE_SLICER_LABEL,
  loadVisiblePanels,
  saveVisiblePanels,
  isSlicerVisible,
  toActualsSlicer,
  sortFiltersByLabel
} from './evmSlicerCatalog';
import {
  ETC_FORMULAS,
  DEFAULT_ETC_FORMULA,
  loadEtcFormula,
  saveEtcFormula,
  applyEtcFormula
} from '../../utils/evmForecastFormulas';
import StaffingPlanTab from './StaffingPlanTab';
import SafetyPlanModal from './SafetyPlanModal';
import IndirectExtrasModal from './IndirectExtrasModal';
import SupervisionPlanModal from './SupervisionPlanModal';
import { StaffingHelpBody } from './StaffingPlanHelpModal';
import { loadExtrasCfg, projectMonthKeys } from '../../utils/gcIndirectExtras';
import {
  mergeRemainingWithActuals,
  resourceMatchesFilters,
  familyFromResource,
  supervisionThresholds,
  supervisionRatiosForResources,
  neededAt,
  gfNeededAt,
  normalizeSafetyRule,
  safetyThresholdsUpTo,
  safetyLineLabelCount,
  safetyCountWord,
  safetyRuleLabel,
  safetyCrewValues,
  SAFETY_RULE_EVERY,
  SAFETY_RULE_FIRST_THEN,
  SAFETY_RULE_THREE,
  buildStaffingModel,
  applyStaffingAdjust,
  loadStaffingAdjust,
  loadSuperCfg,
  saveSuperCfg
} from '../../utils/staffingPlan';
import { hoursInGrain, runningHourCumulatives } from '../../utils/evmHistogramGrain';
import { pmiForResourceSelection } from '../../utils/evmFilteredPmi';
import {
  LEGACY_DISCIPLINE_CATALOG_IDS,
  idsFromCurveProject,
  liveMatchesXerSelection,
  mergeP6ProjectLists,
  migrateDisciplineSelection,
  shouldReplaceHistogramPayload
} from '../../utils/evmXerSelection';
import {
  loadLegendBy,
  saveLegendBy,
  DEFAULT_LEGEND_BY,
  LEGEND_DEFAULT_REV,
  resolveJobLegendBy,
  isP6LegendBy,
  needsBackendLegend,
  p6StackByParam,
  resourceColor,
  collectStackItems,
  budgetStackKey,
  earnedStackKey,
  spentStackKey,
  isColumnLegend,
  pickBudgetStackCurve,
  isInternalChartSeries,
  legendItemsWithData,
  legendColor,
  legendPattern,
  loadLegendColors,
  saveLegendColors,
  setLegendPaint,
  DISC_LABELS
} from '../../utils/evmLegend';
import { hatchedBarShape } from './HatchedBarShape';
import {
  ACCT_LEGEND_FIELDS,
  apportionSlices,
  columnLegendFields,
  spentCurveFromRows
} from '../../utils/evmSpentLegend';
import { loadJobViewPrefs, saveJobViewPrefs } from '../../utils/jobViewPrefs';
import { attachEvmIndices, mergeTrackerWithP6Proposal, stopPerformanceLines } from '../../utils/evmPerformanceLines';
import HistogramPdfModal, {
  DEFAULT_PDF_OPTIONS,
  PDF_CHART_STRETCH_MAX,
  PDF_JPEG_QUALITY,
  PDF_SNAP_SCALE,
  loadPdfOptions,
  pdfChartDrawSize,
  pdfPrintsFilters
} from './HistogramPdfModal';
import LegendColorSwatch from './LegendColorSwatch';

const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const ASSUME_LAST_WEEK_KEY = 'evm-assume-last-week-spent';
const WEEK_START_KEY = 'evm-week-start-v2';

function loadWeekStart() {
  try {
    const v = localStorage.getItem(WEEK_START_KEY);
    if (v === 'sunday' || v === 'monday') return v;
  } catch { /* ignore */ }
  return 'sunday';
}

function loadAssumeLastWeek() {
  try {
    const v = localStorage.getItem(ASSUME_LAST_WEEK_KEY);
    if (v == null) return true;
    return v !== 'false';
  } catch {
    return true;
  }
}

const AXIS_LEFT = 4;
const AXIS_RIGHT = 36;
const FILTER_PANE_W = 300;
// XER picker rows are "ID · project name · codes · data date · hours" — give
// them room (the popover is capped at 92vw by FilterDropdown).
const WIDE_PANE_POPOVER = 860;

/** Stable type — a fresh component inside render remounts and snaps dropdowns shut. */
function PaneFilter(props) {
  return (
    <FilterDropdown
      fullWidth
      portal
      align="right"
      popoverWidth={300}
      {...props}
    />
  );
}

function isP6EpsFolder(p) {
  return String(p?.projectFlag || '').toUpperCase() === 'N'
    && !(p.taskCount > 0 || p.remainHours > 0);
}

function p6ProjectLabel(p) {
  if (!p) return '';
  const hours = Number(p.remainHours) || 0;
  const short = String(p.projShortName || '');
  // Project name rides along whenever P6 has one that is not just the ID again.
  const nameHint = p.projName && String(p.projName).trim().toLowerCase() !== short.toLowerCase() ? String(p.projName).trim() : '';
  // Scenario files: package + calendar is the identity; the ID date is filler.
  if (p.scenario) {
    const coded = p.coded && p.codes
      ? [p.codes.party, p.codes.scope, p.codes.status, p.codes.calendar, p.codes.rev].filter(Boolean).join(' · ')
      : '';
    const bits = [short];
    if (nameHint) bits.push(nameHint);
    bits.push(coded || (p.package ? `${p.package} · ${p.scenario}` : p.scenario));
    // Re-imports leave two copies of the same package+calendar; the date is
    // the only thing that tells 26-LG-0174-1 from 26-LG-0174-260823.
    if (p.dataDate) bits.push(String(p.dataDate).slice(0, 10));
    if (hours) bits.push(`${hours.toLocaleString()} h`);
    else if (!(p.taskCount > 0)) bits.push('empty');
    return bits.join(' · ');
  }
  const kind = isP6EpsFolder(p) ? 'EPS folder'
    : p.kind === 'proposal' ? 'Budget'
    : p.kind === 'working' ? 'Current'
    : p.kind === 'other' ? 'XER'
    : 'Weekly';
  const date = p.dataDate || (p.submitDate && String(p.submitDate).length === 8
    ? `${p.submitDate.slice(0, 4)}-${p.submitDate.slice(4, 6)}-${p.submitDate.slice(6, 8)}`
    : '');
  // Coded projects (0163 onward): the five P6 project codes are the identity —
  // "LGL · OWN · CUR · 5x08 · Precon1" says more than Current/Weekly.
  const codeTxt = p.coded && p.codes
    ? [p.codes.party, p.codes.scope, p.codes.status, p.codes.calendar, p.codes.rev].filter(Boolean).join(' · ')
    : '';
  const bits = [short, codeTxt || kind];
  if (nameHint) bits.push(nameHint);
  if (date) bits.push(date);
  if (hours) bits.push(`${hours.toLocaleString()} h`);
  else if (!(p.taskCount > 0)) bits.push('empty');
  return bits.join(' · ');
}

function scenarioTokenWeight(token) {
  const m = String(token || '').match(/^(\d{1,2})x(\d{1,2})$/i);
  return m ? Number(m[1]) * Number(m[2]) : Number.MAX_SAFE_INTEGER;
}

function P6WeeklyPicker({ projects, selectedIds, onChange, selectStyle, autoSummary, autoOptionLabel, onOpen, embedded = false }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const boxRef = useRef(null);
  useEffect(() => {
    const onDoc = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);
  const selected = new Set((selectedIds || []).map(String));
  const toggle = (id) => {
    const key = String(id);
    const next = selected.has(key)
      ? (selectedIds || []).filter((x) => String(x) !== key)
      : [...(selectedIds || []), key];
    onChange(next);
  };
  const list = projects || [];
  const q = query.trim().toLowerCase();
  const matchesQuery = (p) => {
    if (!q) return true;
    return [p.projShortName, p.projName, p.kind, p.package, p.scenario, p6ProjectLabel(p)]
      .filter(Boolean)
      .some((x) => String(x).toLowerCase().includes(q));
  };
  const picked = (selectedIds || [])
    .map((id) => list.find((p) => String(p.projId) === String(id)))
    .filter(Boolean);

  const scenarioFiles = list.filter((p) => p.scenario && matchesQuery(p));
  const scenarioTokens = [...new Set(list.filter((p) => p.scenario).map((p) => String(p.scenario).toLowerCase()))]
    .sort((a, b) => scenarioTokenWeight(a) - scenarioTokenWeight(b));
  const packageNames = [...new Set(scenarioFiles.map((p) => p.package || '?'))].sort();
  const nonScenario = list.filter((p) => !p.scenario && matchesQuery(p));
  const xerFiles = nonScenario.filter((p) => !isP6EpsFolder(p));
  const epsFolders = nonScenario.filter((p) => isP6EpsFolder(p));

  // One click = that calendar for every package (the program view).
  const applyScenario = (tok) => {
    const byPkg = new Map();
    scenarioFiles
      .filter((p) => String(p.scenario).toLowerCase() === tok)
      .forEach((p) => {
        const k = p.package || p.projShortName;
        if (!byPkg.has(k)) byPkg.set(k, p);
      });
    onChange([...byPkg.values()].map((p) => String(p.projId)));
  };

  const activeScenario = picked.length && picked.every((p) => p.scenario)
    && new Set(picked.map((p) => String(p.scenario).toLowerCase())).size === 1
    ? String(picked[0].scenario).toLowerCase()
    : null;

  // Two calendars of the SAME package double-count that package's scope.
  const dupPackages = (() => {
    const seen = new Map();
    picked.forEach((p) => {
      if (!p.scenario) return;
      const k = p.package || p.projShortName;
      seen.set(k, (seen.get(k) || 0) + 1);
    });
    return [...seen.entries()].filter(([, n]) => n > 1).map(([k]) => k);
  })();

  const summary = !picked.length
    ? (autoSummary || 'Auto — Current or latest weekly')
    : activeScenario && picked.length > 1
      ? `Program ${activeScenario} — ${picked.length} packages`
      : picked.length === 1
        ? p6ProjectLabel(picked[0])
        : `${picked.length} XERs consolidated`;
  const listBody = (
        <div style={embedded ? undefined : {
          position: 'absolute', zIndex: 50, top: '100%', left: 0, marginTop: 4,
          width: '100%', minWidth: 0, maxWidth: '100%',
          maxHeight: 420, overflowY: 'auto',
          background: '#fff', border: '1px solid #cbd5e1', borderRadius: 8,
          boxShadow: '0 8px 24px rgba(15,23,42,0.12)', padding: 6
        }}
        >
          <label style={{ display: 'flex', gap: 6, alignItems: 'center', padding: '4px 6px', fontSize: 12, fontWeight: 700, color: '#0f172a' }}>
            <input type="checkbox" checked={!picked.length} onChange={() => onChange([])} />
            {autoOptionLabel || (scenarioTokens.length
              ? 'Auto — latest weekly, else one scenario per package'
              : 'Auto — latest weekly with activities')}
          </label>
          {list.length > 3 && (
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search XERs…"
              style={{
                width: '100%', boxSizing: 'border-box', height: 26, margin: '2px 0 6px',
                border: '1px solid #cbd5e1', borderRadius: 4, padding: '0 8px', fontSize: 12
              }}
            />
          )}
          {scenarioTokens.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', padding: '4px 6px 6px', borderBottom: '1px solid #f1f5f9' }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: '#64748b' }}>Scenario:</span>
              {scenarioTokens.map((tok) => (
                <button
                  key={tok}
                  type="button"
                  onClick={() => applyScenario(tok)}
                  title={`Check ${tok} for every package — the full program on that calendar`}
                  style={{
                    border: activeScenario === tok ? '1px solid #0f766e' : '1px solid #cbd5e1',
                    background: activeScenario === tok ? '#f0fdfa' : '#f8fafc',
                    color: activeScenario === tok ? '#0f766e' : '#334155',
                    fontWeight: activeScenario === tok ? 800 : 600,
                    borderRadius: 999, fontSize: 11, padding: '2px 10px', cursor: 'pointer'
                  }}
                >
                  {tok}
                </button>
              ))}
            </div>
          )}
          {dupPackages.length > 0 && (
            <div style={{ padding: '4px 6px', fontSize: 11, fontWeight: 700, color: '#b91c1c', lineHeight: 1.35 }}>
              Package {dupPackages.join(', ')} is checked on two calendars — its hours are double-counted. Pick one calendar per package.
            </div>
          )}
          {packageNames.map((pkg) => (
            <div key={pkg}>
              <div style={{ padding: '6px 6px 2px', fontSize: 10, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                Package {pkg}
              </div>
              {scenarioFiles
                .filter((p) => (p.package || '?') === pkg)
                .sort((a, b) => scenarioTokenWeight(a.scenario) - scenarioTokenWeight(b.scenario))
                .map((p) => {
                  const empty = !(p.taskCount > 0 || p.remainHours > 0);
                  return (
                    <label
                      key={p.projId}
                      title={p6ProjectLabel(p)}
                      style={{
                        display: 'flex', gap: 6, alignItems: 'center',
                        padding: '4px 6px 4px 14px', fontSize: 12,
                        color: empty ? '#94a3b8' : '#334155', cursor: 'pointer'
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selected.has(String(p.projId))}
                        onChange={() => toggle(p.projId)}
                      />
                      <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p6ProjectLabel(p)}</span>
                    </label>
                  );
                })}
            </div>
          ))}
          {scenarioFiles.length > 0 && xerFiles.length > 0 && (
            <div style={{ padding: '6px 6px 2px', fontSize: 10, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.4 }}>
              XERs
            </div>
          )}
          {xerFiles.map((p) => {
            const empty = !(p.taskCount > 0 || p.remainHours > 0);
            return (
              <label
                key={p.projId}
                title={p6ProjectLabel(p)}
                style={{
                  display: 'flex', gap: 6, alignItems: 'center',
                  padding: '4px 6px', fontSize: 12,
                  color: empty ? '#94a3b8' : '#334155', cursor: 'pointer'
                }}
              >
                <input
                  type="checkbox"
                  checked={selected.has(String(p.projId))}
                  onChange={() => toggle(p.projId)}
                />
                <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p6ProjectLabel(p)}</span>
              </label>
            );
          })}
          {epsFolders.length > 0 && (
            <div style={{ padding: '6px 6px 2px', fontSize: 10, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.4 }}>
              EPS folders
            </div>
          )}
          {epsFolders.map((p) => (
            <label
              key={p.projId}
              title={p6ProjectLabel(p)}
              style={{
                display: 'flex', gap: 6, alignItems: 'center',
                padding: '4px 6px', fontSize: 12,
                color: '#94a3b8', cursor: 'pointer'
              }}
            >
              <input
                type="checkbox"
                checked={selected.has(String(p.projId))}
                onChange={() => toggle(p.projId)}
              />
              <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p6ProjectLabel(p)}</span>
            </label>
          ))}
          {q && !scenarioFiles.length && !xerFiles.length && !epsFolders.length && (
            <div style={{ padding: '6px', fontSize: 12, color: '#94a3b8' }}>No XER matches “{query}”.</div>
          )}
          <div style={{ padding: '6px 6px 4px', fontSize: 11, color: '#64748b', lineHeight: 1.35 }}>
            {list.length} file{list.length === 1 ? '' : 's'} in P6
            {scenarioTokens.length
              ? ' — a scenario chip checks that calendar for every package. One calendar per package, or hours double-count.'
              : ' — check two or more to add every file’s hours into one histogram. Same rule on every job. EPS folders are not schedules.'}
          </div>
        </div>
  );
  if (embedded) return listBody;
  return (
    <div ref={boxRef} style={{ position: 'relative', minWidth: 0, width: '100%' }}>
      <button
        type="button"
        onClick={() => setOpen((v) => {
          const next = !v;
          if (next && typeof onOpen === 'function') onOpen();
          return next;
        })}
        style={{ ...selectStyle, width: '100%', maxWidth: '100%', boxSizing: 'border-box', textAlign: 'left', cursor: 'pointer', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
      >
        {summary}
      </button>
      {open && listBody}
    </div>
  );
}

function addDaysYmd(ymd, days) {
  const d = parseChartDate(ymd);
  if (!d) return ymd;
  d.setDate(d.getDate() + days);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function activityOverlapsWeek(a, week) {
  if (!week || !a?.start || !a?.finish) return false;
  return a.start <= addDaysYmd(week, 6) && a.finish >= week;
}

function parseChartDate(dateStr) {
  if (!dateStr) return null;
  const d = new Date(`${String(dateStr).slice(0, 10)}T12:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Same x as the bar — year / month / Sunday day. */
function formatBarTotal(v, unitMode) {
  const n = Number(v);
  if (!Number.isFinite(n) || Math.abs(n) < 0.05) return '';
  if (unitMode === 'manweeks') return Math.round(n).toLocaleString();
  return n >= 100 ? Math.round(n).toLocaleString() : String(Math.round(n * 10) / 10);
}

/** Custom legend — click the name to spotlight (fades others). Click the swatch to pick a colour. Never hides a series. */
function HistogramLegend({
  payload,
  spotlight,
  onSpotlight,
  onColorChange,
  onPatternChange,
  hideColorPickers = false,
  hasColorOverrides = false,
  onResetColors,
  direction = 'row',
  title = null
}) {
  const items = (payload || []).filter((item) => {
    if (!item) return false;
    const name = String(item.value ?? item.dataKey ?? item.id ?? '');
    return !isInternalChartSeries(name);
  });
  if (!items.length) return null;
  const column = direction === 'column';
  return (
    <div
      className="recharts-legend-wrapper"
      style={column
        ? { display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'stretch', maxWidth: '100%' }
        : {
          display: 'flex',
          flexWrap: 'wrap',
          gap: '4px 10px',
          justifyContent: 'center',
          padding: '8px 2px 0',
          maxWidth: '100%'
        }}
    >
      {column && title && (
        <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 2 }}>
          {title}
        </div>
      )}
      {column && (
        <div style={{ fontSize: 10, color: '#94a3b8', marginBottom: 6, lineHeight: 1.35 }}>
          Click a name to spotlight · click the square for colour or hash pattern
        </div>
      )}
      {items.map((item) => {
        const key = item.id || item.dataKey;
        const dim = Boolean(spotlight && spotlight !== key);
        const isLine = item.type === 'line';
        const active = spotlight === key;
        return (
          <div
            key={String(key)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: column ? 'flex-start' : 'center',
              gap: 6,
              border: active ? '1px solid #0f766e' : '1px solid transparent',
              borderRadius: 6,
              background: active ? '#f0fdfa' : 'transparent',
              padding: '2px 6px',
              maxWidth: '100%',
              opacity: dim ? 0.42 : 1
            }}
          >
            <LegendColorSwatch
              color={item.color}
              pattern={item.pattern || 'solid'}
              isLine={isLine}
              dashed={item.dashed}
              hidePicker={hideColorPickers}
              onChange={onColorChange ? (hex) => onColorChange(key, hex) : undefined}
              onPatternChange={onPatternChange ? (next) => onPatternChange(key, next, item.color) : undefined}
            />
            <button
              type="button"
              title={active ? 'Click to show every series again' : 'Click to spotlight — others fade'}
              onClick={() => onSpotlight((prev) => (prev === key ? null : key))}
              style={{
                border: 'none',
                background: 'none',
                padding: 0,
                cursor: 'pointer',
                textAlign: 'left',
                fontSize: 12,
                fontWeight: active ? 700 : 500,
                color: dim ? '#94a3b8' : '#334155',
                minWidth: 0,
                overflow: 'hidden',
                whiteSpace: column ? 'nowrap' : 'normal',
                textOverflow: 'ellipsis'
              }}
            >
              {item.value}
            </button>
          </div>
        );
      })}
      {column && hasColorOverrides && typeof onResetColors === 'function' && (
        <button
          type="button"
          onClick={onResetColors}
          style={{
            marginTop: 4,
            border: 'none',
            background: 'none',
            padding: 0,
            textAlign: 'left',
            fontSize: 10,
            fontWeight: 700,
            color: '#0f766e',
            cursor: 'pointer'
          }}
        >
          Reset colours & patterns
        </button>
      )}
    </div>
  );
}

function HistogramTimeTick({ x, y, payload, index }) {
  const d = parseChartDate(payload?.value);
  if (!d || x == null) return null;
  const day = d.getDate();
  const firstOfMonth = day <= 7;
  const showYear = index === 0 || (firstOfMonth && d.getMonth() === 0);
  const showMonth = index === 0 || firstOfMonth;
  return (
    <g transform={`translate(${x},${y})`}>
      <text dy={12} textAnchor="middle" fill="#64748b" fontSize={10}>
        {day}
      </text>
      <text dy={26} textAnchor="middle" fill="#475569" fontSize={10} fontWeight={600}>
        {showMonth ? MONTH_SHORT[d.getMonth()] : ''}
      </text>
      <text dy={40} textAnchor="middle" fill="#0f172a" fontSize={11} fontWeight={700}>
        {showYear ? d.getFullYear() : ''}
      </text>
    </g>
  );
}

/**
 * Classic EVM S-curve marks on the histogram (the textbook "EVM S-curve"
 * picture): data date, BAC and EAC lines, the AC→EAC projection, SV / CV
 * brackets at the data date, ETC / VAC brackets at the end, plus the long
 * "work remaining" (BAC−EV) and "funds remaining" (BAC−AC) brackets.
 * Every short bracket gets a balloon callout with a leader arrow so the
 * numbers stay readable on top of a busy histogram.
 */
const EVM_BALLOON_H = 20;
const EVM_BALLOON_FONT = 11;
function evmBalloonWidth(text) {
  return Math.round(String(text).length * (EVM_BALLOON_FONT * 0.6) + 16);
}
const EVM_LABEL_GAP = 5;
function evmRectsOverlap(a, b, gap = EVM_LABEL_GAP) {
  return a.x < b.x + b.w + gap && a.x + a.w + gap > b.x && a.y < b.y + b.h + gap && a.y + a.h + gap > b.y;
}
/**
 * Place floating labels so none of them overlap each other or the fixed
 * obstacles (pills, rotated bracket labels). Each balloon tries its preferred
 * spot first, then slides up/down in small steps, then shifts sideways away
 * from its anchor; the first free spot wins. Balloons are placed in order of
 * preferred y so neighbours stack predictably.
 */
function layoutEvmBalloons(items, obstacles, bounds) {
  const placed = [...obstacles];
  const fits = (r) => r.y >= bounds.top && r.y + r.h <= bounds.bottom
    && r.x >= bounds.left && r.x + r.w <= bounds.right
    && !placed.some((o) => evmRectsOverlap(r, o));
  const sorted = [...items].sort((a, b) => a.prefY - b.prefY);
  sorted.forEach((it) => {
    const h = EVM_BALLOON_H;
    const prefY = it.prefY - h / 2;
    const xSteps = [0, 1, 2, 3].map((k) => it.x + k * it.shiftDir * (it.w * 0.55 + 12));
    let spot = null;
    for (const x of xSteps) {
      for (let dy = 0; dy <= 360 && !spot; dy += 6) {
        const cands = dy === 0 ? [prefY] : [prefY - dy, prefY + dy];
        for (const y of cands) {
          const r = { x, y, w: it.w, h };
          if (fits(r)) { spot = r; break; }
        }
      }
      if (spot) break;
    }
    if (!spot) {
      spot = { x: it.x, y: Math.min(Math.max(prefY, bounds.top), bounds.bottom - h), w: it.w, h };
    }
    it.x = spot.x;
    it.y = spot.y;
    placed.push(spot);
  });
  return sorted;
}
function evmArrowId(color) {
  return `evm-arrow-${String(color).replace(/[^a-z0-9]/gi, '')}`;
}
function EvmCurveMarks({ rows, bandScale, dataDate, bac, eac, etc, vac }) {
  const plot = usePlotArea();
  const xDomain = useXAxisDomain();
  const yDomain = useYAxisDomain('right');
  if (!plot?.width || !Array.isArray(xDomain) || xDomain.length === 0 || !Array.isArray(yDomain) || !rows?.length) {
    return null;
  }
  const yLo = Number(yDomain[0]);
  const yHi = Number(yDomain[1]);
  if (!Number.isFinite(yLo) || !Number.isFinite(yHi) || yHi === yLo) return null;

  const lastRowWhere = (pred) => {
    for (let i = rows.length - 1; i >= 0; i -= 1) {
      if (pred(rows[i])) return rows[i];
    }
    return null;
  };
  const svRow = lastRowWhere((r) => r.evm_sv != null);
  const cvRow = lastRowWhere((r) => r.evm_cv != null);
  const ddRow = (dataDate && rows.find((r) => String(r.date) === String(dataDate)))
    || svRow
    || cvRow;
  if (!ddRow) return null;

  const xOfDate = (date) => {
    const i = xDomain.findIndex((d) => String(d) === String(date));
    if (i < 0) return null;
    return bandScale || xDomain.length <= 1
      ? plot.x + (i + 0.5) * (plot.width / Math.max(xDomain.length, 1))
      : plot.x + (i / (xDomain.length - 1)) * plot.width;
  };
  const yOf = (v) => {
    if (v == null) return null;
    const n = Number(v);
    if (!Number.isFinite(n)) return null;
    const t = (n - yLo) / (yHi - yLo);
    return plot.y + plot.height * (1 - t);
  };
  const fmtSigned = (n) => {
    const v = Math.round(Number(n));
    if (!Number.isFinite(v)) return '';
    return `${v > 0 ? '+' : v < 0 ? '−' : ''}${Math.abs(v).toLocaleString()}`;
  };
  const fmtAbs = (n) => {
    const v = Math.round(Number(n));
    return Number.isFinite(v) ? v.toLocaleString() : '';
  };
  const fmtDate = (s) => {
    const d = parseChartDate(s);
    return d ? `${d.getDate()} ${MONTH_SHORT[d.getMonth()]} ${d.getFullYear()}` : String(s || '');
  };

  const xDd = xOfDate(ddRow.date);
  const xStart = plot.x;
  const xEnd = plot.x + plot.width;
  const yTop = plot.y;
  const yBottom = plot.y + plot.height;

  // Levels at the data date. EV from the last earned week; AC from the last
  // spent week (Maestro usually runs a week behind P6).
  const evLevel = svRow ? Number(svRow.earned_cumulative) : null;
  const acRaw = cvRow ? Number(cvRow.actual_cumulative) : (ddRow.actual_cumulative == null ? null : Number(ddRow.actual_cumulative));
  const hasAc = Number.isFinite(acRaw) && acRaw > 0;
  const acLevel = hasAc ? acRaw : 0;
  // BAC from the figures card; if that is not loaded yet, the budget curve's
  // plateau is the same number.
  const bacCard = Number(bac);
  const bacCurve = rows.reduce((m, r) => Math.max(m, Number(r.budget_cumulative) || 0), 0);
  const bacNum = Number.isFinite(bacCard) && bacCard > 0 ? bacCard : bacCurve;
  const hasBac = Number.isFinite(bacNum) && bacNum > 0;
  // EAC from the selected ETC formula. With no spent hours BAC ÷ CPI
  // collapses, so fall back to where the forecast curve ends (EV + remaining).
  // The only sanity floor is AC: a job cannot finish for less than it has
  // already spent. EAC *can* sit below EV when CPI > 1 (underrun) — that is
  // the whole point of the picture, so never reject the card's FAC for that.
  const forecastEnd = lastRowWhere((r) => r.remaining_cumulative != null);
  const facNum = Number(eac);
  const facSane = hasAc && Number.isFinite(facNum) && facNum >= acLevel;
  const eacNum = facSane
    ? facNum
    : (forecastEnd ? Number(forecastEnd.remaining_cumulative) : null);
  const hasEac = Number.isFinite(eacNum) && eacNum > 0;
  const eacFromCurve = hasEac && !facSane;
  const etcNum = hasEac ? (facSane && etc != null ? Number(etc) : eacNum - acLevel) : null;
  const vacNum = hasEac && hasBac ? (facSane && vac != null ? Number(vac) : bacNum - eacNum) : null;

  const yBac = hasBac ? yOf(bacNum) : null;
  const yEac = hasEac ? yOf(eacNum) : null;
  const yEv = yOf(evLevel);
  const yAc = yOf(acLevel);
  const xSv = svRow ? xOfDate(svRow.date) : null;
  const xCv = cvRow ? xOfDate(cvRow.date) : null;
  const yEvSv = svRow ? yOf(svRow.earned_cumulative) : null;
  const yPvSv = svRow ? yOf(svRow.budget_cumulative) : null;
  const yEvCv = cvRow ? yOf(cvRow.earned_cumulative) : null;
  const yAcCv = cvRow ? yOf(cvRow.actual_cumulative) : null;

  const C = {
    dd: '#0f172a', bac: '#1d4ed8', eac: '#b91c1c', sv: '#0369a1', cv: '#c2410c',
    etc: '#7c3aed', good: '#15803d', bad: '#b91c1c', work: '#15803d', funds: '#b91c1c'
  };
  const halo = { paintOrder: 'stroke', stroke: '#fff', strokeWidth: 3 };

  /** Vertical bracket; returns its midpoint so a balloon can point at it. */
  const bracketGeom = (x, y1, y2) => {
    if (x == null || y1 == null || y2 == null || Math.abs(y1 - y2) < 1) return null;
    return { x, y1, y2, mid: (y1 + y2) / 2 };
  };
  const drawBracket = (g, color, side, width = 2) => {
    if (!g) return null;
    const tick = side === 'left' ? -5 : 5;
    return (
      <g>
        <line x1={g.x} x2={g.x} y1={g.y1} y2={g.y2} stroke={color} strokeWidth={width} />
        <line x1={g.x} x2={g.x + tick} y1={g.y1} y2={g.y1} stroke={color} strokeWidth={width} />
        <line x1={g.x} x2={g.x + tick} y1={g.y2} y2={g.y2} stroke={color} strokeWidth={width} />
      </g>
    );
  };
  /** Label written along a long bracket (reads top-to-bottom). */
  const alongLabel = (g, color, text, side) => {
    if (!g || Math.abs(g.y1 - g.y2) < 70) return null;
    const x = g.x + (side === 'left' ? -8 : 8);
    return (
      <text
        x={x}
        y={g.mid}
        transform={`rotate(-90 ${x} ${g.mid})`}
        textAnchor="middle"
        dominantBaseline="middle"
        fill={color}
        fontSize={10}
        fontWeight={700}
        style={halo}
      >
        {text}
      </text>
    );
  };
  const hLine = (y, color, { dashed = false, x1 = xStart } = {}) => {
    if (y == null) return null;
    return <line x1={x1} x2={xEnd} y1={y} y2={y} stroke={color} strokeWidth={1.5} strokeDasharray={dashed ? '6 4' : undefined} />;
  };
  const guide = (y, color) => {
    if (y == null || xDd == null) return null;
    return <line x1={xDd} x2={xEnd} y1={y} y2={y} stroke={color} strokeWidth={1} strokeDasharray="2 4" strokeOpacity={0.6} />;
  };
  /** Pill label sitting on a horizontal line. */
  const pillRect = (y, text, { at = 'left', below = false, inset = 6 } = {}) => {
    if (y == null) return null;
    const w = evmBalloonWidth(text);
    const x = at === 'left' ? xStart + inset : xEnd - inset - w;
    const top = below ? y + 4 : y - EVM_BALLOON_H - 4;
    return { x, y: top, w, h: EVM_BALLOON_H };
  };
  const drawPill = (r, color, text) => {
    if (!r) return null;
    return (
      <g>
        <rect x={r.x} y={r.y} width={r.w} height={r.h} rx={5} fill={color} />
        <text x={r.x + r.w / 2} y={r.y + r.h / 2 + 0.5} dominantBaseline="middle" textAnchor="middle" fill="#fff" fontSize={EVM_BALLOON_FONT} fontWeight={700}>
          {text}
        </text>
      </g>
    );
  };
  /** Footprint of a rotated along-bracket label, for collision avoidance. */
  const alongRect = (g, side) => {
    if (!g || Math.abs(g.y1 - g.y2) < 70) return null;
    const x = g.x + (side === 'left' ? -8 : 8);
    return { x: x - 7, y: Math.min(g.y1, g.y2), w: 14, h: Math.abs(g.y1 - g.y2) };
  };

  // ---- geometry -----------------------------------------------------------
  const svG = svRow ? bracketGeom(xSv != null ? xSv - 8 : null, yPvSv, yEvSv) : null;
  const cvG = cvRow ? bracketGeom(xCv != null ? xCv + 8 : null, yAcCv, yEvCv) : null;
  const eacDistinct = yEac != null && (yBac == null || Math.abs(yEac - yBac) > 1);
  const etcG = hasEac && etcNum != null ? bracketGeom(xEnd - 40, yAc, yEac) : null;
  const vacG = eacDistinct && vacNum != null ? bracketGeom(xEnd - 18, yBac, yEac) : null;
  const workRemaining = hasBac && evLevel != null ? bacNum - evLevel : null;
  const fundsRemaining = hasBac && hasAc ? bacNum - acLevel : null;
  const workG = workRemaining != null && xDd != null ? bracketGeom(xDd + 16, yEv, yBac) : null;
  const fundsG = fundsRemaining != null && xDd != null ? bracketGeom(xDd + 34, yAc, yBac) : null;

  // ---- fixed labels (pills, rotated bracket labels) -----------------------
  const ddText = `Data date · ${fmtDate(ddRow.date)}`;
  const ddW = evmBalloonWidth(ddText);
  const ddX = xDd != null ? Math.max(xStart + 2, Math.min(xDd - ddW / 2, xEnd - 2 - ddW)) : null;
  const ddRect = xDd != null ? { x: ddX, y: yTop + 2, w: ddW, h: EVM_BALLOON_H } : null;
  const bacText = `BAC ${fmtAbs(bacNum)} h`;
  const bacRect = hasBac
    ? pillRect(yBac, bacText, { at: 'left', below: yBac != null && yBac - yTop < EVM_BALLOON_H + 6 })
    : null;
  const eacText = `${eacFromCurve ? 'EAC (forecast curve)' : 'EAC'} ${fmtAbs(eacNum)} h`;
  const eacRect = eacDistinct
    ? pillRect(yEac, eacText, {
      at: 'right',
      inset: 54, // clear of the ETC / VAC brackets hugging the right edge
      below: eacNum > bacNum || (yEac != null && yEac - yTop < EVM_BALLOON_H + 6)
    })
    : null;
  const workText = `Work remaining ${fmtAbs(workRemaining)} h`;
  const fundsText = `Funds remaining ${fmtAbs(fundsRemaining)} h`;
  const obstacles = [
    ddRect, bacRect, eacRect,
    alongRect(workG, 'right'), alongRect(fundsG, 'right'),
    // keep balloons off the bracket stems themselves
    svG && { x: svG.x - 6, y: Math.min(svG.y1, svG.y2), w: 12, h: Math.abs(svG.y1 - svG.y2) },
    cvG && { x: cvG.x - 6, y: Math.min(cvG.y1, cvG.y2), w: 12, h: Math.abs(cvG.y1 - cvG.y2) },
    etcG && { x: etcG.x - 6, y: Math.min(etcG.y1, etcG.y2), w: 12, h: Math.abs(etcG.y1 - etcG.y2) },
    vacG && { x: vacG.x - 6, y: Math.min(vacG.y1, vacG.y2), w: 12, h: Math.abs(vacG.y1 - vacG.y2) }
  ].filter(Boolean);

  // ---- balloons -----------------------------------------------------------
  // Each balloon starts well clear of its bracket (so the leader arrow is
  // readable) and the placer slides it until it touches nothing else.
  const BALLOON_STANDOFF = 56;
  const balloons = [];
  const addBalloon = (id, color, text, g, column) => {
    if (!g) return;
    balloons.push({ id, color, text, w: evmBalloonWidth(text), ax: g.x, ay: g.mid, prefY: g.mid, column });
  };
  addBalloon('sv', C.sv, `SV ${fmtSigned(svRow?.evm_sv)} h`, svG, 'dd');
  addBalloon('cv', C.cv, `CV ${fmtSigned(cvRow?.evm_cv)} h`, cvG, 'dd');
  addBalloon('etc', C.etc, `ETC ${fmtAbs(etcNum)} h`, etcG, 'end');
  if (vacG) addBalloon('vac', vacNum >= 0 ? C.good : C.bad, `VAC ${fmtSigned(vacNum)} h`, vacG, 'end');

  // Data-date balloons sit left of the line when there is room, else right
  // of the long "remaining" brackets. End balloons sit left of the ETC/VAC
  // brackets and the EAC pill.
  const ddMaxW = balloons.filter((b) => b.column === 'dd').reduce((m, b) => Math.max(m, b.w), 0);
  const ddLeftRoom = xDd != null && xDd - BALLOON_STANDOFF - ddMaxW >= xStart + 4;
  balloons.forEach((b) => {
    if (b.column === 'dd') {
      if (ddLeftRoom) {
        b.x = xDd - BALLOON_STANDOFF - b.w;
        b.shiftDir = -1;
      } else {
        b.x = (xDd ?? xStart) + BALLOON_STANDOFF + 20;
        b.shiftDir = 1;
      }
    } else {
      b.x = xEnd - BALLOON_STANDOFF - 40 - b.w;
      b.shiftDir = -1;
    }
    b.x = Math.max(xStart + 2, Math.min(b.x, xEnd - 2 - b.w));
  });
  layoutEvmBalloons(balloons, obstacles, {
    top: yTop + 2, bottom: yBottom - 2, left: xStart + 2, right: xEnd - 2
  });
  const colors = Array.from(new Set(balloons.map((b) => b.color)));

  const drawBalloon = (b) => {
    const cx = b.x + b.w / 2;
    const cy = b.y + EVM_BALLOON_H / 2;
    // Leader leaves from the side of the balloon that faces the anchor.
    let lx = cx;
    let ly = cy;
    if (b.ax > b.x + b.w) lx = b.x + b.w;
    else if (b.ax < b.x) lx = b.x;
    else if (b.ay > cy) ly = b.y + EVM_BALLOON_H;
    else ly = b.y;
    if (lx !== cx) ly = Math.min(Math.max(b.ay, b.y + 4), b.y + EVM_BALLOON_H - 4);
    return (
      <g key={b.id}>
        <line x1={lx} y1={ly} x2={b.ax} y2={b.ay} stroke={b.color} strokeWidth={1.3} markerEnd={`url(#${evmArrowId(b.color)})`} />
        <rect x={b.x} y={b.y} width={b.w} height={EVM_BALLOON_H} rx={6} fill="#fff" fillOpacity={0.96} stroke={b.color} strokeWidth={1.4} />
        <text x={cx} y={cy + 0.5} dominantBaseline="middle" textAnchor="middle" fill={b.color} fontSize={EVM_BALLOON_FONT} fontWeight={700}>
          {b.text}
        </text>
      </g>
    );
  };

  return (
    <g>
      <defs>
        {colors.map((col) => (
          <marker key={col} id={evmArrowId(col)} viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto">
            <path d="M0,0 L10,5 L0,10 z" fill={col} />
          </marker>
        ))}
      </defs>
      {guide(yEv, C.good)}
      {hasAc && guide(yAc, C.bad)}
      {hLine(yBac, C.bac)}
      {eacDistinct && hLine(yEac, C.eac, { dashed: true, x1: xDd ?? xStart })}
      {hasEac && xDd != null && yAc != null && yEac != null && (
        <line x1={xDd} x2={xEnd} y1={yAc} y2={yEac} stroke={C.eac} strokeWidth={1.5} strokeDasharray="3 4" strokeOpacity={0.8} />
      )}
      {xDd != null && (
        <g>
          <line x1={xDd} x2={xDd} y1={yTop} y2={yBottom} stroke={C.dd} strokeWidth={1.5} strokeDasharray="6 4" />
          {drawPill(ddRect, C.dd, ddText)}
        </g>
      )}
      {drawPill(bacRect, C.bac, bacText)}
      {drawPill(eacRect, C.eac, eacText)}
      {drawBracket(workG, C.work, 'right', 1.5)}
      {alongLabel(workG, C.work, workText, 'right')}
      {drawBracket(fundsG, C.funds, 'right', 1.5)}
      {alongLabel(fundsG, C.funds, fundsText, 'right')}
      {drawBracket(svG, C.sv, 'left')}
      {drawBracket(cvG, C.cv, 'right')}
      {drawBracket(etcG, C.etc, 'left')}
      {drawBracket(vacG, vacNum >= 0 ? C.good : C.bad, 'left')}
      {balloons.map(drawBalloon)}
    </g>
  );
}

/** Y scale that stays put while the histogram scrolls sideways. */

function ScaleGutter({ side, width, height, domain, tickFormatter }) {
  return (
    <ComposedChart
      width={width}
      height={height}
      data={[{ date: '' }]}
      margin={{ top: 22, right: 0, left: 0, bottom: 8 }}
    >
      <XAxis dataKey="date" height={52} tick={false} axisLine={false} tickLine={false} />
      <YAxis
        width={width}
        orientation={side === 'right' ? 'right' : 'left'}
        domain={domain}
        allowDataOverflow
        tickFormatter={tickFormatter}
        tick={{ fontSize: 11 }}
        stroke="#6b7280"
      />
    </ComposedChart>
  );
}

const SCROLL_CATEGORY_PX = 36;
const LEGEND_PLACES = [
  { id: 'top', label: 'Top' },
  { id: 'right', label: 'Right' },
  { id: 'bottom', label: 'Bottom' },
  { id: 'left', label: 'Left' }
];

async function loadImageEl(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

const GAUGE_COLORS = { green: '#16a34a', yellow: '#ca8a04', red: '#dc2626', grey: '#9ca3af' };

const HPW_OPTIONS = [40, 44, 48, 50, 54, 56, 60];
const HPW_CREW_LABEL = { 40: '4x10', 50: '5x10', 60: '6x10' };
function hpwOptionLabel(h) {
  return HPW_CREW_LABEL[h] ? `${h} · ${HPW_CREW_LABEL[h]}` : `${h} h/wk`;
}

/** Leftover catalog — only used when the job has no Discipline UDF. */
const CALENDAR_DISCIPLINES = [
  'piping', 'mechanical', 'structural', 'hvac', 'ducts',
  'boilermaker', 'vessels', 'electrical', 'civil', 'indirect'
];

function whatIfParentKey(disc) {
  return String(disc || '').trim().toLowerCase()
    .replace(/\s+extra$/, '')
    .replace(/\s+additional\s+scope$/, '');
}

/** Raised fist — Budget Hours Baseline entry point */
const FistIcon = ({ size = 20, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M8.5 13.5V8.25a1.25 1.25 0 0 1 2.5 0V12M11 12V6.75a1.25 1.25 0 0 1 2.5 0V12M13.5 12V7.5a1.25 1.25 0 0 1 2.5 0V13M16 13.25v-2a1.25 1.25 0 0 1 2.4.5c.1.4.1.85 0 1.35l-1.6 6.1A3.5 3.5 0 0 1 13.4 21H9.2a3.5 3.5 0 0 1-3.35-2.45L4.5 14.5A2.25 2.25 0 0 1 6.6 11.5H8.5"
      stroke={color}
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    />
    <path
      d="M8.5 13.5c0 0-.2-2.2.8-3.2"
      stroke={color}
      strokeWidth="1.75"
      strokeLinecap="round"
      opacity="0.35"
    />
  </svg>
);

/** Semi-circle KPI gauge (PBIX-style) */
const KpiGauge = ({ label, value, colorKey }) => {
  const v = value == null || Number.isNaN(Number(value)) ? null : Number(value);
  const color = GAUGE_COLORS[colorKey] || GAUGE_COLORS.grey;
  // Map 0..2 → 180°..0° (needle); clamp
  const ratio = v == null ? 0.5 : Math.min(Math.max(v / 2, 0), 1);
  const angle = 180 - ratio * 180;
  const rad = (angle * Math.PI) / 180;
  const cx = 70;
  const cy = 66;
  const r = 50;
  const nx = cx + r * Math.cos(rad);
  const ny = cy - r * Math.sin(rad);
  return (
    <div style={{ textAlign: 'center', padding: '0.45rem' }}>
      <svg width="140" height="84" viewBox="0 0 140 84">
        <path d="M 20 66 A 50 50 0 0 1 120 66" fill="none" stroke="#e5e7eb" strokeWidth="11" strokeLinecap="round" />
        <path d="M 20 66 A 50 50 0 0 1 120 66" fill="none" stroke={color} strokeWidth="11" strokeLinecap="round"
          strokeDasharray={`${Math.PI * 50 * (v == null ? 0 : Math.min(Math.max(v, 0), 2) / 2)} ${Math.PI * 50}`} />
        {v != null && <line x1={cx} y1={cy} x2={nx} y2={ny} stroke="#1f2937" strokeWidth="2.5" strokeLinecap="round" />}
        <circle cx={cx} cy={cy} r="4" fill="#1f2937" />
        <text x={cx} y={80} textAnchor="middle" fontSize="15" fontWeight="700" fill={color}>
          {v == null ? '—' : v.toFixed(2)}
        </text>
      </svg>
      <div style={{ fontSize: '0.8rem', color: '#6b7280', fontWeight: 600 }}>{label}</div>
    </div>
  );
};

// Permission check component
const AccessDenied = () => (
  <div style={{
    padding: '2rem',
    textAlign: 'center',
    backgroundColor: '#f8d7da',
    border: '1px solid #f5c6cb',
    borderRadius: '8px',
    color: '#721c24'
  }}>
    <h3 style={{ margin: '0 0 0.5rem 0' }}>🔒 Access Denied</h3>
    <p style={{ margin: 0 }}>You don't have permission to view project reports.</p>
  </div>
);

// Same S-curve generation as Budget Report - without rounding to avoid zeros
const generateNormalDistribution = (startDate, finishDate, totalHours) => {
  if (!startDate || !finishDate || !totalHours || totalHours <= 0) return [];
  
  const start = new Date(startDate);
  const end = new Date(finishDate);
  const daysDiff = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
  
  if (daysDiff <= 0) return [];
  
  const weeks = Math.ceil(daysDiff / 7);
  const data = [];
  const mean = weeks / 2;
  const stdDev = weeks / 6;
  
  let rawValues = [];
  let totalRaw = 0;
  
  for (let i = 0; i < weeks; i++) {
    const x = i + 0.5;
    const exponent = -0.5 * Math.pow((x - mean) / stdDev, 2);
    const value = Math.exp(exponent);
    rawValues.push(value);
    totalRaw += value;
  }
  
  let cumulative = 0;
  const currentDate = new Date(start);
  
  for (let i = 0; i < weeks; i++) {
    // Don't round - keep decimal precision to avoid zeros
    const periodHours = (rawValues[i] / totalRaw) * totalHours;
    cumulative += periodHours;
    
    const weekStart = new Date(currentDate);
    currentDate.setDate(currentDate.getDate() + 7);
    
    data.push({
      date: weekStart.toISOString().split('T')[0],
      period: periodHours,
      cumulative: cumulative
    });
  }
  
  return data;
};

// EXACT copy of combineAllDistributions from BudgetReport.js — reserved for future EVM charts
// eslint-disable-next-line no-unused-vars
const combineBudgetDistributions = (disciplineData) => {
  // Find overall date range across all disciplines
  let minDate = null;
  let maxDate = null;
  
  Object.values(disciplineData).forEach(disc => {
    ['shop', 'field'].forEach(type => {
      if (disc[type]?.startDate && disc[type]?.finishDate) {
        const start = new Date(disc[type].startDate);
        const end = new Date(disc[type].finishDate);
        if (!minDate || start < minDate) minDate = start;
        if (!maxDate || end > maxDate) maxDate = end;
      }
    });
  });
  
  if (!minDate || !maxDate) return [];
  
  // Generate weekly periods for the entire range
  const daysDiff = Math.ceil((maxDate - minDate) / (1000 * 60 * 60 * 24)) + 1;
  const weeks = Math.ceil(daysDiff / 7);
  
  const combined = [];
  const currentDate = new Date(minDate);
  
  // Pre-generate all distributions
  const distributions = {};
  Object.entries(disciplineData).forEach(([discId, data]) => {
    ['shop', 'field'].forEach(type => {
      if (data[type]?.startDate && data[type]?.finishDate && data[type]?.hours) {
        distributions[`${discId}_${type}`] = {
          data: generateNormalDistribution(data[type].startDate, data[type].finishDate, parseFloat(data[type].hours)),
          startDate: new Date(data[type].startDate),
          finishDate: new Date(data[type].finishDate)
        };
      }
    });
  });
  
  for (let i = 0; i < weeks; i++) {
    const weekStart = new Date(currentDate);
    currentDate.setDate(currentDate.getDate() + 7);
    
    let totalPeriod = 0;
    
    // For each distribution, find which week index this global week corresponds to
    Object.entries(distributions).forEach(([key, dist]) => {
      if (dist.data.length > 0) {
        // Calculate the week index relative to this distribution's start
        const distWeekIndex = Math.floor((weekStart - dist.startDate) / (7 * 24 * 60 * 60 * 1000));
        
        if (distWeekIndex >= 0 && distWeekIndex < dist.data.length) {
          totalPeriod += dist.data[distWeekIndex].period;
        }
      }
    });
    
    combined.push({
      date: weekStart.toISOString().split('T')[0],
      budget_period: totalPeriod
    });
  }
  
  // Calculate running cumulative totals
  let runningTotal = 0;
  combined.forEach(week => {
    runningTotal += week.budget_period;
    week.budget_cumulative = runningTotal;
  });
  
  return combined;
};

// Type filter items for Fab/Field (Fab in the resource name; not a P6 column)
const TYPE_ITEMS = [
  { id: 'shop', name: 'Fab', color: '#f97316' },
  { id: 'field', name: 'Field', color: '#10b981' }
];

const SAFETY_RULE_INP = { width: 46, fontSize: '0.72rem', border: '1px solid #d1d5db', borderRadius: 4, padding: '2px 4px' };

function SafetyRulePicker({
  name, mode, menPer, firstAt, thenEvery, finalEvery,
  onMode, onMenPer, onFirstAt, onThenEvery, onFinalEvery
}) {
  const row = {
    display: 'flex',
    alignItems: 'center',
    gap: 5,
    margin: '3px 0',
    fontSize: '0.72rem',
    color: '#334155',
    flexWrap: 'wrap'
  };
  const nextAt = Math.max(1, Math.round(Number(firstAt) || 0)) + Math.max(1, Math.round(Number(thenEvery) || 0));
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, margin: '6px 0' }} role="radiogroup" aria-label="Safety rule">
      <label style={{ ...row, fontWeight: mode === SAFETY_RULE_EVERY ? 700 : 500 }}>
        <input
          type="radio"
          name={name}
          checked={mode === SAFETY_RULE_EVERY}
          onChange={() => onMode(SAFETY_RULE_EVERY)}
          style={{ accentColor: '#dc2626' }}
        />
        1 per
        <input type="number" min={1} value={menPer} disabled={mode !== SAFETY_RULE_EVERY} onChange={(e) => onMenPer(e.target.value)} style={SAFETY_RULE_INP} />
        men
      </label>
      <label style={{ ...row, fontWeight: mode === SAFETY_RULE_FIRST_THEN ? 700 : 500 }}>
        <input
          type="radio"
          name={name}
          checked={mode === SAFETY_RULE_FIRST_THEN}
          onChange={() => onMode(SAFETY_RULE_FIRST_THEN)}
          style={{ accentColor: '#dc2626' }}
        />
        First at
        <input type="number" min={1} value={firstAt} disabled={mode !== SAFETY_RULE_FIRST_THEN} onChange={(e) => onFirstAt(e.target.value)} style={SAFETY_RULE_INP} />
        then every
        <input type="number" min={1} value={thenEvery} disabled={mode !== SAFETY_RULE_FIRST_THEN} onChange={(e) => onThenEvery(e.target.value)} style={SAFETY_RULE_INP} />
      </label>
      <label style={{ ...row, fontWeight: mode === SAFETY_RULE_THREE ? 700 : 500 }}>
        <input
          type="radio"
          name={name}
          checked={mode === SAFETY_RULE_THREE}
          onChange={() => onMode(SAFETY_RULE_THREE)}
          style={{ accentColor: '#dc2626' }}
        />
        First at
        <input type="number" min={1} value={firstAt} disabled={mode !== SAFETY_RULE_THREE} onChange={(e) => onFirstAt(e.target.value)} style={SAFETY_RULE_INP} />
        next +
        <input type="number" min={1} value={thenEvery} disabled={mode !== SAFETY_RULE_THREE} onChange={(e) => onThenEvery(e.target.value)} style={SAFETY_RULE_INP} />
        then every
        <input type="number" min={1} value={finalEvery} disabled={mode !== SAFETY_RULE_THREE} onChange={(e) => onFinalEvery(e.target.value)} style={SAFETY_RULE_INP} />
      </label>
      {mode === SAFETY_RULE_THREE && (
        <div style={{ fontSize: '0.68rem', color: '#64748b', marginLeft: 18, lineHeight: 1.35 }}>
          {firstAt} men → 1 safety, {nextAt} → 2, then one more every {finalEvery}
        </div>
      )}
    </div>
  );
}

// PBIX Resource Control family slicer (KPI cards)
const PBIX_FAMILIES = [
  { id: 'overall', label: 'Overall' },
  { id: 'mechanical', label: 'Mech' },
  { id: 'piping', label: 'Pipe' },
  { id: 'electrical', label: 'Elec' },
  { id: 'indirect', label: 'Indirect' },
  { id: 'pipe_fab', label: 'Pipe Fab' }
];

function familiesForActivity(a) {
  const fam = familyFromResource({
    discipline: a?.discipline,
    shopOrField: a?.shop_or_field
  });
  const out = ['overall'];
  if (fam === 'pipe_fab') out.push('pipe_fab', 'piping');
  else if (fam === 'piping' || fam === 'mechanical' || fam === 'electrical' || fam === 'indirect') {
    out.push(fam);
  }
  return out;
}

const PROGRESS_SCOPES = [
  { id: 'baseline', label: 'Baseline (no EX)' },
  { id: 'extra', label: 'Extra (EX / Additional Scope)' },
  { id: 'all', label: 'All (baseline + extra)' }
];

// Group By options for chart aggregation
const GROUP_BY_OPTIONS = [
  { id: 'day', name: 'Day', icon: '' },
  { id: 'week', name: 'Week', icon: '' },
  { id: 'month', name: 'Month', icon: '' }
];

/** Bucket key for group-by. Week start is Sunday (Maestro/FP) or Monday (P6 Resource Assignments). */
function groupBucketKey(dateStr, groupBy, weekStart = 'sunday') {
  const d = String(dateStr || '').slice(0, 10);
  if (!d) return '';
  if (groupBy === 'month') return d.slice(0, 7);
  if (groupBy === 'day') return d;
  const dt = new Date(`${d}T12:00:00`);
  if (Number.isNaN(dt.getTime())) return d;
  if (weekStart === 'sunday') {
    dt.setDate(dt.getDate() - dt.getDay());
  } else {
    const back = dt.getDay() === 0 ? 6 : dt.getDay() - 1;
    dt.setDate(dt.getDate() - back);
  }
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const day = String(dt.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function bucketDateLabel(key, groupBy) {
  if (groupBy === 'month') return `${key}-01`;
  return key;
}

/** Aggregate EVM / KPI series by day | week | month (sums periods; last cum wins). */
function aggregateSeriesByGroup(rows, groupBy, periodKeys = [], cumKeys = [], weekStart = 'sunday', dataDate = null) {
  if (!rows?.length) return [];
  const mode = groupBy || 'week';
  const dd = dataDate ? String(dataDate).slice(0, 10) : null;
  const ddWeek = dd ? groupBucketKey(dd, mode, weekStart) : null;
  const buckets = new Map();
  for (const r of rows) {
    const key = groupBucketKey(r.date, mode, weekStart);
    if (!key) continue;
    if (!buckets.has(key)) {
      const base = { date: bucketDateLabel(key, mode) };
      periodKeys.forEach((k) => { base[k] = 0; });
      cumKeys.forEach((k) => { base[k] = null; });
      buckets.set(key, base);
    }
    const b = buckets.get(key);
    const rowDate = String(r.date || '').slice(0, 10);
    periodKeys.forEach((k) => {
      if (r[k] != null) b[k] = (Number(b[k]) || 0) + (Number(r[k]) || 0);
    });
    cumKeys.forEach((k) => {
      if (r[k] == null) return;
      // Data-date week: keep Cum. Budget at the as-of day so the tooltip
      // matches the PV card (don't let Sat of that week overwrite Sunday).
      if (dd && ddWeek && key === ddWeek && rowDate > dd && k === 'budget_cumulative' && b[k] != null) {
        return;
      }
      b[k] = Number(r[k]);
    });
    if (r.spent_assumed) b.spent_assumed = true;
  }
  return [...buckets.values()].sort((a, b) => String(a.date).localeCompare(String(b.date)));
}

/** Drop leading/trailing weeks where no visible series has period hours. */
function clipSeriesToVisibleData(rows, visible) {
  if (!rows?.length) return rows;
  const eps = 0.0005;
  const has = (r, prev) => {
    if ((visible.actual_period || visible.actual_cumulative) && Math.abs(Number(r.actual_period) || 0) > eps) return true;
    if ((visible.earned_period || visible.earned_cumulative) && Math.abs(Number(r.earned_period) || 0) > eps) return true;
    if ((visible.budget_period || visible.budget_cumulative) && Math.abs(Number(r.budget_period) || 0) > eps) return true;
    if ((visible.remaining_cumulative || visible.remaining_period) && Math.abs(Number(r.remaining_period) || 0) > eps) return true;
    // Weeks whose bars round to zero can still carry hours — keep every week
    // where a visible cumulative (always in hours) is still climbing, so the
    // cum lines reach the real total instead of stopping at the last big bar.
    if (prev) {
      const climbs = (key, on) => on && Math.abs((Number(r[key]) || 0) - (Number(prev[key]) || 0)) > 0.5;
      if (climbs('actual_cumulative', visible.actual_cumulative || visible.actual_period)) return true;
      if (climbs('earned_cumulative', visible.earned_cumulative || visible.earned_period)) return true;
      if (climbs('budget_cumulative', visible.budget_cumulative || visible.budget_period)) return true;
      if (climbs('remaining_cumulative', visible.remaining_cumulative || visible.remaining_period)) return true;
    }
    return false;
  };
  let first = -1;
  let last = -1;
  for (let i = 0; i < rows.length; i += 1) {
    if (has(rows[i], i > 0 ? rows[i - 1] : null)) {
      if (first < 0) first = i;
      last = i;
    }
  }
  if (first < 0) return rows;
  // Let Cum. Remaining visually join the earned curve one week earlier
  if (visible.remaining_cumulative && first > 0 && rows[first - 1]?.earned_cumulative != null) {
    first -= 1;
  }
  return rows.slice(first, last + 1);
}

/** X-axis rows from last-good P6 curves so bars paint before EVM/analytics return. */
function rowsFromHistogramCurves(remaining, proposal, weeklyTarget) {
  const byDate = new Map();
  const put = (totals, periodKey, cumKey) => {
    (totals || []).forEach((t) => {
      const date = t?.date;
      if (!date) return;
      const row = byDate.get(date) || { date };
      row[periodKey] = Number(t.period) || 0;
      row[cumKey] = t.cumulative == null ? null : Number(t.cumulative);
      byDate.set(date, row);
    });
  };
  put(proposal?.totals || weeklyTarget?.totals, 'budget_period', 'budget_cumulative');
  put(remaining?.totals, 'remaining_period', 'remaining_cumulative');
  return [...byDate.keys()].sort().map((date) => ({
    date,
    actual_period: 0,
    earned_period: 0,
    budget_period: 0,
    remaining_period: 0,
    shop_earned_period: 0,
    field_earned_period: 0,
    ...byDate.get(date)
  }));
}

function aggregateKpiTrend(rows, groupBy, weekStart = 'sunday') {
  if (!rows?.length) return [];
  const mode = groupBy || 'week';
  const aggregated = aggregateSeriesByGroup(
    rows,
    mode,
    ['per_spent', 'per_earned', 'per_planned', 'per_sv', 'per_cv'],
    ['cum_spi', 'cum_cpi', 'cum_sv', 'cum_cv', 'cum_spent', 'cum_earned', 'cum_planned'],
    weekStart
  );
  return aggregated.map((b) => {
    const per_spi = b.per_planned > 0 ? b.per_earned / b.per_planned : null;
    const per_cpi = b.per_spent > 0 ? b.per_earned / b.per_spent : null;
    return {
      ...b,
      per_spi: per_spi == null ? null : Math.round(per_spi * 1000) / 1000,
      per_cpi: per_cpi == null ? null : Math.round(per_cpi * 1000) / 1000
    };
  });
}

const EVM_QUIZ_POOL = [
  { id: 'p1', q: 'Spent (AC) hours on this report come mainly from…', choices: ['Maestro labour actuals', 'P6 proposal baseline', 'Financial periods only', 'Excel S-curves'], correct: 0 },
  { id: 'p2', q: 'SPI is calculated as…', choices: ['EV ÷ PV', 'EV ÷ AC', 'BAC ÷ CPI', 'AC ÷ PV'], correct: 0 },
  { id: 'p3', q: 'Forecast on the histogram is best described as…', choices: ['Earned through data date, then remaining work after', 'Only proposal BAC spread', 'Maestro hours times CPI', 'Next week shop hours only'], correct: 0 },
  { id: 'p4', q: 'Clicking a legend item should…', choices: ['Spotlight that series and fade the others', 'Delete the series forever', 'Export a PDF', 'Switch to Progress tab'], correct: 0 },
  { id: 'p5', q: 'CPI below 1.0 usually means…', choices: ['Burning more hours than progress earned', 'Ahead of schedule', 'Underrunning labour', 'No earned value yet'], correct: 0 },
  { id: 'p6', q: 'Proposal / BAC on this page comes from…', choices: ['P6 proposal baseline', 'Maestro Excel only', 'What-if calendars', 'Shop hours × CPI'], correct: 0 },
  { id: 'p7', q: 'SV (schedule variance) is…', choices: ['EV − PV', 'EV − AC', 'BAC − FAC', 'AC − PV'], correct: 0 },
  { id: 'p8', q: 'Group By Month on the histogram…', choices: ['Uses P6 Resource Assignments calendar-month cells (not summed weeks)', 'Deletes SPI', 'Only affects PDF export', 'Changes Maestro source files'], correct: 0 },
  { id: 'p9', q: 'Activities table row selection…', choices: ['Filters the histogram curve to those activities', 'Exports a CSV', 'Changes user permissions', 'Resets BAC'], correct: 0 },
  { id: 'p10', q: 'TCPI tells you…', choices: ['How efficient remaining work must be to hit BAC', 'Yesterday’s weather', 'Shop vs Field only', 'Which calendar is default'], correct: 0 },
  { id: 'p11', q: 'Man-weeks mode divides hours by…', choices: ['Each resource\u2019s own P6 calendar (what-if ranges override)', 'Always 40', 'SPI', 'Number of employees'], correct: 0 },
  { id: 'p12', q: 'Fun learning points…', choices: ['Are motivational only and do not affect pay or roles', 'Set your authority level', 'Unlock admin', 'Change project budgets'], correct: 0 },
  { id: 'p13', q: 'Maestro spent Date is week-ending Saturday. The histogram plots P6 week-starting Sunday, so Saturday 8/1 lands on…', choices: ['Sunday 8/2', 'Sunday 7/26', 'Monday 8/3', 'The same Saturday'], correct: 0 },
  { id: 'p14', q: 'Staffing Plan remaining for a week is…', choices: ['The 7-week average of histogram remaining (±3 weeks), per resource', 'Maestro spent ÷ CPI', 'Proposal BAC only', 'Always last week’s headcount'], correct: 0 }
];

function pickQuizQuestions(pool, count = 4, excludeIds = []) {
  const available = pool.filter((q) => !excludeIds.includes(q.id));
  const src = available.length >= count ? available : pool;
  const shuffled = [...src].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, count);
}

const EVMReport = ({ jobNumber: jobNumberProp, estimateNumber = null, openBudgetBaseline = false }) => {
  const jobNumber = String(jobNumberProp || estimateNumber || '').trim();
  const { instance, accounts } = useMsal();
  
  // Permission states
  const [hasPermission, setHasPermission] = useState(null);
  const [permissionLoading, setPermissionLoading] = useState(true);
  
  const [chartData, setChartData] = useState([]);
  const [rawChartData, setRawChartData] = useState([]); // Unfiltered data
  const [loading, setLoading] = useState(true);
  const [refreshBusy, setRefreshBusy] = useState(false);
  const [legendBusy, setLegendBusy] = useState(false);
  const [summary, setSummary] = useState(null);
  const [lastFetchedAt, setLastFetchedAt] = useState(null);

  // Auto-refresh state and refs
  const fetchReportDataRef = useRef(null);
  const fetchSummaryRef = useRef(null);
  const fetchLegendStacksRef = useRef(null);
  const p6RetryTimerRef = useRef(null);
  const hasHistRef = useRef(false);
  const hasAnalyticsRef = useRef(false);
  const legendGenRef = useRef(0);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [refreshInterval, setRefreshInterval] = useState(600);
  const [actualsSource, setActualsSource] = useState('auto');
  const maestroFileRef = useRef(null);
  const [maestroUploading, setMaestroUploading] = useState(false);
  const [maestroImportMeta, setMaestroImportMeta] = useState(null);
  const [maestroReplace, setMaestroReplace] = useState(true);
  const [assumeLastWeekSpent, setAssumeLastWeekSpent] = useState(loadAssumeLastWeek);
  const [assumedSpentMeta, setAssumedSpentMeta] = useState(null);
  const [safetyOn, setSafetyOn] = useState(() => {
    try { return localStorage.getItem('evm-safety-lines-v1') === 'true'; } catch { return false; }
  });
  const [safetyMenPer, setSafetyMenPer] = useState(() => {
    try {
      const n = Number(localStorage.getItem('evm-safety-men-per-v1'));
      return Number.isFinite(n) && n >= 1 ? Math.round(n) : 25;
    } catch { return 25; }
  });
  const [safetyRuleMode, setSafetyRuleMode] = useState(() => {
    try {
      const stored = localStorage.getItem('evm-safety-rule-v1');
      if (stored === SAFETY_RULE_FIRST_THEN || stored === SAFETY_RULE_THREE) return stored;
      return SAFETY_RULE_EVERY;
    } catch { return SAFETY_RULE_EVERY; }
  });
  const [safetyFirstAt, setSafetyFirstAt] = useState(() => {
    try {
      const n = Number(localStorage.getItem('evm-safety-first-at-v1'));
      return Number.isFinite(n) && n >= 1 ? Math.round(n) : 20;
    } catch { return 20; }
  });
  const [safetyThenEvery, setSafetyThenEvery] = useState(() => {
    try {
      const n = Number(localStorage.getItem('evm-safety-then-every-v1'));
      return Number.isFinite(n) && n >= 1 ? Math.round(n) : 40;
    } catch { return 40; }
  });
  const [safetyFinalEvery, setSafetyFinalEvery] = useState(() => {
    try {
      const n = Number(localStorage.getItem('evm-safety-final-every-v1'));
      return Number.isFinite(n) && n >= 1 ? Math.round(n) : 50;
    } catch { return 50; }
  });
  const [safetyPlanOpen, setSafetyPlanOpen] = useState(false);
  const [safetyCfg, setSafetyCfg] = useState(() => {
    try {
      const raw = JSON.parse(localStorage.getItem('evm-safety-cfg-v1') || 'null');
      if (raw && typeof raw === 'object') {
        return { includeIndirect: raw.includeIndirect !== false, from: raw.from || '', to: raw.to || '' };
      }
    } catch { /* ignore */ }
    return { includeIndirect: true, from: '', to: '' };
  });
  const patchSafetyCfg = (next) => {
    setSafetyCfg(next);
    try { localStorage.setItem('evm-safety-cfg-v1', JSON.stringify(next)); } catch { /* ignore */ }
  };
  const [staffingSafetyCrew, setStaffingSafetyCrew] = useState([]);
  const onStaffingSafetyCrew = useCallback((crew) => {
    const next = Array.isArray(crew) ? crew : [];
    setStaffingSafetyCrew((prev) => {
      if (prev.length === next.length
        && prev.every((p, i) => p.date === next[i]?.date && p.men === next[i]?.men && p.indirect === next[i]?.indirect)) {
        return prev;
      }
      return next;
    });
  }, []);
  const [supervisionOn, setSupervisionOn] = useState(() => {
    try { return localStorage.getItem('evm-supervision-on-v1') === 'true'; } catch { return false; }
  });
  const [menPerFm, setMenPerFm] = useState(() => {
    try {
      const n = Number(localStorage.getItem('evm-supervision-fm-v1'));
      return Number.isFinite(n) && n >= 1 ? Math.round(n) : 8;
    } catch { return 8; }
  });
  const [fmPerGf, setFmPerGf] = useState(() => {
    try {
      const n = Number(localStorage.getItem('evm-supervision-gf-v1'));
      return Number.isFinite(n) && n >= 1 ? Math.round(n) : 3;
    } catch { return 3; }
  });
  const [supervisionPlanOpen, setSupervisionPlanOpen] = useState(false);
  const [superCfg, setSuperCfg] = useState(() => loadSuperCfg(jobNumber));
  const [extrasPlanOpen, setExtrasPlanOpen] = useState(false);
  const [extrasCfg, setExtrasCfg] = useState(() => loadExtrasCfg(jobNumber));
  useEffect(() => {
    setExtrasCfg(loadExtrasCfg(jobNumber));
    setSuperCfg(loadSuperCfg(jobNumber));
  }, [jobNumber]);
  const patchSuperCfg = (patch) => {
    setSuperCfg((prev) => {
      const next = { ...prev, ...patch };
      saveSuperCfg(jobNumber, next);
      return next;
    });
  };
  const [showStaffCounts, setShowStaffCounts] = useState(() => {
    try { return localStorage.getItem('evm-staffing-numbers-v1') === 'true'; } catch { return false; }
  });
  const [legendColors, setLegendColors] = useState(() => loadLegendColors(jobNumber));
  useEffect(() => {
    setLegendColors(loadLegendColors(jobNumber));
  }, [jobNumber]);
  const legendStoreKey = (key) => ({
    budget_cumulative: 'budget_period',
    earned_cumulative: 'earned_period',
    remaining_cumulative: 'remaining_period',
    actual_cumulative: 'actual_period'
  })[key] || key;
  const changeLegendColor = (key, hex) => {
    if (!key) return;
    const nextHex = legendColor({ k: hex }, 'k', null);
    if (!nextHex) return;
    const storeKey = legendStoreKey(key);
    setLegendColors((prev) => {
      const next = setLegendPaint(prev, storeKey, { color: nextHex });
      saveLegendColors(jobNumber, next);
      return next;
    });
  };
  const changeLegendPattern = (key, pattern, displayedColor) => {
    if (!key) return;
    const storeKey = legendStoreKey(key);
    setLegendColors((prev) => {
      const color = legendColor(prev, storeKey, displayedColor);
      const next = setLegendPaint(prev, storeKey, { color, pattern });
      saveLegendColors(jobNumber, next);
      return next;
    });
  };
  const resetLegendColors = () => {
    setLegendColors({});
    saveLegendColors(jobNumber, {});
  };
  // Staffing chart controls — lifted here so they live on the filter pane.
  const [staffingRadius, setStaffingRadius] = useState(() => {
    try {
      const n = Number(localStorage.getItem('evm-staffing-radius-v3'));
      return Number.isInteger(n) && n >= 0 && n <= 5 ? n : 0;
    } catch { return 0; }
  });
  const changeStaffingRadius = (n) => {
    const v = Math.max(0, Math.min(5, Number(n) || 0));
    setStaffingRadius(v);
    try { localStorage.setItem('evm-staffing-radius-v3', String(v)); } catch { /* ignore */ }
  };
  const [staffingHorizon, setStaffingHorizon] = useState(() => {
    try {
      const v = localStorage.getItem('evm-staffing-horizon-v1');
      if (v === 'past' || v === 'future' || v === 'all') return v;
    } catch { /* ignore */ }
    return 'future';
  });
  const changeStaffingHorizon = (v) => {
    setStaffingHorizon(v);
    try { localStorage.setItem('evm-staffing-horizon-v1', v); } catch { /* ignore */ }
  };
  const [staffingView, setStaffingView] = useState(() => {
    try {
      const v = localStorage.getItem('evm-staffing-view-v2');
      if (v === 'curves' || v === 'crew') return v;
    } catch { /* ignore */ }
    return 'curves';
  });
  const changeStaffingView = (v) => {
    setStaffingView(v);
    try { localStorage.setItem('evm-staffing-view-v2', v); } catch { /* ignore */ }
  };
  const staffingCsvRef = useRef(null);
  const staffingResetRef = useRef(null);
  const [staffingSummary, setStaffingSummary] = useState(null);
  const onStaffingSummary = useCallback((next) => {
    setStaffingSummary((prev) => {
      if (prev
        && prev.peakBaseline === next.peakBaseline
        && prev.peakCur === next.peakCur
        && prev.peakBaselineWeek === next.peakBaselineWeek
        && prev.peakCurWeek === next.peakCurWeek
        && prev.hireNet === next.hireNet
        && prev.hire4 === next.hire4
        && prev.layoff4 === next.layoff4
        && prev.hireHorizon === next.hireHorizon
        && prev.weekHoursDefault === next.weekHoursDefault
        && prev.adjustCount === next.adjustCount
        && prev.dataDate === next.dataDate) {
        return prev;
      }
      return next;
    });
  }, []);
  const changeSafetyMenPer = (n) => {
    const v = Math.max(1, Math.round(Number(n) || 25));
    setSafetyMenPer(v);
    try { localStorage.setItem('evm-safety-men-per-v1', String(v)); } catch { /* ignore */ }
  };
  const changeSafetyRuleMode = (mode) => {
    const v = mode === SAFETY_RULE_FIRST_THEN || mode === SAFETY_RULE_THREE
      ? mode
      : SAFETY_RULE_EVERY;
    setSafetyRuleMode(v);
    try { localStorage.setItem('evm-safety-rule-v1', v); } catch { /* ignore */ }
  };
  const changeSafetyFirstAt = (n) => {
    const v = Math.max(1, Math.round(Number(n) || 20));
    setSafetyFirstAt(v);
    try { localStorage.setItem('evm-safety-first-at-v1', String(v)); } catch { /* ignore */ }
  };
  const changeSafetyThenEvery = (n) => {
    const v = Math.max(1, Math.round(Number(n) || 40));
    setSafetyThenEvery(v);
    try { localStorage.setItem('evm-safety-then-every-v1', String(v)); } catch { /* ignore */ }
  };
  const changeSafetyFinalEvery = (n) => {
    const v = Math.max(1, Math.round(Number(n) || 50));
    setSafetyFinalEvery(v);
    try { localStorage.setItem('evm-safety-final-every-v1', String(v)); } catch { /* ignore */ }
  };
  const safetyRule = useMemo(() => normalizeSafetyRule({
    mode: safetyRuleMode,
    menPer: safetyMenPer,
    firstAt: safetyFirstAt,
    thenEvery: safetyThenEvery,
    finalEvery: safetyFinalEvery
  }), [safetyRuleMode, safetyMenPer, safetyFirstAt, safetyThenEvery, safetyFinalEvery]);
  const changeMenPerFm = (n) => {
    const v = Math.max(1, Math.round(Number(n) || 8));
    setMenPerFm(v);
    try { localStorage.setItem('evm-supervision-fm-v1', String(v)); } catch { /* ignore */ }
  };
  const changeFmPerGf = (n) => {
    const v = Math.max(1, Math.round(Number(n) || 3));
    setFmPerGf(v);
    try { localStorage.setItem('evm-supervision-gf-v1', String(v)); } catch { /* ignore */ }
  };
  
  // Filter states (same pattern as Actuals Report)
  const [filterOptions, setFilterOptions] = useState({});
  const [selectedFilters, setSelectedFilters] = useState({ ...EMPTY_ACTUALS_FILTERS });
  const [selectedTypes, setSelectedTypes] = useState(['shop', 'field']);
  const [groupBy, setGroupBy] = useState('week');
  const [weekStart, setWeekStart] = useState(loadWeekStart);
  const [etcFormula, setEtcFormula] = useState(loadEtcFormula);
  const [selectedDisciplines, setSelectedDisciplines] = useState([]);
  const [showBudgetBaseline, setShowBudgetBaseline] = useState(!!openBudgetBaseline);
  const [showHelp, setShowHelp] = useState(false);
  // Remembered across refreshes — landing back on Histogram from Staffing was a bug.
  const [activeTab, setActiveTab] = useState(() => {
    try {
      const v = localStorage.getItem('evm-active-tab-v1');
      if (['histogram', 'staffing', 'kpis', 'progress'].includes(v)) return v;
    } catch { /* ignore */ }
    return 'histogram';
  });
  useEffect(() => {
    try { localStorage.setItem('evm-active-tab-v1', activeTab); } catch { /* ignore */ }
  }, [activeTab]);
  const [showFilters, setShowFilters] = useState(() => {
    try { return localStorage.getItem('evm-show-filters-v2') === 'true'; } catch { return false; }
  });
  const [showFigures, setShowFigures] = useState(() => {
    try { return localStorage.getItem('evm-show-figures-v2') === 'true'; } catch { return false; }
  });
  const [showFilterPicker, setShowFilterPicker] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [slicerPickerSearch, setSlicerPickerSearch] = useState('');
  const [slicerFields, setSlicerFields] = useState([]);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfOpen, setPdfOpen] = useState(false);
  const [pdfOptions, setPdfOptions] = useState(() => loadPdfOptions(jobNumber));
  // PDF capture only: taller chart ("fill the page") and a temporary slicer-card
  // selection while the filters panel is photographed for the PDF.
  const [pdfChartHeight, setPdfChartHeight] = useState(null);
  const [pdfPanelsOverride, setPdfPanelsOverride] = useState(null);
  const [pdfForceFigures, setPdfForceFigures] = useState(null);
  const [pdfCaptureFilters, setPdfCaptureFilters] = useState(false);
  const reportPrintRef = useRef(null);
  const histogramChartRef = useRef(null);
  const plotBoxRef = useRef(null);
  const [plotBoxW, setPlotBoxW] = useState(0);
  const [liveChartH, setLiveChartH] = useState(460);
  const filtersPanelRef = useRef(null);
  const histogramChartH = pdfChartHeight || liveChartH;
  const filtersPaneInnerRef = useRef(null);
  const figuresPanelRef = useRef(null);
  const filterPickerRef = useRef(null);
  const settingsRef = useRef(null);
  useEffect(() => {
    if (pdfChartHeight || activeTab !== 'histogram') return undefined;
    const measure = () => {
      const top = histogramChartRef.current?.getBoundingClientRect().top;
      const next = Math.round(window.innerHeight - (top || 220) - 16);
      if (Number.isFinite(next) && next > 0) setLiveChartH(Math.max(280, next));
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [pdfChartHeight, activeTab, showFilters, showFigures]);
  const [unitMode, setUnitMode] = useState('manweeks'); // hours | manweeks
  const [chartSpan, setChartSpan] = useState('fit'); // fit | scroll
  const [legendPlace, setLegendPlace] = useState('left');
  useEffect(() => {
    const el = plotBoxRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const measure = () => setPlotBoxW(el.clientWidth || 0);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [activeTab, chartSpan, legendPlace, showFilters, showFigures, loading]);
  const [p6Calendars, setP6Calendars] = useState([]);
  const [selectedCalendarId, setSelectedCalendarId] = useState('');
  const [jobCalendarSeed, setJobCalendarSeed] = useState(null);
  const [useJobSeed, setUseJobSeed] = useState(false);
  const calendarTouchedRef = useRef(false);
  // What-if: per discipline, multiple { from, to, hoursPerWeek } ranges
  const [disciplineRanges, setDisciplineRanges] = useState({});
  const [showCalendarHelp, setShowCalendarHelp] = useState(false);
  const [showDiscCalendars, setShowDiscCalendars] = useState(false);
  const [visibleSeries, setVisibleSeries] = useState({
    actual_period: true,
    earned_period: true,
    budget_period: true,
    actual_cumulative: true,
    earned_cumulative: true,
    budget_cumulative: true,
    remaining_period: true,
    remaining_cumulative: true
  });
  const [curveMarks, setCurveMarks] = useState({ bars: true, lines: true });
  const [showEvmIndices, setShowEvmIndices] = useState(false);
  const showBars = curveMarks.bars !== false;
  const showLines = curveMarks.lines !== false;
  const [selectedFamilies, setSelectedFamilies] = useState(PBIX_FAMILIES.map((f) => f.id));
  const [progressScopes, setProgressScopes] = useState(PROGRESS_SCOPES.map((s) => s.id));
  const [earnedSource, setEarnedSource] = useState(() => {
    try {
      const raw = localStorage.getItem('evm-earned-source-v1');
      if (raw === 'temp' || raw === 'perm' || raw === 'as_is' || raw === 'manual') return raw;
    } catch { /* ignore */ }
    return 'as_is';
  });
  const [fabMode, setFabMode] = useState('both'); // with | without | both
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedActivityCodes, setSelectedActivityCodes] = useState([]);
  const [activitySearch, setActivitySearch] = useState('');
  const [chartFilterWeek, setChartFilterWeek] = useState(null);
  const [collapsedActivityWbs, setCollapsedActivityWbs] = useState(null);
  // Budget / Current picks survive a page refresh (per job).
  const projPickStorageKey = `evm-p6-projects-${jobNumber}`;
  const readProjPicks = () => {
    try { return JSON.parse(localStorage.getItem(projPickStorageKey) || '{}') || {}; } catch { return {}; }
  };
  const [selectedProposalProjIds, setSelectedProposalProjIdsRaw] = useState(() => {
    const picks = readProjPicks();
    if (Array.isArray(picks.proposals) && picks.proposals.length) return picks.proposals.map(String);
    if (picks.proposal) return [String(picks.proposal)];
    return [];
  });
  const [selectedWeeklyProjIds, setSelectedWeeklyProjIdsRaw] = useState(() => {
    const picks = readProjPicks();
    if (Array.isArray(picks.weeklies) && picks.weeklies.length) return picks.weeklies.map(String);
    if (picks.weekly) return [String(picks.weekly)];
    return [];
  });
  const persistProjPick = (patch) => {
    try {
      localStorage.setItem(projPickStorageKey, JSON.stringify({ ...readProjPicks(), ...patch }));
    } catch { /* ignore */ }
  };
  const setSelectedProposalProjIds = (ids) => {
    const next = (ids || []).map(String);
    setSelectedProposalProjIdsRaw(next);
    persistProjPick({ proposals: next, proposal: next.length === 1 ? next[0] : '' });
  };
  const setSelectedWeeklyProjIds = (ids) => {
    const next = (ids || []).map(String);
    setSelectedWeeklyProjIdsRaw(next);
    persistProjPick({ weeklies: next, weekly: next.length === 1 ? next[0] : '' });
  };
  const [spotlightSeries, setSpotlightSeries] = useState(null); // legend spotlight
  const [collapsedKpi, setCollapsedKpi] = useState({});
  const [visibleKpiCharts, setVisibleKpiCharts] = useState({
    gauges: true,
    spi_per: true, spi_cum: true,
    cpi_per: true, cpi_cum: true,
    sv: true, cv: true, forecast: true, spentEarned: true
  });
  const actualsSlicers = (slicerFields.length ? slicerFields.map(toActualsSlicer) : FALLBACK_ACTUALS_SLICERS);
  const FILTER_PANEL_DEFS = [
    ...BUILTIN_SLICERS,
    ...actualsSlicers
  ];
  const PICKER_GROUPS = ['Histogram', 'P6 / Schedule', 'Time Tracker / Actuals', 'Employee master'];
  const pickerDefaultOn = (f) => DEFAULT_TOOLBAR_ON.includes(f.id) || DEFAULT_ACTUALS_ON.includes(f.id);
  const [visibleFilterPanels, setVisibleFilterPanels] = useState(loadVisiblePanels);
  // During a PDF filters capture the override picks which cards are in shot.
  const effFilterPanels = pdfPanelsOverride || visibleFilterPanels;
  const [slicerLayout, setSlicerLayout] = useState(loadSlicerLayout);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizResult, setQuizResult] = useState(null);
  const [quizSubmitting, setQuizSubmitting] = useState(false);
  const [activeQuiz, setActiveQuiz] = useState(() => pickQuizQuestions(EVM_QUIZ_POOL, 4));
  const lastQuizIdsRef = useRef([]);

  const refreshQuiz = () => {
    const next = pickQuizQuestions(EVM_QUIZ_POOL, 4, lastQuizIdsRef.current);
    lastQuizIdsRef.current = next.map((q) => q.id);
    setActiveQuiz(next);
    setQuizAnswers({});
    setQuizResult(null);
  };

  const submitEvmQuiz = async () => {
    let score = 0;
    activeQuiz.forEach((item) => {
      if (Number(quizAnswers[item.id]) === item.correct) score += 1;
    });
    setQuizSubmitting(true);
    try {
      const res = await authenticatedApiCall(instance, accounts, 'POST', '/learning-fun/quiz', {
        guideId: 'evm-report-help',
        score,
        maxScore: activeQuiz.length,
        answers: quizAnswers
      });
      const body = res?.data || {};
      setQuizResult({
        score,
        maxScore: activeQuiz.length,
        awarded: body.awarded,
        disclaimer: body.disclaimer
          || 'These points are only for fun — they do not affect your role, pay, or reviews.'
      });
      // Rotate question set for the next attempt (keep score visible)
      const next = pickQuizQuestions(EVM_QUIZ_POOL, 4, [
        ...lastQuizIdsRef.current,
        ...activeQuiz.map((q) => q.id)
      ]);
      lastQuizIdsRef.current = next.map((q) => q.id);
      setActiveQuiz(next);
      setQuizAnswers({});
    } catch (err) {
      const apiMsg = err?.response?.data?.error;
      setQuizResult({
        score,
        maxScore: activeQuiz.length,
        error: apiMsg || err?.message || 'Could not save points (table may need migration).',
        disclaimer: 'These points are only for fun — they do not affect your role, pay, or reviews.'
      });
    } finally {
      setQuizSubmitting(false);
    }
  };

  const CURVE_GROUPS = [
    { id: 'budget', label: 'Budget', color: '#7dd3fc', keys: ['budget_period', 'budget_cumulative'] },
    { id: 'earned', label: 'Earned', color: '#15803d', keys: ['earned_period', 'earned_cumulative'] },
    { id: 'remaining', label: 'Remaining', color: '#f59e0b', keys: ['remaining_period', 'remaining_cumulative'] },
    { id: 'actuals', label: 'Spent', color: '#dc2626', keys: ['actual_period', 'actual_cumulative'] }
  ];

  const toggleCurveGroup = (id) => {
    const group = CURVE_GROUPS.find((g) => g.id === id);
    if (!group) return;
    setVisibleSeries((prev) => {
      const on = group.keys.every((k) => prev[k]);
      const next = { ...prev };
      group.keys.forEach((k) => { next[k] = !on; });
      return next;
    });
  };

  const curveGroupOn = (id) => {
    const group = CURVE_GROUPS.find((g) => g.id === id);
    return group ? group.keys.every((k) => visibleSeries[k]) : false;
  };

  const formatAxisTick = (v) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return '';
    if (Math.abs(n) >= 1000) return Math.round(n).toLocaleString();
    if (Math.abs(n) >= 10) return (Math.round(n * 10) / 10).toString();
    return (Math.round(n * 100) / 100).toString();
  };

  const formatPeriodTick = (v) => {
    if (unitMode === 'manweeks') return String(Math.round(Number(v) || 0));
    return formatAxisTick(v);
  };

  const progressMetricKeys = (() => {
    const withFab = [
      ['Cum. W Fab', 'cum_w_fab'],
      ['Per. W Fab', 'per_w_fab']
    ];
    const withoutFab = [
      ['Cum. WO Fab', 'cum_wo_fab'],
      ['Per. WO Fab', 'per_wo_fab']
    ];
    if (fabMode === 'with') return withFab;
    if (fabMode === 'without') return withoutFab;
    return [...withFab, ...withoutFab];
  })();

  // Discipline toggle functions
  const toggleDiscipline = (disciplineId) => {
    setSelectedDisciplines(prev => 
      prev.includes(disciplineId) 
        ? prev.filter(d => d !== disciplineId)
        : [...prev, disciplineId]
    );
  };
  
  // Phase selection: 'estimation' (Phase 1) or 'execution' (Phase 2)
  const [selectedPhase, setSelectedPhase] = useState('estimation');
  const [phase2CurveData, setPhase2CurveData] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [p6ProjectList, setP6ProjectList] = useState([]);
  const [liveRemaining, setLiveRemaining] = useState(null);
  const [liveP6Actual, setLiveP6Actual] = useState(null);
  const [liveMaestroActuals, setLiveMaestroActuals] = useState(null);
  const [liveProposal, setLiveProposal] = useState(null);
  const [liveWeeklyTarget, setLiveWeeklyTarget] = useState(null);
  const [liveHistMeta, setLiveHistMeta] = useState(null);
  const disciplineOptions = useMemo(
    () => (Array.isArray(liveHistMeta?.disciplineOptions) ? liveHistMeta.disciplineOptions : []),
    [liveHistMeta]
  );
  const allDisciplineIds = useMemo(() => disciplineOptions.map((d) => d.id), [disciplineOptions]);
  const whatIfDisciplines = useMemo(() => {
    if (disciplineOptions.length) {
      return disciplineOptions.filter((d) => d.id && d.id !== '__none__');
    }
    return CALENDAR_DISCIPLINES.map((id) => ({ id, name: DISC_LABELS[id] || id }));
  }, [disciplineOptions]);
  const disciplineOptionsKey = allDisciplineIds.join('|');
  useEffect(() => {
    if (!allDisciplineIds.length) return;
    setSelectedDisciplines((prev) => {
      const next = migrateDisciplineSelection(prev, allDisciplineIds);
      if (prev.length === next.length && next.every((id) => prev.includes(id))) return prev;
      return next;
    });
  }, [disciplineOptionsKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const selectAllDisciplines = () => {
    const ids = allDisciplineIds;
    if (!ids.length) {
      setSelectedDisciplines([]);
      return;
    }
    if (selectedDisciplines.length === ids.length && ids.every((id) => selectedDisciplines.includes(id))) {
      setSelectedDisciplines([]);
    } else {
      setSelectedDisciplines(ids);
    }
  };
  // Legend by any P6 column: catalog of stackable fields + rebuilt legend curves
  const [legendFields, setLegendFields] = useState([]);
  const [liveLegendStacks, setLiveLegendStacks] = useState(null);
  const [spentColumnCurve, setSpentColumnCurve] = useState(null);
  const [spentLegendBusy, setSpentLegendBusy] = useState(false);
  const [selectedResourceIds, setSelectedResourceIds] = useState(null);
  const [legendBy, setLegendBy] = useState(loadLegendBy);
  // Staffing baseline schedule: Current by default; Budget/proposal on request.
  const [staffingBaseline, setStaffingBaseline] = useState(() => {
    try {
      const v = localStorage.getItem('evm-staffing-baseline-v1');
      if (v === 'current' || v === 'budget') return v;
    } catch { /* ignore */ }
    return 'current';
  });
  const changeStaffingBaseline = (v) => {
    setStaffingBaseline(v);
    try { localStorage.setItem('evm-staffing-baseline-v1', v); } catch { /* ignore */ }
  };
  const [dataSource, setDataSource] = useState(null);
  const autoSwitchedRef = useRef(false);

  // Check permissions on mount
  useEffect(() => {
    const checkPermissions = async () => {
      try {
        const response = await authenticatedApiCall(instance, accounts, 'GET', '/auth/permissions-info');
        if (response.data.success) {
          setHasPermission(
            canAccessEvmReport(response.data.data)
            || Boolean(estimateNumber)
          );
        } else {
          setHasPermission(false);
        }
      } catch (error) {
        console.error('Error checking permissions:', error);
        setHasPermission(false);
      } finally {
        setPermissionLoading(false);
      }
    };
    checkPermissions();
  }, [instance, accounts, estimateNumber]);

  const selectedActivityCodesRef = useRef(selectedActivityCodes);
  selectedActivityCodesRef.current = selectedActivityCodes;
  const earnedSourceRef = useRef(earnedSource);
  earnedSourceRef.current = earnedSource;
  const selectedWeeklyProjIdsRef = useRef(selectedWeeklyProjIds);
  const selectedProposalProjIdsRef = useRef(selectedProposalProjIds);
  selectedWeeklyProjIdsRef.current = selectedWeeklyProjIds;
  selectedProposalProjIdsRef.current = selectedProposalProjIds;
  const assumeLastWeekRef = useRef(assumeLastWeekSpent);
  assumeLastWeekRef.current = assumeLastWeekSpent;
  const legendByRef = useRef(legendBy);
  legendByRef.current = legendBy;
  const fetchGenRef = useRef(0);

  const fetchReportData = useCallback(async (opts = {}) => {
    if (!jobNumber) return;
    const gen = ++fetchGenRef.current;
    const stale = () => gen !== fetchGenRef.current;
    const soft = !!opts.soft;
    const activityCodes = opts.activityCodes != null
      ? opts.activityCodes
      : selectedActivityCodesRef.current;
    
    const scrollY = (typeof window !== 'undefined' && soft) ? window.scrollY : null;
    try {
      if (!soft && !hasHistRef.current) setLoading(true);
      
      // Build query params with filters
      const params = new URLSearchParams();
      params.append('job_number', jobNumber);
      params.append('group_by', groupBy);
      // Legacy /evm-report still reads tracker history tables keyed by the
      // old catalog slugs. Always send all of them so a missing param does
      // not collapse that stub to piping-only. Histogram / analytics use
      // the live P6 UDF ids below.
      params.append('disciplines', LEGACY_DISCIPLINE_CATALOG_IDS.join(','));
      // Pass budget source to backend - backend handles both phases with exact math
      const budgetPhase = earnedSourceRef.current === 'manual'
        ? 'estimation'
        : selectedPhase;
      params.append('budget_source', budgetPhase === 'execution' ? 'execution' : 'estimation');
      
      const actualsActive = {};
      Object.entries(selectedFilters || {}).forEach(([key, vals]) => {
        if (Array.isArray(vals) && vals.length) actualsActive[key] = vals;
      });
      if (Object.keys(actualsActive).length) {
        params.append('actuals_filters', JSON.stringify(actualsActive));
      }
      
      if (selectedFamilies.length > 0 && selectedFamilies.length < PBIX_FAMILIES.length) {
        params.append('families', selectedFamilies.join(','));
      }
      if (selectedTypes.length > 0 && selectedTypes.length < 2) {
        params.append('shop_field', selectedTypes.join(','));
      }

      const analyticsParams = new URLSearchParams();
      if ((activityCodes || []).length) {
        analyticsParams.append('activities', activityCodes.join(','));
      }
      if (selectedFamilies.length > 0 && selectedFamilies.length < PBIX_FAMILIES.length) {
        analyticsParams.append('families', selectedFamilies.join(','));
      }
      if (selectedDisciplines.length > 0 && allDisciplineIds.length && selectedDisciplines.length < allDisciplineIds.length) {
        analyticsParams.append('disciplines', selectedDisciplines.join(','));
      }
      if (selectedTypes.length > 0 && selectedTypes.length < 2) {
        analyticsParams.append('shop_field', selectedTypes.join(','));
      }
      if (progressScopes?.length) {
        analyticsParams.append('scopes', progressScopes.join(','));
      }
      analyticsParams.append('earned_source', earnedSourceRef.current);
      params.append('earned_source', earnedSourceRef.current);
      if (selectedProposalProjIds.length) {
        const proposalIds = selectedProposalProjIds.join(',');
        params.append('proposal_proj_id', proposalIds);
        analyticsParams.append('proposal_proj_id', proposalIds);
      }
      if (selectedWeeklyProjIds.length) {
        const weeklyIds = selectedWeeklyProjIds.join(',');
        params.append('weekly_proj_id', weeklyIds);
        analyticsParams.append('weekly_proj_id', weeklyIds);
      }
      if (assumeLastWeekRef.current) {
        params.append('assume_last_week', '1');
        analyticsParams.append('assume_last_week', '1');
      }
      if (opts.forceP6) {
        params.append('refresh', '1');
        analyticsParams.append('refresh', '1');
      }
      const actQ = analyticsParams.toString() ? `?${analyticsParams.toString()}` : '';
      // Legend stacks are a separate request — do not bust / wait on the
      // cached histogram just because the user flipped Activity ID.
      const liveParams = new URLSearchParams(analyticsParams);
      const liveQ = liveParams.toString() ? `?${liveParams.toString()}` : '';

      const applyLiveHistogram = (liveHistResponse) => {
        if (stale()) return false;
        const liveOk = liveHistResponse?.data?.success;
        const liveEmptyRetry = !!(liveHistResponse?.data?.meta?.p6Retrying)
          && !(Number(liveHistResponse?.data?.remaining?.totalHours) > 0
            || Number(liveHistResponse?.data?.proposal?.totalHours) > 0
            || Number(liveHistResponse?.data?.weeklyTarget?.totalHours) > 0);
        if (liveOk && !liveEmptyRetry) {
          if (!liveMatchesXerSelection(
            liveHistResponse.data,
            selectedWeeklyProjIds,
            selectedProposalProjIds
          )) {
            if (liveHistResponse.data.meta) {
              setLiveHistMeta((prev) => ({
                ...(prev || {}),
                ...liveHistResponse.data.meta,
                xerSelectionPending: true
              }));
            }
            return false;
          }
          if (!shouldReplaceHistogramPayload(liveHistResponse.data, { hasPainted: hasHistRef.current })) {
            if (liveHistResponse.data.meta) {
              setLiveHistMeta((prev) => ({
                ...(prev || {}),
                ...liveHistResponse.data.meta
              }));
            }
            return true;
          }
          setLiveRemaining(liveHistResponse.data.remaining || null);
          setLiveP6Actual(liveHistResponse.data.p6Actual || null);
          setLiveMaestroActuals(liveHistResponse.data.actuals || null);
          setLiveProposal(liveHistResponse.data.proposal || null);
          setLiveWeeklyTarget(liveHistResponse.data.weeklyTarget || null);
          setLiveHistMeta(liveHistResponse.data.meta || null);
          if (Array.isArray(liveHistResponse.data.legendFields)) {
            setLegendFields(liveHistResponse.data.legendFields);
          }
          const stacks = liveHistResponse.data.legendStacks;
          if (stacks && stacks.stackBy === p6StackByParam(legendByRef.current)) {
            setLiveLegendStacks(stacks);
          }
          if (liveHistResponse?.data?.meta?.lastMaestroImport) {
            setMaestroImportMeta(liveHistResponse.data.meta.lastMaestroImport);
          }
          hasHistRef.current = true;
          setLoading(false);
          return true;
        }
        if (liveHistResponse?.data?.meta) {
          setLiveHistMeta((prev) => ({ ...(prev || {}), ...liveHistResponse.data.meta }));
        }
        return false;
      };

      const liveP = authenticatedApiCall(
        instance, accounts, 'GET', `/resource-histogram/${jobNumber}${liveQ}`, null, { timeout: 120000 }
      ).catch((e) => e);

      const liveHistResponse = await liveP;
      if (stale()) return;
      const xerMismatch = !!(liveHistResponse?.data?.success)
        && !liveMatchesXerSelection(
          liveHistResponse.data,
          selectedWeeklyProjIds,
          selectedProposalProjIds
        );
      const liveOk = applyLiveHistogram(liveHistResponse);
      if (opts.histogramOnly) {
        const refreshingOnly = !!(liveHistResponse?.data?.meta?.refreshing);
        const needRetry = !!(
          refreshingOnly
          || liveHistResponse?.data?.meta?.p6Retrying
          || liveHistResponse?.data?.meta?.stale
        );
        if (needRetry || xerMismatch) {
          if (p6RetryTimerRef.current) clearTimeout(p6RetryTimerRef.current);
          p6RetryTimerRef.current = setTimeout(() => {
            fetchReportDataRef.current?.({ soft: true, histogramOnly: true });
          }, refreshingOnly ? 4000 : 8000);
        }
        return;
      }

      const evmP = authenticatedApiCall(
        instance, accounts, 'GET', `/evm-report?${params.toString()}`, null, { timeout: 120000 }
      ).catch((e) => e);
      const analyticsP = authenticatedApiCall(instance, accounts, 'GET',
        `/resource-histogram/${jobNumber}/analytics${actQ}`, null, { timeout: 120000 }).catch((e) => e);
      const scheduleP = authenticatedApiCall(
        instance, accounts, 'GET', `/schedule-baseline/${jobNumber}/curve`, null, { timeout: 60000 }
      ).catch(() => null);

      const liveEmptyRetry = !!(liveHistResponse?.data?.meta?.p6Retrying)
        && !(Number(liveHistResponse?.data?.remaining?.totalHours) > 0
          || Number(liveHistResponse?.data?.proposal?.totalHours) > 0
          || Number(liveHistResponse?.data?.weeklyTarget?.totalHours) > 0);

      const [evmResponse, analyticsResponse, scheduleResponse] = await Promise.all([
        evmP, analyticsP, scheduleP
      ]);
      if (stale()) return;

      const analyticsOk = analyticsResponse?.data?.success;
      // Get data from EVM report - includes actuals, earned, AND budget (for selected phase)
      const evmData = evmResponse?.data?.success ? (evmResponse.data.data || []) : [];
      if (evmResponse?.data?.source) setDataSource(evmResponse.data.source);
      if (evmResponse?.data?.actuals_source) setActualsSource(evmResponse.data.actuals_source);
      if (liveHistResponse?.data?.meta?.lastMaestroImport) {
        setMaestroImportMeta(liveHistResponse.data.meta.lastMaestroImport);
      }
      if (analyticsResponse?.data?.meta?.assumedSpentWeek || (analyticsResponse?.data?.meta?.assumedSpentWeeks || []).length) {
        setAssumedSpentMeta({
          week: analyticsResponse.data.meta.assumedSpentWeek,
          weeks: analyticsResponse.data.meta.assumedSpentWeeks || [analyticsResponse.data.meta.assumedSpentWeek],
          from: analyticsResponse.data.meta.assumedSpentFromWeekEnding
            || analyticsResponse.data.meta.assumedSpentFromWeek
        });
      } else {
        setAssumedSpentMeta(null);
      }
      
      if (!soft) {
        if (evmResponse?.data?.filters) {
          setFilterOptions(evmResponse.data.filters);
        }
        if (Array.isArray(evmResponse?.data?.slicerFields)) {
          setSlicerFields(evmResponse.data.slicerFields);
        }
      }

      const analyticsRetrying = !!(analyticsResponse?.data?.meta?.p6Retrying)
        && !(analyticsResponse?.data?.p6Projects || []).length
        && !(Number(analyticsResponse?.data?.pmiSummary?.bac) > 0);
      const analyticsRefreshing = !!(analyticsResponse?.data?.meta?.refreshing);
      const keepAnalytics = analyticsRefreshing && hasAnalyticsRef.current;
      const useTrackerFeed = earnedSourceRef.current !== 'manual'
        && evmResponse?.data?.actuals_source === 'time_tracker' && evmData.length > 0;
      const analyticsUseful = analyticsOk && !analyticsRetrying && (
        Number(analyticsResponse?.data?.meta?.total_spent) > 0
        || Number(analyticsResponse?.data?.meta?.total_earned) > 0
        || Number(analyticsResponse?.data?.pmiSummary?.bac) > 0
      );
      const p6Bac = Number(analyticsResponse?.data?.pmiSummary?.bac) || 0;
      if (useTrackerFeed) {
        if (analyticsOk && !analyticsRetrying && !keepAnalytics) {
          setAnalytics(analyticsResponse.data);
          hasAnalyticsRef.current = true;
        } else if (!analyticsRetrying && !keepAnalytics) setAnalytics(null);
        const trackerPmi = evmResponse?.data?.pmiSummary || {};
        setSummary((prev) => ({
          ...(prev || {}),
          ...(evmResponse?.data?.summary || {}),
          total_budget_hours: p6Bac || evmResponse?.data?.summary?.total_budget_hours,
          pmiSummary: { ...trackerPmi, bac: p6Bac || trackerPmi.bac },
          source: 'tracker'
        }));
      } else if (analyticsUseful && !keepAnalytics) {
        setAnalytics(analyticsResponse.data);
        hasAnalyticsRef.current = true;
        if (analyticsResponse.data.summary) {
          setSummary((prev) => ({
            ...(prev || {}),
            ...analyticsResponse.data.summary,
            pmiSummary: analyticsResponse.data.pmiSummary,
            source: 'resource-control-analytics'
          }));
        }
      } else if (!analyticsRetrying) {
        setAnalytics(null);
        if (evmResponse?.data?.pmiSummary) {
          setSummary((prev) => ({
            ...(prev || {}),
            ...evmResponse.data.summary,
            pmiSummary: evmResponse.data.pmiSummary,
            source: 'tracker'
          }));
        }
      }
      
      // Store Phase 2 availability (live P6 first, then Excel schedule baseline)
      const liveCurve = (liveOk && !liveEmptyRetry) ? liveHistResponse?.data?.phase2?.curve : null;
      const hasLive = Array.isArray(liveCurve) && liveCurve.length > 0;
      if (hasLive) {
        setPhase2CurveData(liveCurve);
      } else if (scheduleResponse?.data?.success && scheduleResponse.data.data) {
        setPhase2CurveData(scheduleResponse.data.data);
      }

      // Auto-switch to Execution when live P6 / Maestro pilot data is available.
      // Manual is the PBIX presentation dump — keep Estimation (proposal dump).
      if (!soft && !autoSwitchedRef.current && earnedSourceRef.current !== 'manual'
        && (hasLive || evmResponse?.data?.source === 'p6-maestro-fallback')) {
        autoSwitchedRef.current = true;
        if (selectedPhase !== 'execution') {
          setSelectedPhase('execution');
        }
      }
      
      // Backend now handles all budget calculations with exact date math
      // No fuzzy matching needed - just use the data directly
      // Prefer analytics series when present (correct forecast continuation + data-date clip)
      const seriesSrc = useTrackerFeed
        ? mergeTrackerWithP6Proposal(analyticsResponse?.data?.evmSeries || [], evmData)
        : (analyticsUseful && analyticsResponse?.data?.evmSeries?.length
          ? analyticsResponse.data.evmSeries
          : evmData);
      const mergedData = seriesSrc.map(evmItem => ({
        date: evmItem.date,
        actual_period: parseFloat(evmItem.actual_period) || 0,
        actual_cumulative: evmItem.actual_cumulative == null ? null : parseFloat(evmItem.actual_cumulative),
        shop_earned_period: parseFloat(evmItem.shop_earned_period) || 0,
        field_earned_period: parseFloat(evmItem.field_earned_period) || 0,
        earned_period: parseFloat(evmItem.earned_period) || 0,
        earned_cumulative: evmItem.earned_cumulative == null ? null : parseFloat(evmItem.earned_cumulative),
        budget_period: parseFloat(evmItem.budget_period) || 0,
        budget_cumulative: parseFloat(evmItem.budget_cumulative) || 0,
        remaining_period: parseFloat(evmItem.forecast_period || evmItem.remaining_period) || 0,
        remaining_cumulative: evmItem.forecast_cumulative == null && evmItem.remaining_cumulative == null
          ? null
          : parseFloat(evmItem.forecast_cumulative ?? evmItem.remaining_cumulative),
        spent_assumed: !!evmItem.spent_assumed
      }));
      
      // Sort by date
      mergedData.sort((a, b) => new Date(a.date) - new Date(b.date));

      if (mergedData.length && !xerMismatch) {
        setRawChartData(stopPerformanceLines(mergedData));
      }
      setLastFetchedAt(new Date());
      const refreshing = !!(liveHistResponse?.data?.meta?.refreshing);
      const needP6Retry = !!(
        refreshing
        || xerMismatch
        || liveHistResponse?.data?.meta?.p6Retrying
        || liveHistResponse?.data?.meta?.stale
        || analyticsResponse?.data?.meta?.p6Retrying
        || analyticsResponse?.data?.meta?.stale
      );
      if (needP6Retry) {
        if (p6RetryTimerRef.current) clearTimeout(p6RetryTimerRef.current);
        p6RetryTimerRef.current = setTimeout(() => {
          fetchReportDataRef.current?.({ soft: true, histogramOnly: true });
        }, refreshing ? 4000 : 8000);
      }
      if (!soft) setLoading(false);
      if (soft && scrollY != null) {
        requestAnimationFrame(() => {
          window.scrollTo(0, scrollY);
          requestAnimationFrame(() => window.scrollTo(0, scrollY));
        });
      }
    } catch (error) {
      if (stale()) return;
      console.error('Error fetching EVM report:', error);
      if (!soft) setLoading(false);
    }
  }, [instance, accounts, jobNumber, selectedFilters, groupBy, selectedPhase, selectedDisciplines, disciplineOptionsKey, selectedFamilies, selectedTypes, progressScopes, selectedProposalProjIds, selectedWeeklyProjIds]);

  // A remembered Budget/Current pick that no longer exists in P6 falls back to Auto.
  useEffect(() => {
    const projects = p6ProjectList.length ? p6ProjectList : analytics?.p6Projects;
    if (!projects?.length) return;
    const ids = new Set(projects.map((p) => String(p.projId)));
    if (selectedProposalProjIds.length) {
      const keepProposals = selectedProposalProjIds.filter((id) => ids.has(String(id)));
      if (keepProposals.length !== selectedProposalProjIds.length) setSelectedProposalProjIds(keepProposals);
    }
    if (selectedWeeklyProjIds.length) {
      const keep = selectedWeeklyProjIds.filter((id) => ids.has(String(id)));
      if (keep.length !== selectedWeeklyProjIds.length) setSelectedWeeklyProjIds(keep);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [p6ProjectList, analytics?.p6Projects]);

  // Apply type filters to earned data in chart
  useEffect(() => {
    if (rawChartData.length === 0) {
      setChartData([]);
      return;
    }

    const bothTypes = selectedTypes.includes('shop') && selectedTypes.includes('field');
    let earnedCumulative = 0;
    const filteredData = rawChartData.map(item => {
      if (bothTypes || selectedTypes.length === 0) {
        return { ...item };
      }
      let periodEarned = 0;
      if (selectedTypes.includes('shop')) {
        periodEarned += parseFloat(item.shop_earned_period) || 0;
      }
      if (selectedTypes.includes('field')) {
        periodEarned += parseFloat(item.field_earned_period) || 0;
      }
      earnedCumulative += periodEarned;
      
      return {
        ...item,
        earned_period: periodEarned,
        earned_cumulative: item.earned_cumulative == null ? null : earnedCumulative,
        remaining_period: parseFloat(item.remaining_period) || 0,
        remaining_cumulative: item.remaining_cumulative == null ? null : parseFloat(item.remaining_cumulative)
      };
    });

    setChartData(filteredData);
  }, [rawChartData, selectedTypes]);

  const fetchSummary = useCallback(async (opts = {}) => {
    if (!jobNumber) return;
    
    try {
      // Prefer live Resource Control analytics (Proposal BAC) over empty budget_baseline_data
      const [summaryResponse, analyticsResponse] = await Promise.all([
        authenticatedApiCall(instance, accounts, 'GET', `/evm-report/summary?job_number=${jobNumber}`, null, { timeout: 120000 }).catch(() => null),
        authenticatedApiCall(instance, accounts, 'GET', `/resource-histogram/${jobNumber}/analytics?${new URLSearchParams({
          // Refresh button: the KPI cards must come from the same forced P6
          // read as the chart, not from a cached copy served in parallel.
          ...(opts.forceP6 ? { refresh: '1' } : {}),
          ...(progressScopes?.length ? { scopes: progressScopes.join(',') } : {}),
          earned_source: earnedSourceRef.current,
          ...(selectedWeeklyProjIdsRef.current.length
            ? { weekly_proj_id: selectedWeeklyProjIdsRef.current.join(',') }
            : {}),
          ...(selectedProposalProjIdsRef.current.length
            ? { proposal_proj_id: selectedProposalProjIdsRef.current.join(',') }
            : {})
        }).toString()}`, null, { timeout: 120000 }).catch(() => null)
      ]);
      
      let summaryData = summaryResponse?.data?.success ? summaryResponse.data.data : {};
      if (analyticsResponse?.data?.success && (analyticsResponse.data.summary || analyticsResponse.data.pmiSummary)
        && !(analyticsResponse.data.meta?.refreshing && hasAnalyticsRef.current)) {
        summaryData = {
          ...summaryData,
          ...analyticsResponse.data.summary,
          pmiSummary: analyticsResponse.data.pmiSummary || summaryData.pmiSummary,
          source: 'resource-control-analytics'
        };
      }
      
      setSummary(summaryData);
    } catch (error) {
      console.error('Error fetching EVM summary:', error);
    }
  }, [instance, accounts, jobNumber, progressScopes]);

  useEffect(() => {
    if (!jobNumber) return;
    authenticatedApiCall(instance, accounts, 'GET', `/resource-histogram/calendars?job_number=${jobNumber}`, null, { timeout: 120000 })
      .then((res) => {
        if (res.data?.success) {
          setP6Calendars(res.data.calendars || []);
          const seed = res.data.jobSeed || null;
          setJobCalendarSeed(seed);
          if (!calendarTouchedRef.current) {
            const preferred = seed?.preferredP6CalendarName;
            const hit = (res.data.calendars || []).find((c) => c.clndrName === preferred)
              || (res.data.calendars || []).find((c) => c.isDefault)
              || (res.data.calendars || [])[0];
            if (hit) setSelectedCalendarId(String(hit.clndrId));
          }
        }
      })
      .catch(() => {
        setP6Calendars([]);
      });
  }, [instance, accounts, jobNumber]); // eslint-disable-line react-hooks/exhaustive-deps

  const refreshP6Projects = useCallback(async () => {
    if (!jobNumber) return;
    try {
      const res = await authenticatedApiCall(
        instance,
        accounts,
        'GET',
        `/resource-histogram/${encodeURIComponent(jobNumber)}/projects`,
        null,
        { timeout: 120000 }
      );
      if (res.data?.success && Array.isArray(res.data.projects)) {
        setP6ProjectList(res.data.projects);
      }
    } catch {
      /* keep the last list */
    }
  }, [instance, accounts, jobNumber]);

  useEffect(() => {
    const incoming = analytics?.p6Projects;
    if (!incoming?.length) return;
    setP6ProjectList((prev) => mergeP6ProjectLists(prev, incoming));
  }, [analytics?.p6Projects]);

  useEffect(() => {
    refreshP6Projects();
  }, [refreshP6Projects]);

  // Picker hours follow the painted remaining curve (live 70,513), not a
  // last-good SUMTRSRC snapshot (70,505) that arrives while P6 is refreshing.
  useEffect(() => {
    if (liveHistMeta?.refreshing) return;
    const hrs = Number(liveRemaining?.totalHours);
    if (!(hrs > 0)) return;
    const ids = idsFromCurveProject(
      liveRemaining.project,
      liveRemaining.consolidatedFrom
    );
    if (ids.length !== 1) return;
    const id = ids[0];
    const rounded = Math.round(hrs);
    setP6ProjectList((prev) => {
      let changed = false;
      const next = prev.map((p) => {
        if (String(p.projId) !== String(id)) return p;
        if (Number(p.remainHours) === rounded) return p;
        changed = true;
        return { ...p, remainHours: rounded };
      });
      return changed ? next : prev;
    });
  }, [liveRemaining, liveHistMeta?.refreshing]);

  useEffect(() => {
    if (calendarTouchedRef.current) return;
    const projects = p6ProjectList.length ? p6ProjectList : (analytics?.p6Projects || []);
    if (!projects.length) return;
    const weekly = (selectedWeeklyProjIds[0] && projects.find((p) => String(p.projId) === String(selectedWeeklyProjIds[0])))
      || projects.find((p) => p.kind === 'working' && p.taskCount > 0)
      || projects.find((p) => p.kind === 'submitted' && p.taskCount > 0)
      || projects[0];
    const calId = weekly?.clndrId;
    if (!calId) return;
    const hit = (p6Calendars || []).find((c) => String(c.clndrId) === String(calId));
    if (hit) setSelectedCalendarId(String(hit.clndrId));
  }, [p6ProjectList, analytics?.p6Projects, selectedWeeklyProjIds, p6Calendars]);

  // Keep refs updated for auto-refresh
  fetchReportDataRef.current = fetchReportData;
  useEffect(() => () => {
    if (p6RetryTimerRef.current) clearTimeout(p6RetryTimerRef.current);
  }, []);
  fetchSummaryRef.current = fetchSummary;

  const xerPickKeyRef = useRef(null);
  const xerPickKey = `${[...selectedProposalProjIds].sort().join(',')}|${[...selectedWeeklyProjIds].sort().join(',')}`;

  useEffect(() => {
    autoSwitchedRef.current = false;
    hasHistRef.current = false;
    hasAnalyticsRef.current = false;
    legendGenRef.current += 1;
    setLegendBusy(false);
    setRefreshBusy(false);
    setAnalytics(null);
    setP6ProjectList([]);
    setLiveRemaining(null);
    setLiveP6Actual(null);
    setLiveMaestroActuals(null);
    setLiveProposal(null);
    setLiveWeeklyTarget(null);
    setLiveHistMeta(null);
    setSelectedResourceIds(null);
    setCollapsedActivityWbs(null);
    setDataSource(null);
    setPdfOptions(loadPdfOptions(jobNumber));
    setShowBudgetBaseline(!!openBudgetBaseline);
    xerPickKeyRef.current = null;
  }, [jobNumber, openBudgetBaseline]);

  useEffect(() => {
    if (!jobNumber) return;
    if (xerPickKeyRef.current == null) {
      xerPickKeyRef.current = xerPickKey;
      return;
    }
    if (xerPickKeyRef.current === xerPickKey) return;
    xerPickKeyRef.current = xerPickKey;
    hasHistRef.current = false;
    hasAnalyticsRef.current = false;
    setLiveRemaining(null);
    setLiveP6Actual(null);
    setLiveProposal(null);
    setLiveWeeklyTarget(null);
    setLiveLegendStacks(null);
    setRawChartData([]);
    setChartData([]);
    setLoading(true);
  }, [jobNumber, xerPickKey]);

  // ——— Per-job personalization: every filter, slicer size/spot, tab, calendar
  // and staffing control is remembered per job (jobs are not all alike).
  const viewPrefsHydratedRef = useRef(null);
  useEffect(() => {
    viewPrefsHydratedRef.current = null;
    const prefs = loadJobViewPrefs(jobNumber);
    if (prefs) {
      if (Array.isArray(prefs.disciplines) && prefs.disciplines.length) setSelectedDisciplines(prefs.disciplines);
      if (Array.isArray(prefs.families) && prefs.families.length) setSelectedFamilies(prefs.families);
      if (Array.isArray(prefs.types) && prefs.types.length) setSelectedTypes(prefs.types);
      if (Array.isArray(prefs.scopes) && prefs.scopes.length) setProgressScopes(prefs.scopes);
      if (prefs.filters && typeof prefs.filters === 'object') setSelectedFilters({ ...EMPTY_ACTUALS_FILTERS, ...prefs.filters });
      if (prefs.resourceIds !== undefined) setSelectedResourceIds(prefs.resourceIds);
      if (prefs.groupBy) setGroupBy(prefs.groupBy);
      if (prefs.weekStart) setWeekStart(prefs.weekStart);
      if (prefs.unitMode) setUnitMode(prefs.unitMode);
      if (prefs.chartSpan === 'scroll' || prefs.chartSpan === 'fit') setChartSpan(prefs.chartSpan);
      if (LEGEND_PLACES.some((p) => p.id === prefs.legendPlace)) setLegendPlace(prefs.legendPlace);
      if (prefs.visibleSeries) setVisibleSeries((prev) => ({ ...prev, ...prefs.visibleSeries }));
      if (prefs.curveMarks && typeof prefs.curveMarks === 'object') {
        setCurveMarks({
          bars: prefs.curveMarks.bars !== false,
          lines: prefs.curveMarks.lines !== false
        });
      }
      if (typeof prefs.showEvmIndices === 'boolean') setShowEvmIndices(prefs.showEvmIndices);
      setLegendBy(resolveJobLegendBy(prefs));
      if (prefs.fabMode) setFabMode(prefs.fabMode);
      if (prefs.dateFrom !== undefined) setDateFrom(prefs.dateFrom || '');
      if (prefs.dateTo !== undefined) setDateTo(prefs.dateTo || '');
      if (prefs.earnedSource) setEarnedSource(prefs.earnedSource);
      if (prefs.etcFormula) setEtcFormula(prefs.etcFormula);
      if (typeof prefs.assumeLastWeekSpent === 'boolean') setAssumeLastWeekSpent(prefs.assumeLastWeekSpent);
      if (typeof prefs.safetyOn === 'boolean') setSafetyOn(prefs.safetyOn);
      if (Number(prefs.safetyMenPer) >= 1) setSafetyMenPer(Number(prefs.safetyMenPer));
      if (prefs.safetyRule === SAFETY_RULE_FIRST_THEN || prefs.safetyRule === SAFETY_RULE_THREE || prefs.safetyRule === SAFETY_RULE_EVERY) {
        setSafetyRuleMode(prefs.safetyRule);
      }
      if (Number(prefs.safetyFirstAt) >= 1) setSafetyFirstAt(Number(prefs.safetyFirstAt));
      if (Number(prefs.safetyThenEvery) >= 1) setSafetyThenEvery(Number(prefs.safetyThenEvery));
      if (Number(prefs.safetyFinalEvery) >= 1) setSafetyFinalEvery(Number(prefs.safetyFinalEvery));
      if (typeof prefs.supervisionOn === 'boolean') setSupervisionOn(prefs.supervisionOn);
      if (Number(prefs.menPerFm) >= 1) setMenPerFm(Number(prefs.menPerFm));
      if (Number(prefs.fmPerGf) >= 1) setFmPerGf(Number(prefs.fmPerGf));
      if (typeof prefs.showStaffCounts === 'boolean') setShowStaffCounts(prefs.showStaffCounts);
      if (Number.isInteger(prefs.staffingRadius)) setStaffingRadius(prefs.staffingRadius);
      if (prefs.staffingHorizon) setStaffingHorizon(prefs.staffingHorizon);
      if (prefs.staffingView) setStaffingView(prefs.staffingView);
      if (prefs.staffingBaseline) setStaffingBaseline(prefs.staffingBaseline);
      if (prefs.calendarId) {
        calendarTouchedRef.current = true;
        setSelectedCalendarId(String(prefs.calendarId));
      }
      if (prefs.disciplineRanges && typeof prefs.disciplineRanges === 'object') setDisciplineRanges(prefs.disciplineRanges);
      if (typeof prefs.useJobSeed === 'boolean') setUseJobSeed(prefs.useJobSeed);
      if (prefs.filterLayout >= FILTER_LAYOUT_VERSION && prefs.visibleFilterPanels && typeof prefs.visibleFilterPanels === 'object') {
        setVisibleFilterPanels(prefs.visibleFilterPanels);
      }
      if (prefs.slicerLayout && typeof prefs.slicerLayout === 'object') setSlicerLayout(prefs.slicerLayout);
      if (prefs.visibleKpiCharts) setVisibleKpiCharts((prev) => ({ ...prev, ...prefs.visibleKpiCharts }));
      if (['histogram', 'staffing', 'kpis', 'progress'].includes(prefs.activeTab)) setActiveTab(prefs.activeTab);
    }
    viewPrefsHydratedRef.current = jobNumber;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobNumber]);

  useEffect(() => {
    if (!jobNumber || viewPrefsHydratedRef.current !== jobNumber) return undefined;
    const timer = setTimeout(() => {
      saveJobViewPrefs(jobNumber, {
        disciplines: selectedDisciplines,
        families: selectedFamilies,
        types: selectedTypes,
        scopes: progressScopes,
        filters: selectedFilters,
        resourceIds: selectedResourceIds,
        groupBy,
        weekStart,
        unitMode,
        chartSpan,
        legendPlace,
        visibleSeries,
        curveMarks,
        showEvmIndices,
        legendBy,
        legendDefaultRev: LEGEND_DEFAULT_REV,
        fabMode,
        dateFrom,
        dateTo,
        earnedSource,
        etcFormula,
        assumeLastWeekSpent,
        safetyOn,
        safetyMenPer,
        safetyRule: safetyRuleMode,
        safetyFirstAt,
        safetyThenEvery,
        safetyFinalEvery,
        supervisionOn,
        menPerFm,
        fmPerGf,
        showStaffCounts,
        staffingRadius,
        staffingHorizon,
        staffingView,
        staffingBaseline,
        calendarId: calendarTouchedRef.current ? selectedCalendarId : null,
        disciplineRanges,
        useJobSeed,
        filterLayout: FILTER_LAYOUT_VERSION,
        visibleFilterPanels,
        slicerLayout,
        visibleKpiCharts,
        activeTab
      });
    }, 400);
    return () => clearTimeout(timer);
  }, [jobNumber, selectedDisciplines, selectedFamilies, selectedTypes, progressScopes, selectedFilters,
    selectedResourceIds, groupBy, weekStart, unitMode, chartSpan, legendPlace, visibleSeries, curveMarks, showEvmIndices, legendBy, fabMode, dateFrom, dateTo,
    earnedSource, etcFormula, assumeLastWeekSpent, safetyOn, safetyMenPer, safetyRuleMode, safetyFirstAt, safetyThenEvery, safetyFinalEvery, supervisionOn, menPerFm, fmPerGf, showStaffCounts, staffingRadius,
    staffingHorizon, staffingView, staffingBaseline, selectedCalendarId, disciplineRanges, useJobSeed,
    visibleFilterPanels, slicerLayout, visibleKpiCharts, activeTab]);

  useEffect(() => {
    if (activeTab === 'filters') setActiveTab('histogram');
  }, [activeTab]);

  const selectedCalWeekHrs = (() => {
    const cal = (p6Calendars || []).find((c) => String(c.clndrId) === String(selectedCalendarId));
    return Number(cal?.weekHrCnt) || 40;
  })();

  // A 4x10 program = 40 h/person-week regardless of which generic calendar the
  // P6 files sit on (P6's "divide interval totals by"). Without this, 4x10 /
  // 5x10 / 6x10 scenarios all divide by the same calendar and show identical
  // manpower — the presentation bug of 2026-09-01.
  const scenarioHpw = Number(liveHistMeta?.scenarioHoursPerWeek) > 0
    ? Number(liveHistMeta.scenarioHoursPerWeek)
    : null;
  const baseWeekHrs = scenarioHpw || selectedCalWeekHrs;

  const rangesForDiscipline = (disc) => {
    const key = String(disc || '').trim();
    const parent = whatIfParentKey(key);
    for (const k of [key, key.toLowerCase(), parent]) {
      if (!k) continue;
      const custom = disciplineRanges[k];
      if (Array.isArray(custom) && custom.length) return custom;
    }
    return null;
  };

  const electricalSeedRanges = jobCalendarSeed?.electrical || null;
  const fieldSeedRanges = jobCalendarSeed?.field || null;
  const goodyearManualPbix = earnedSource === 'manual' && !!(electricalSeedRanges || fieldSeedRanges);
  const useGoodyearManualPbix = goodyearManualPbix || (!!useJobSeed && !!(electricalSeedRanges || fieldSeedRanges));

  const hpwFromRanges = (dateYmd, ranges) => {
    if (!Array.isArray(ranges)) return null;
    const hit = ranges.find((r) => dateYmd >= r.from && dateYmd <= r.to && Number(r.hoursPerWeek) > 0);
    return hit ? Number(hit.hoursPerWeek) : null;
  };

  const isElectricalDisc = (disc) => {
    const canon = String(disc || '').trim().toLowerCase();
    return canon.includes('electrical') || canon === 'ele' || canon === 'ei';
  };

  const hpwForDiscipline = (dateYmd, disc) => {
    const ranges = rangesForDiscipline(disc);
    if (ranges) {
      const hit = hpwFromRanges(dateYmd, ranges);
      if (hit) return hit;
    }
    if (useGoodyearManualPbix) {
      const seeded = isElectricalDisc(disc)
        ? hpwFromRanges(dateYmd, electricalSeedRanges)
        : hpwFromRanges(dateYmd, fieldSeedRanges);
      if (seeded) return seeded;
    }
    return baseWeekHrs;
  };

  const hpwForDate = (dateYmd) => {
    const discs = selectedDisciplines.length
      ? selectedDisciplines
      : (allDisciplineIds.length ? allDisciplineIds : CALENDAR_DISCIPLINES);
    const vals = [];
    for (const d of discs) {
      const ranges = rangesForDiscipline(d);
      if (!ranges) continue;
      const hit = ranges.find((r) => dateYmd >= r.from && dateYmd <= r.to);
      if (hit && Number(hit.hoursPerWeek) > 0) vals.push(Number(hit.hoursPerWeek));
    }
    if (vals.length === discs.length && vals.length > 0) {
      return vals.reduce((s, v) => s + v, 0) / vals.length;
    }
    if (discs.length === 1 && vals.length === 1) return vals[0];
    if (vals.length > 0) {
      const missing = discs.length - vals.length;
      return (vals.reduce((s, v) => s + v, 0) + baseWeekHrs * missing) / discs.length;
    }
    if (useGoodyearManualPbix && discs.length === 1) {
      const seeded = hpwForDiscipline(dateYmd, discs[0]);
      if (seeded) return seeded;
    }
    return baseWeekHrs;
  };

  const hoursOnWeek = (resources, bucket) => hoursInGrain(resources, bucket, groupBy, weekStart);

  // Man units follow the Group By grain: Week bars are man-weeks (1/week = 1
  // person), Month bars are MAN-MONTHS — hours ÷ (hpw × weeks in that month)
  // ≈ average crew that month — never a sum of the month's man-weeks.
  const daysInMonthOf = (ymd) => {
    const y = Number(String(ymd).slice(0, 4));
    const m = Number(String(ymd).slice(5, 7));
    if (!y || !m) return 30;
    return new Date(Date.UTC(y, m, 0)).getUTCDate();
  };
  const grainWeeksOf = (bucketYmd) => (groupBy === 'month' ? daysInMonthOf(bucketYmd) / 7 : 1);
  const manUnitLabel = groupBy === 'month' ? 'Man-months' : 'Man-weeks';

  const manWeeksOnDate = (curve, dateYmd) => {
    if (!curve?.byResource || !Object.keys(curve.byResource).length) return null;
    const shopField = selectedTypes.length === 1 ? selectedTypes[0] : null;
    const scope = progressScopes?.length === 1 ? progressScopes[0] : null;
    let mw = 0;
    Object.values(curve.byResource).forEach((r) => {
      if (!resourceMatchesFilters(r, {
        disciplines: selectedDisciplines,
        allDisciplines: allDisciplineIds,
        families: selectedFamilies,
        allFamilies: PBIX_FAMILIES.map((f) => f.id),
        shopField,
        scope: scope === 'all' ? null : scope,
        resourceIds: selectedResourceIds == null ? undefined : selectedResourceIds
      })) return;
      const hrs = hoursOnWeek([r], dateYmd);
      if (!hrs) return;
      mw += hrs / (hpwForDiscipline(dateYmd, r.discipline) || selectedCalWeekHrs || 40);
    });
    return Math.round(mw);
  };

  const updateDiscRange = (disc, idx, field, value) => {
    setDisciplineRanges((prev) => {
      const list = [...(prev[disc] || [{ from: '', to: '', hoursPerWeek: selectedCalWeekHrs }])];
      list[idx] = { ...list[idx], [field]: field === 'hoursPerWeek' ? Number(value) || 40 : value };
      return { ...prev, [disc]: list };
    });
  };

  const addDiscRange = (disc) => {
    setDisciplineRanges((prev) => ({
      ...prev,
      [disc]: [...(prev[disc] || []), { from: '', to: '', hoursPerWeek: selectedCalWeekHrs }]
    }));
  };

  const removeDiscRange = (disc, idx) => {
    setDisciplineRanges((prev) => {
      const list = [...(prev[disc] || [])];
      list.splice(idx, 1);
      const next = { ...prev };
      if (list.length) next[disc] = list;
      else delete next[disc];
      return next;
    });
  };

  const histogramAxisRows = rowsFromHistogramCurves(liveRemaining, liveProposal, liveWeeklyTarget);
  const axisRows = (chartData && chartData.length) ? chartData : histogramAxisRows;
  const displayChartData = (() => {
    if (unitMode !== 'manweeks') return axisRows || [];
    return (axisRows || []).map((row) => {
      const hpw = hpwForDate(row.date) || 40;
      // One decimal, not integer: schedule-tail weeks (punch list, demob,
      // small trades) are fractions of a man-week — rounding them to 0 made
      // the clip drop those weeks and the cum line quit short of the total
      // (26-LG-0174 B: 19,857 shown vs 20,523 in the XER).
      const sc = (v) => Math.round(((Number(v) || 0) / hpw) * 10) / 10;
      if (goodyearManualPbix) {
        const earnedMw = manWeeksOnDate(liveP6Actual, row.date);
        const remainMw = manWeeksOnDate(liveRemaining, row.date);
        const budgetMw = manWeeksOnDate(liveProposal, row.date);
        const spentMw = manWeeksOnDate(liveMaestroActuals, row.date);
        return {
          ...row,
          actual_period: spentMw == null ? sc(row.actual_period) : spentMw,
          earned_period: earnedMw == null ? sc(row.earned_period) : earnedMw,
          budget_period: budgetMw == null ? sc(row.budget_period) : budgetMw,
          remaining_period: remainMw == null ? sc(row.remaining_period) : remainMw,
          shop_earned_period: sc(row.shop_earned_period),
          field_earned_period: sc(row.field_earned_period)
        };
      }
      return {
        ...row,
        actual_period: sc(row.actual_period),
        earned_period: sc(row.earned_period),
        budget_period: sc(row.budget_period),
        remaining_period: sc(row.remaining_period),
        shop_earned_period: sc(row.shop_earned_period),
        field_earned_period: sc(row.field_earned_period)
      };
    });
  })();

  useEffect(() => {
    if (jobNumber) {
      fetchReportData();
      fetchSummary();
    }
  }, [jobNumber, fetchReportData, fetchSummary]);

  // Soft activity filter — keep chart mounted (avoids page jump)
  const activityFilterBoot = useRef(true);
  useEffect(() => {
    if (!jobNumber) return;
    if (activityFilterBoot.current) {
      activityFilterBoot.current = false;
      return;
    }
    fetchReportData({ soft: true, activityCodes: selectedActivityCodes });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedActivityCodes]);

  const applyManualPresentation = () => {
    setSpotlightSeries(null);
    setSelectedPhase('estimation');
  };

  const earnedSourceBoot = useRef(true);
  const manualPresBoot = useRef(false);
  useEffect(() => {
    if (earnedSource !== 'manual' || manualPresBoot.current) return;
    manualPresBoot.current = true;
    applyManualPresentation();
  }, [earnedSource]);
  useEffect(() => {
    if (!jobNumber) return;
    if (earnedSourceBoot.current) {
      earnedSourceBoot.current = false;
      return;
    }
    fetchReportDataRef.current?.({ soft: false });
    fetchSummaryRef.current?.();
  }, [earnedSource, jobNumber]);

  const assumeWeekBoot = useRef(true);
  useEffect(() => {
    if (!jobNumber) return;
    if (assumeWeekBoot.current) {
      assumeWeekBoot.current = false;
      return;
    }
    fetchReportDataRef.current?.({ soft: true });
  }, [assumeLastWeekSpent, jobNumber]);

  const fetchLegendStacks = useCallback(async (stackBy, opts = {}) => {
    if (!jobNumber || !stackBy) {
      setLiveLegendStacks(null);
      setLegendBusy(false);
      return;
    }
    const gen = ++legendGenRef.current;
    setLegendBusy(true);
    try {
      const liveParams = new URLSearchParams();
      liveParams.append('earned_source', earnedSourceRef.current);
      if (selectedProposalProjIds.length) liveParams.append('proposal_proj_id', selectedProposalProjIds.join(','));
      if (selectedWeeklyProjIds.length) liveParams.append('weekly_proj_id', selectedWeeklyProjIds.join(','));
      const activityCodes = selectedActivityCodesRef.current || [];
      if (activityCodes.length) liveParams.append('activities', activityCodes.join(','));
      liveParams.append('stack_by', stackBy);
      liveParams.append('legend_wait', '1');
      if (opts.force) liveParams.append('refresh', '1');
      const res = await authenticatedApiCall(
        instance, accounts, 'GET', `/resource-histogram/${jobNumber}?${liveParams.toString()}`, null, { timeout: 120000 }
      ).catch((e) => e);
      if (gen !== legendGenRef.current) return;
      if (res?.data?.success) {
        if (Array.isArray(res.data.legendFields)) setLegendFields(res.data.legendFields);
        if (res.data.legendStacks?.stackBy === stackBy) setLiveLegendStacks(res.data.legendStacks);
      }
    } finally {
      if (gen === legendGenRef.current) setLegendBusy(false);
    }
  }, [instance, accounts, jobNumber, selectedProposalProjIds, selectedWeeklyProjIds]);
  fetchLegendStacksRef.current = fetchLegendStacks;

  // Activity / UDF legends only — Resource ID and Name remap on the client.
  const stackByChoice = p6StackByParam(legendBy);
  useEffect(() => {
    if (!jobNumber) return;
    if (!needsBackendLegend(legendBy)) {
      setLiveLegendStacks(null);
      setLegendBusy(false);
      return;
    }
    setLiveLegendStacks((prev) => (prev?.stackBy === stackByChoice ? prev : null));
    fetchLegendStacksRef.current?.(stackByChoice);
  }, [stackByChoice, legendBy, jobNumber, selectedWeeklyProjIds, selectedProposalProjIds]);

  // Time Tracker / employee column legends — one column, weekly hours.
  useEffect(() => {
    if (!jobNumber || !String(legendBy).startsWith('tt:')) {
      setSpentColumnCurve(null);
      setSpentLegendBusy(false);
      return undefined;
    }
    const field = String(legendBy).slice(3);
    let cancel = false;
    setSpentLegendBusy(true);
    authenticatedApiCall(
      instance, accounts, 'GET',
      `/resource-histogram/${jobNumber}/spent-legend?field=${encodeURIComponent(field)}`,
      null,
      { timeout: 60000 }
    ).then((res) => {
      if (cancel) return;
      setSpentColumnCurve(res?.data?.byResource ? res.data : null);
    }).catch(() => {
      if (!cancel) setSpentColumnCurve(null);
    }).finally(() => {
      if (!cancel) setSpentLegendBusy(false);
    });
    return () => { cancel = true; };
  }, [legendBy, jobNumber, instance, accounts]);

  // Auto-refresh polling
  useEffect(() => {
    if (!autoRefresh || !jobNumber) return;
    const intervalMs = refreshInterval * 1000;
    const timer = setInterval(() => {
      if (document.visibilityState === 'visible') {
        fetchReportDataRef.current?.();
        fetchSummaryRef.current?.();
      }
    }, intervalMs);
    return () => clearInterval(timer);
  }, [autoRefresh, refreshInterval, jobNumber]);

  const handleRefresh = () => {
    setRefreshBusy(true);
    const legendP = needsBackendLegend(legendByRef.current)
      ? fetchLegendStacks(p6StackByParam(legendByRef.current), { force: true })
      : Promise.resolve();
    Promise.all([
      fetchReportData({ soft: true, forceP6: true }),
      Promise.resolve(fetchSummary({ forceP6: true })),
      legendP
    ]).finally(() => setRefreshBusy(false));
  };

  const changeSpentFeed = async (v) => {
    const labels = { auto: 'Auto', time_tracker: 'Time Tracker', maestro_dump: 'Maestro' };
    if (!window.confirm(`Change spent feed to “${labels[v] || v}” for job ${jobNumber}? This changes EVM actuals for everyone on this job.`)) {
      return false;
    }
    const prev = actualsSource;
    setActualsSource(v);
    try {
      await authenticatedApiCall(instance, accounts, 'PATCH', `/projects/by-job/${jobNumber}/actuals-source`, { actuals_source: v });
      handleRefresh();
      return true;
    } catch (err) {
      setActualsSource(prev);
      window.alert(err?.response?.status === 403
        ? 'Only a project admin can change the spent feed.'
        : (err?.response?.data?.message || 'Could not update spent feed.'));
      return false;
    }
  };

  const uploadMaestroActuals = async (file) => {
    if (!jobNumber || !file) return;
    try {
      setMaestroUploading(true);
      const formData = new FormData();
      formData.append('file', file);
      const res = await authenticatedApiCall(
        instance,
        accounts,
        'POST',
        `/resource-histogram/${jobNumber}/maestro-actuals?replace=${maestroReplace ? 'true' : 'false'}`,
        formData,
        { timeout: 180000 }
      );
      const hours = res?.data?.totalHours;
      const rows = res?.data?.rowCount;
      if (actualsSource !== 'maestro_dump') {
        await authenticatedApiCall(
          instance,
          accounts,
          'PATCH',
          `/projects/by-job/${jobNumber}/actuals-source`,
          { actuals_source: 'maestro_dump' }
        );
        setActualsSource('maestro_dump');
      }
      if (res?.data?.actuals) {
        setMaestroImportMeta(res.data.actuals.lastImport || {
          imported_at: new Date().toISOString(),
          total_hours: hours,
          filename: file.name
        });
      }
      handleRefresh();
      window.alert(
        `Imported ${Number(hours || 0).toLocaleString()} Maestro hours`
        + (rows ? ` (${rows} rows)` : '')
        + '. Premium / expense rows were skipped. Spent feed is Maestro until you switch to Time Tracker.'
      );
    } catch (err) {
      console.error('Maestro actuals import failed:', err);
      window.alert(err.response?.data?.error || err.message || 'Maestro import failed');
    } finally {
      setMaestroUploading(false);
      if (maestroFileRef.current) maestroFileRef.current.value = '';
    }
  };

  // Filter handling functions (same pattern as Actuals Report)
  const handleMultiSelectChange = (filterName, event) => {
    const options = event.target.options;
    const selected = [];
    for (let i = 0; i < options.length; i++) {
      if (options[i].selected) {
        selected.push(options[i].value);
      }
    }
    setSelectedFilters(prev => ({
      ...prev,
      [filterName]: selected
    }));
  };

  const toggleActualsFilter = (filterName, value) => {
    setSelectedFilters((prev) => {
      const cur = prev[filterName] || [];
      return {
        ...prev,
        [filterName]: cur.includes(value) ? cur.filter((x) => x !== value) : [...cur, value]
      };
    });
  };

  const selectAllActualsFilter = (filterName, options) => {
    setSelectedFilters((prev) => ({
      ...prev,
      [filterName]: (prev[filterName] || []).length === (options || []).length ? [] : [...(options || [])]
    }));
  };

  const clearFilters = () => {
    setSelectedFilters({ ...EMPTY_ACTUALS_FILTERS });
    setSelectedTypes(['shop', 'field']);
    setSelectedDisciplines(allDisciplineIds);
    setSelectedResourceIds(null);
    setSelectedFamilies(PBIX_FAMILIES.map((f) => f.id));
    setProgressScopes(PROGRESS_SCOPES.map((s) => s.id));
    setFabMode('both');
    setDateFrom('');
    setDateTo('');
    setSelectedActivityCodes([]);
    setActivitySearch('');
    setChartFilterWeek(null);
    setCollapsedActivityWbs(null);
    setGroupBy('week');
    setVisibleSeries({
      actual_period: true,
      earned_period: true,
      budget_period: true,
      actual_cumulative: true,
      earned_cumulative: true,
      budget_cumulative: true,
      remaining_period: true,
      remaining_cumulative: true
    });
  };

  // Type filter toggle functions
  const toggleType = (typeId) => {
    setSelectedTypes(prev => 
      prev.includes(typeId) 
        ? prev.filter(t => t !== typeId)
        : [...prev, typeId]
    );
  };

  const selectAllTypes = () => {
    if (selectedTypes.length === TYPE_ITEMS.length) {
      setSelectedTypes([]);
    } else {
      setSelectedTypes(TYPE_ITEMS.map(t => t.id));
    }
  };

  const formatNumber = (num) => {
    if (num === null || num === undefined) return '0';
    return parseFloat(num).toLocaleString('en-US', { maximumFractionDigits: 0 });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = parseChartDate(dateStr);
    if (!date) return String(dateStr).slice(0, 10);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const maestroWeekEndingLabel = (weekStart) => {
    const we = addDaysYmd(String(weekStart).slice(0, 10), -1);
    const d = parseChartDate(we);
    if (!d) return we;
    return d.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' });
  };

  const downloadCsvRows = (rows, filename) => {
    const csv = rows.map((r) => r.map((c) => (c == null ? '' : String(c))).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    a.click();
  };

  /** ↓ CSV downloads the open tab: histogram, staffing plan, KPI trends, or progress. */
  const handleCsvExport = () => {
    if (activeTab === 'staffing' && staffingCsvRef.current) {
      staffingCsvRef.current();
      return;
    }
    if (activeTab === 'kpis') {
      const headers = ['date', 'per_spi', 'per_cpi', 'cum_spi', 'cum_cpi', 'per_sv', 'per_cv',
        'cum_sv', 'cum_cv', 'per_spent', 'per_earned', 'per_planned', 'cum_spent', 'cum_earned', 'cum_planned'];
      downloadCsvRows(
        [headers, ...(kpiTrendData || []).map((r) => headers.map((h) => r[h]))],
        `evm-${jobNumber}-kpis.csv`
      );
      return;
    }
    if (activeTab === 'progress' && analytics?.progressTable?.rows?.length) {
      const weeks = (analytics.progressTable.weeks || [])
        .filter((w) => (!dateFrom || w >= dateFrom) && (!dateTo || w <= dateTo));
      const rows = [['Metric', ...weeks]];
      analytics.progressTable.rows
        .filter((row) => progressScopes.includes(row.scope))
        .forEach((row) => {
          progressMetricKeys.forEach(([label, key]) => {
            rows.push([`${row.label} - ${label} %`, ...weeks.map((w) => row.weeks?.[w]?.[key] ?? '')]);
          });
        });
      downloadCsvRows(rows, `evm-${jobNumber}-progress.csv`);
      return;
    }
    const rows = chartData.map((r) => ({
      date: r.date,
      per_spent: r.actual_period,
      per_earned: r.earned_period,
      per_proposal: r.budget_period,
      per_forecast: r.forecast_period,
      cum_spent: r.actual_cumulative,
      cum_earned: r.earned_cumulative,
      cum_proposal: r.budget_cumulative,
      cum_forecast: r.forecast_cumulative
    }));
    const headers = Object.keys(rows[0] || { date: '' });
    downloadCsvRows(
      [headers, ...rows.map((r) => headers.map((h) => r[h]))],
      `evm-${jobNumber}-histogram.csv`
    );
  };

  /** One title for every estimate: number, name, then the report. */
  const pdfTitle = [
    liveHistMeta?.estimateNumber || estimateNumber || jobNumber,
    liveHistMeta?.estimateName,
    'Preliminary Resource Histogram'
  ].filter(Boolean).join(' - ');

  /** Filter cards currently on the pane — what the PDF dialog offers to print. */
  const pdfCardOptions = FILTER_PANEL_DEFS
    .filter((f) => isSlicerVisible(visibleFilterPanels, f.id, pickerDefaultOn(f)))
    .map((f) => ({ id: f.id, label: f.label }));

  const handleDownloadPdf = async (opts = pdfOptions) => {
    if (!reportPrintRef.current || pdfBusy) return;
    setPdfBusy(true);
    const filtersWereOpen = showFilters;
    try {
      const o = { ...DEFAULT_PDF_OPTIONS, ...(opts || {}) };
      const title = String(o.title || '').trim() || pdfTitle;
      const clauses = o.clauses || [];

      const [{ default: jsPDF }, { default: html2canvas }] = await Promise.all([
        import('jspdf'),
        import('html2canvas')
      ]);
      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      const pageW = doc.internal.pageSize.getWidth();
      const pageH = doc.internal.pageSize.getHeight();
      const { y: letterheadY } = await applyLetterhead(doc, {
        instance, accounts, jobNumber, margin: 10, omitJobLine: !o.showJobLine
      });

      doc.setFontSize(12);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(15, 23, 42);
      const titleLines = doc.splitTextToSize(title, pageW - 20);
      titleLines.forEach((line, i) => {
        if (o.titleAlign === 'center') doc.text(line, pageW / 2, letterheadY + i * 5.5, { align: 'center' });
        else doc.text(line, 10, letterheadY + i * 5.5);
      });

      // Footnotes sit on the bottom of the page, so the chart gets whatever
      // height is left after the clauses the user kept.
      const clauseLines = [];
      doc.setFontSize(7.5);
      doc.setFont(undefined, 'normal');
      clauses
        .map((c) => String(c).trim())
        .filter(Boolean)
        .forEach((clause) => {
          doc.splitTextToSize(`• ${clause}`, pageW - 20).forEach((line) => clauseLines.push(line));
        });
      const stampY = pageH - 5;
      const clauseLead = 3.3;
      const footerTop = stampY - 5 - (clauseLines.length ? clauseLines.length * clauseLead + 1 : 0);

      const top = letterheadY + (titleLines.length - 1) * 5.5 + 6;
      const maxW = pageW - 16;
      const maxH = footerTop - top - 4;

      const snapNode = async (node, backgroundColor) => {
        if (!node) return null;
        const canvas = await html2canvas(node, {
          scale: PDF_SNAP_SCALE, useCORS: true, logging: false, backgroundColor
        });
        return { data: canvas.toDataURL('image/jpeg', PDF_JPEG_QUALITY), w: canvas.width, h: canvas.height };
      };
      const addJpeg = (img, x, y, w, h) => {
        doc.addImage(img.data, 'JPEG', x, y, w, h);
      };

      let figuresImg = null;
      if (o.figuresMode === 'top' || o.figuresMode === 'bottom') {
        setPdfForceFigures(true);
        setShowFilterPicker(false);
        await new Promise((r) => setTimeout(r, 260));
        figuresImg = await snapNode(figuresPanelRef.current, '#ffffff');
        setPdfForceFigures(false);
      }

      const printFilters = pdfPrintsFilters(o.filtersMode, pdfCardOptions, o.filterCards);
      const filtersOnRight = printFilters && o.filtersMode === 'right';
      const filtersStacked = printFilters && (o.filtersMode === 'top' || o.filtersMode === 'bottom');
      let filtersImg = null;
      if (printFilters) {
        const chosen = o.filterCards || {};
        const override = {};
        FILTER_PANEL_DEFS.forEach((f) => {
          const onScreen = isSlicerVisible(visibleFilterPanels, f.id, pickerDefaultOn(f));
          override[f.id] = onScreen && chosen[f.id] !== false;
        });
        setPdfPanelsOverride(override);
        setPdfCaptureFilters(true);
        setShowFilters(true);
        setShowFilterPicker(false);
        await new Promise((r) => setTimeout(r, 260));
        filtersImg = await snapNode(
          filtersPaneInnerRef.current || filtersPanelRef.current,
          '#f8fafc'
        );
        setPdfPanelsOverride(null);
        setPdfCaptureFilters(false);
      }

      // Extras share half the free height when stacked; a right-hand filter
      // column sits beside the chart (same as the on-screen drawer).
      const fitExtra = (img, budget, width = maxW) => {
        if (!img) return { w: 0, h: 0 };
        const naturalH = (width * img.h) / img.w;
        const h = Math.min(naturalH, budget);
        return { w: width * (h / naturalH), h };
      };
      const extraBudget = maxH * 0.5;
      const figuresCap = !printFilters
        ? Math.min(14, maxH * 0.1)
        : (figuresImg && filtersStacked ? extraBudget * 0.28 : extraBudget * 0.35);
      const figuresFit = fitExtra(figuresImg, figuresCap);
      const filtersFit = filtersStacked
        ? fitExtra(filtersImg, extraBudget - (figuresFit.h || 0))
        : { w: 0, h: 0 };
      const figuresW = figuresFit.w;
      const figuresH = figuresFit.h;
      const filtersW = filtersFit.w;
      const filtersH = filtersFit.h;
      const extraGap = (figuresImg ? 2 : 0) + (filtersStacked ? 2 : 0);
      const chartMaxH = maxH - figuresH - filtersH - extraGap;
      const filterColW = filtersOnRight && filtersImg
        ? Math.min(68, Math.max(54, maxW * 0.25))
        : 0;
      const filterGap = filterColW ? 3 : 0;
      const chartAreaW = maxW - filterColW - filterGap;

      // 2) Chart capture — filters and figures collapsed so they are not double-printed.
      setShowFilters(false);
      setShowFilterPicker(false);
      setPdfForceFigures(false);
      await new Promise((r) => setTimeout(r, 220));
      let chartStretched = false;
      const snapEl = () => histogramChartRef.current || reportPrintRef.current;
      if (o.chartFill !== false && snapEl() && chartMaxH > 20) {
        const node = snapEl();
        const wPx = node.offsetWidth || 1;
        const targetHPx = Math.round((wPx * chartMaxH) / Math.max(chartAreaW, 1));
        setPdfChartHeight(Math.max(560, Math.min(PDF_CHART_STRETCH_MAX, targetHPx)));
        chartStretched = true;
        await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
        await new Promise((r) => setTimeout(r, 420));
      }
      const canvas = await html2canvas(snapEl(), {
        scale: PDF_SNAP_SCALE,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff'
      });
      if (chartStretched) setPdfChartHeight(null);
      const imgData = canvas.toDataURL('image/jpeg', PDF_JPEG_QUALITY);

      // 3) Compose: figures full-width, chart left, filters right (or stacked).
      // The curve always uses the leftover height after header / figures / footnotes.
      const placeExtra = (img, w, h, y) => {
        addJpeg(img, 8, y, w, h);
        return y + h + 2;
      };
      let y = top;
      if (figuresImg && o.figuresMode === 'top') y = placeExtra(figuresImg, figuresW, figuresH, y);
      if (filtersImg && o.filtersMode === 'top') y = placeExtra(filtersImg, filtersW, filtersH, y);
      const chartTop = y;
      const leftoverH = Math.max(24, footerTop - y - 1);
      const fillChart = o.chartFill !== false || !printFilters;
      const { w: drawW, h: drawH } = pdfChartDrawSize(
        canvas.width,
        canvas.height,
        chartAreaW,
        leftoverH,
        { fill: fillChart }
      );
      addJpeg({ data: imgData }, 8, y, drawW, drawH);
      let rightFiltersH = 0;
      if (filtersOnRight && filtersImg) {
        const naturalH = (filterColW * filtersImg.h) / filtersImg.w;
        rightFiltersH = Math.min(naturalH, leftoverH);
        const fW = naturalH > leftoverH ? filterColW * (leftoverH / naturalH) : filterColW;
        addJpeg(filtersImg, 8 + chartAreaW + filterGap, chartTop, fW, rightFiltersH);
      }
      y = chartTop + Math.max(drawH, rightFiltersH);
      if (figuresImg && o.figuresMode === 'bottom') y = placeExtra(figuresImg, figuresW, figuresH, y + 2);
      if (filtersImg && o.filtersMode === 'bottom') {
        addJpeg(filtersImg, 8, y + 2, filtersW, filtersH);
      }

      doc.setDrawColor(203, 213, 225);
      doc.setLineWidth(0.2);
      doc.line(10, footerTop, pageW - 10, footerTop);
      let fy = footerTop + 3.5;
      doc.setFontSize(7.5);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(71, 85, 105);
      clauseLines.forEach((line) => {
        doc.text(line, 10, fy);
        fy += clauseLead;
      });

      const printed = new Date().toLocaleString('en-CA', {
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', hour12: false
      });
      doc.setFontSize(7);
      doc.setTextColor(148, 163, 184);
      doc.text(`Printed ${printed}`, 10, stampY);
      // Plain text on purpose — the reference must not be a live hyperlink.
      const link = typeof window !== 'undefined' ? window.location.href.split('#')[0] : '';
      if (link) {
        doc.text(doc.splitTextToSize(link, pageW - 90)[0], pageW - 10, stampY, { align: 'right' });
      }

      doc.save(exportFilename({ job: jobNumber, reportSlug: 'Preliminary_Resource_Histogram', ext: 'pdf' }));
      setPdfOpen(false);
    } catch (err) {
      console.error('PDF export failed:', err);
      window.print();
    } finally {
      setShowFilters(filtersWereOpen);
      setPdfChartHeight(null);
      setPdfPanelsOverride(null);
      setPdfForceFigures(null);
      setPdfCaptureFilters(false);
      setPdfBusy(false);
    }
  };

  // Settings menu closes on outside click. Filter dropdowns / + Add slicer
  // use AnchoredPopover so a P6 refresh cannot remount them closed.
  useEffect(() => {
    if (!showSettings) return undefined;
    const onDoc = (e) => {
      if (settingsRef.current && !settingsRef.current.contains(e.target)) {
        setShowSettings(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [showSettings]);

  const dataDateYmd = String(liveHistMeta?.dataDate || analytics?.meta?.dataDate || '').slice(0, 10) || null;

  const staffingBaselineCurve = staffingBaseline === 'budget' ? liveProposal : liveWeeklyTarget;
  const staffingBaselineLabel = (() => {
    const proj = staffingBaselineCurve?.project;
    const kind = staffingBaseline === 'budget' ? 'Budget' : 'Current';
    if (!proj) return `${kind} schedule — pick the exact XER in Filters`;
    const date = proj.dataDate ? ` · ${String(proj.dataDate).slice(0, 10)}` : '';
    return `${kind} · ${proj.projShortName || ''}${date}`;
  })();

  // Men conversion precedence: what-if range (per discipline) > scenario crew
  // calendar (4x10 = 40 h/wk) > the resource's own P6 calendar (weekHrCnt from
  // the live curves) > the selected calendar.
  const staffingHpw = useCallback((dateYmd, rec) => {
    const ranges = rangesForDiscipline(rec?.discipline) || [];
    const hit = ranges.find((r) => r.from && r.to && dateYmd >= r.from && dateYmd <= r.to && Number(r.hoursPerWeek) > 0);
    if (hit) return Number(hit.hoursPerWeek);
    if (earnedSource === 'manual' || useJobSeed) {
      const recDisc = String(rec?.discipline || '').trim().toLowerCase();
      const isElec = recDisc.includes('electrical') || recDisc === 'ele' || recDisc === 'ei';
      const seedRanges = isElec ? jobCalendarSeed?.electrical : jobCalendarSeed?.field;
      const seeded = Array.isArray(seedRanges)
        ? seedRanges.find((r) => dateYmd >= r.from && dateYmd <= r.to && Number(r.hoursPerWeek) > 0)
        : null;
      if (seeded) return Number(seeded.hoursPerWeek);
    }
    if (scenarioHpw) return scenarioHpw;
    if (Number(rec?.weekHrCnt) > 0) return Number(rec.weekHrCnt);
    return selectedCalWeekHrs;
  }, [disciplineRanges, selectedCalWeekHrs, scenarioHpw, earnedSource, useJobSeed, jobCalendarSeed]);

  const resourceCatalog = useMemo(() => {
    const merged = mergeRemainingWithActuals(liveRemaining, liveP6Actual, dataDateYmd);
    const byResource = { ...merged.byResource };
    Object.values(staffingBaselineCurve?.byResource || {}).forEach((r) => {
      const id = r?.resourceId;
      if (id && !byResource[id]) byResource[id] = r;
    });
    return { ...merged, byResource };
  }, [liveRemaining, liveP6Actual, staffingBaselineCurve, dataDateYmd]);

  const supervisionStaffingModel = useMemo(() => {
    const shopField = selectedTypes.length === 1 ? selectedTypes[0] : null;
    const scope = progressScopes?.length === 1 ? progressScopes[0] : null;
    return buildStaffingModel({
      baseline: staffingBaselineCurve,
      remaining: liveRemaining,
      actuals: liveP6Actual,
      dataDate: dataDateYmd,
      radius: staffingRadius,
      forecastFactor: 1,
      weekHoursFn: (d, meta) => staffingHpw(d, meta),
      filters: {
        disciplines: selectedDisciplines,
        allDisciplines: allDisciplineIds,
        families: selectedFamilies,
        allFamilies: PBIX_FAMILIES.map((f) => f.id),
        shopField,
        scope: scope === 'all' ? null : scope,
        resourceIds: selectedResourceIds == null ? undefined : selectedResourceIds
      }
    });
  }, [
    staffingBaselineCurve, liveRemaining, liveP6Actual, dataDateYmd, staffingRadius,
    staffingHpw, selectedDisciplines, allDisciplineIds, selectedFamilies, selectedTypes,
    progressScopes, selectedResourceIds
  ]);

  const supervisionMenBy = useMemo(
    () => applyStaffingAdjust(supervisionStaffingModel, loadStaffingAdjust(jobNumber)),
    [supervisionStaffingModel, jobNumber, supervisionPlanOpen]
  );

  const extrasMonths = useMemo(
    () => projectMonthKeys(supervisionStaffingModel?.weeks || []),
    [supervisionStaffingModel]
  );

  const resourceOptions = useMemo(() => {
    const shopField = selectedTypes.length === 1 ? selectedTypes[0] : null;
    const scope = progressScopes?.length === 1 ? progressScopes[0] : null;
    const rows = Object.values(resourceCatalog.byResource || {})
      .filter((r) => resourceMatchesFilters(r, {
        disciplines: selectedDisciplines,
        allDisciplines: allDisciplineIds,
        families: selectedFamilies,
        allFamilies: PBIX_FAMILIES.map((f) => f.id),
        shopField,
        scope: scope === 'all' ? null : scope
      }))
      .map((r) => ({
        id: r.resourceId,
        label: r.resourceId,
        p6Name: r.resourceName || r.rsrc_name || r.resourceId,
        color: resourceColor(r),
        discipline: r.discipline
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
    const seen = new Set();
    return rows.filter((r) => {
      if (seen.has(r.id)) return false;
      seen.add(r.id);
      return true;
    });
  }, [resourceCatalog, selectedDisciplines, selectedFamilies, selectedTypes, progressScopes]);

  const allResourceIds = resourceOptions.map((r) => r.id);
  const effectiveResourceIds = selectedResourceIds == null ? allResourceIds : selectedResourceIds;
  const allResourcesOn = selectedResourceIds == null
    || (allResourceIds.length > 0 && selectedResourceIds.length === allResourceIds.length);

  const toggleResourceId = (id) => {
    if (!id) return;
    const raw = String(id);
    if (raw.startsWith('d:')) {
      toggleDiscipline(raw.slice(2));
      return;
    }
    const rid = raw.startsWith('r:') ? raw.slice(2) : raw;
    setSelectedResourceIds((prev) => {
      const current = prev == null ? allResourceIds : prev;
      const next = current.includes(rid) ? current.filter((x) => x !== rid) : [...current, rid];
      if (next.length === allResourceIds.length) return null;
      return next;
    });
  };

  const selectAllResources = () => {
    setSelectedResourceIds(allResourcesOn ? [] : null);
  };

  const resourceNameOptions = useMemo(() => {
    const map = new Map();
    resourceOptions.forEach((r) => {
      const name = r.p6Name || r.id;
      if (!map.has(name)) map.set(name, { id: name, label: name, color: r.color, resourceIds: [] });
      map.get(name).resourceIds.push(r.id);
    });
    return [...map.values()].sort((a, b) => a.label.localeCompare(b.label));
  }, [resourceOptions]);

  const selectedResourceNames = resourceNameOptions
    .filter((n) => n.resourceIds.length && n.resourceIds.every((id) => effectiveResourceIds.includes(id)))
    .map((n) => n.id);
  const allResourceNamesOn = resourceNameOptions.length > 0
    && selectedResourceNames.length === resourceNameOptions.length;

  const toggleResourceName = (name) => {
    const ids = resourceNameOptions.find((n) => n.id === name)?.resourceIds || [];
    if (!ids.length) return;
    setSelectedResourceIds((prev) => {
      const current = prev == null ? allResourceIds : prev;
      const allOn = ids.every((id) => current.includes(id));
      const next = allOn
        ? current.filter((id) => !ids.includes(id))
        : [...new Set([...current, ...ids])];
      if (next.length === allResourceIds.length) return null;
      return next;
    });
  };

  const changeLegendBy = (v) => {
    setLegendBy(v);
    saveLegendBy(v);
    setSpotlightSeries(null);
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div style={{
          background: 'white',
          padding: '12px',
          border: '1px solid #d1d5db',
          borderRadius: '8px',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
        }}>
          <p style={{ fontWeight: '600', marginBottom: '4px', color: '#1f2937' }}>
            Week starting {formatDate(label)}
          </p>
          {(visibleSeries.actual_period || visibleSeries.actual_cumulative) && (
            <p style={{ margin: '0 0 8px', fontSize: '0.75rem', color: '#64748b' }}>
              Maestro week ending {maestroWeekEndingLabel(label)} (Saturday → next Sunday)
            </p>
          )}
          {payload[0]?.payload?.spent_assumed && (
            <p style={{ margin: '0 0 6px', fontSize: '0.75rem', color: '#b45309' }}>
              Spent this week assumed = last week (Maestro lag)
            </p>
          )}
          {payload.map((entry, index) => {
            if (!entry || entry.name === '_remainTotal' || entry.name === '_budgetTotal' || entry.name === '_earnedTotal' || entry.name === '_spentTotal' || entry.value == null) return null;
            if (entry.dataKey === 'evm_spi' || entry.dataKey === 'evm_cpi') return null;
            const isCum = /cum\.|cumulative/i.test(entry.name || '');
            const unit = unitMode === 'manweeks' && !isCum
              ? (groupBy === 'month' ? ' man-months' : ' man-weeks')
              : ' hrs';
            return (
            <p key={index} style={{ color: entry.color, margin: '4px 0', fontSize: '0.875rem' }}>
              {entry.name}: {formatNumber(entry.value)}{unit}
            </p>
            );
          })}
          {showEvmIndices && payload[0]?.payload && (() => {
            const row = payload[0].payload;
            const fmtIdx = (v) => (v == null || !Number.isFinite(Number(v)) ? '—' : Number(v).toFixed(2));
            const fmtVar = (v) => {
              if (v == null || !Number.isFinite(Number(v))) return '—';
              const n = Math.round(Number(v));
              return `${n > 0 ? '+' : ''}${n.toLocaleString()} hrs`;
            };
            return (
              <div style={{ marginTop: 8, paddingTop: 6, borderTop: '1px solid #e2e8f0', fontSize: '0.8rem' }}>
                <p style={{ margin: '2px 0', color: '#0e7490' }}>SPI: {fmtIdx(row.evm_spi)}</p>
                <p style={{ margin: '2px 0', color: '#7c3aed' }}>CPI: {fmtIdx(row.evm_cpi)}</p>
                <p style={{ margin: '2px 0', color: '#0369a1' }}>SV: {fmtVar(row.evm_sv)}</p>
                <p style={{ margin: '2px 0', color: '#c2410c' }}>CV: {fmtVar(row.evm_cv)}</p>
              </div>
            );
          })()}
        </div>
      );
    }
    return null;
  };

  // Permission check - show loading or access denied
  if (permissionLoading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p style={{ color: '#6b7280' }}>Checking permissions...</p>
      </div>
    );
  }

  if (!hasPermission) {
    return <AccessDenied />;
  }

  if (!jobNumber) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <h2 style={{ color: '#6b7280' }}>No project selected</h2>
        <p style={{ color: '#9ca3af' }}>Please select a project from the dashboard to view the EVM Report.</p>
      </div>
    );
  }

  const btnH = 36;
  const btnBase = {
    height: btnH,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.4rem',
    padding: '0 0.85rem',
    borderRadius: '8px',
    fontSize: '0.8rem',
    fontWeight: 600,
    cursor: 'pointer',
    boxSizing: 'border-box',
    whiteSpace: 'nowrap',
    lineHeight: 1,
    background: '#ffffff',
    color: '#374151',
    border: '1px solid #d1d5db'
  };
  const chromeBtn = {
    padding: '0.15rem 0.45rem',
    background: 'white',
    color: '#374151',
    border: '1px solid #d1d5db',
    borderRadius: '5px',
    fontSize: '0.68rem',
    fontWeight: 600,
    cursor: 'pointer'
  };
  const sectionTitle = {
    fontSize: '0.62rem',
    fontWeight: 800,
    color: '#64748b',
    letterSpacing: 0.4,
    textTransform: 'uppercase',
    margin: '8px 0 4px'
  };
  const settingsRule = {
    borderTop: '1px solid #e2e8f0',
    marginTop: 10,
    paddingTop: 10
  };
  const itemTitle = {
    ...sectionTitle,
    margin: '0 0 6px'
  };
  const btnActive = {
    ...btnBase,
    background: '#f1f5f9',
    color: '#0f172a',
    border: '1px solid #94a3b8'
  };

  const seriesOpacity = (key) => {
    if (!spotlightSeries) return 1;
    return spotlightSeries === key ? 1 : 0.12;
  };

  const filterCard = {
    background: 'white',
    padding: '0.4rem 0.5rem',
    borderRadius: '6px',
    border: '1px solid #e2e8f0',
    minWidth: '110px'
  };
  const moreCard = {
    ...filterCard,
    width: 148,
    minWidth: 148,
    maxWidth: 148,
    height: 'auto',
    minHeight: 0,
    flex: 'none',
    overflow: 'visible',
    boxSizing: 'border-box',
    padding: '0.3rem 0.4rem'
  };
  const checkRow = {
    display: 'flex',
    alignItems: 'center',
    gap: 5,
    fontSize: '0.72rem',
    cursor: 'pointer',
    color: '#1e2937',
    lineHeight: 1.35,
    margin: 0
  };
  const filterCardTitle = {
    fontWeight: 600,
    color: '#475569',
    fontSize: '0.68rem',
    marginBottom: '0.28rem',
    paddingBottom: '0.2rem',
    borderBottom: '1px solid #e5e7eb'
  };
  const filterSelect = {
    width: '100%',
    height: 26,
    fontSize: '0.7rem',
    borderRadius: 5,
    border: '1px solid #d1d5db',
    background: '#fff'
  };
  const p6Projects = p6ProjectList.length ? p6ProjectList : (analytics?.p6Projects || []);
  const kpiTrendData = aggregateKpiTrend(analytics?.spiCpiTrend, groupBy, weekStart);
  const pmiServer = analytics?.pmiSummary || summary?.pmiSummary;
  // Resource ID is applied on the chart only. Rebuild the cards from those
  // same resources so unchecking one moves BAC / CPI with the bars.
  const figureFilter = {
    disciplines: selectedDisciplines,
    allDisciplines: allDisciplineIds,
    families: selectedFamilies,
    allFamilies: PBIX_FAMILIES.map((f) => f.id),
    shopField: selectedTypes.length === 1 ? selectedTypes[0] : null,
    scope: progressScopes?.length === 1 && progressScopes[0] !== 'all' ? progressScopes[0] : null
  };
  const pmiBase = (!allResourcesOn && pmiServer)
    ? pmiForResourceSelection({
      proposal: liveProposal,
      weeklyTarget: liveWeeklyTarget,
      earned: liveP6Actual,
      remaining: liveRemaining,
      actuals: liveMaestroActuals,
      selectedIds: effectiveResourceIds,
      accept: (r) => resourceMatchesFilters(r, figureFilter),
      dataDate: dataDateYmd || pmiServer.data_date,
      serverPmi: pmiServer
    })
    : pmiServer;
  const pmiForecast = applyEtcFormula(pmiBase, etcFormula);
  // Staffing forecast: scale P6 remaining so its total equals the selected FAC formula's ETC.
  const staffingForecastFactor = (() => {
    if (etcFormula === 'p6_remain') return 1;
    const remainTotal = Number(pmiBase?.remaining_hours) || Number(liveRemaining?.totalHours) || 0;
    if (!(remainTotal > 0) || pmiForecast?.etc == null) return 1;
    const f = pmiForecast.etc / remainTotal;
    return Number.isFinite(f) && f > 0 ? f : 1;
  })();

  const EVM_TABS = [
    { id: 'histogram', label: 'Histogram' },
    { id: 'staffing', label: 'Staffing' },
    { id: 'kpis', label: 'KPIs' },
    { id: 'progress', label: 'Progress' }
  ];

  const allActivities = analytics?.activities || [];
  const activitySearchQ = activitySearch.trim().toLowerCase();
  const weekHitActivities = chartFilterWeek
    ? allActivities.filter((a) => activityOverlapsWeek(a, chartFilterWeek))
    : null;
  const weekDisc = weekHitActivities && new Set(weekHitActivities.map((a) => a.discipline).filter(Boolean));
  const weekTypes = weekHitActivities && new Set(weekHitActivities.map((a) => a.shop_or_field).filter(Boolean));
  const weekFamilies = weekHitActivities && new Set(weekHitActivities.flatMap(familiesForActivity));
  const slicerPaint = (inWeek) => ({
    opacity: !chartFilterWeek || inWeek ? 1 : 0.22,
    transition: 'opacity 0.15s'
  });

  const displayedActivities = allActivities.filter((a) => {
    if (chartFilterWeek && !activityOverlapsWeek(a, chartFilterWeek)) return false;
    if (!activitySearchQ) return true;
    const hay = [
      a.activity_code,
      a.activity_name,
      a.wbs_path,
      a.wbs_name,
      a.start,
      a.finish,
      a.budget_hours,
      a.actual_hours,
      a.remaining_hours
    ].join(' ').toLowerCase();
    return hay.includes(activitySearchQ);
  });
  const hasActivityWbs = allActivities.some((a) => a.wbs_path || a.wbs_name);
  const activityOutline = hasActivityWbs
    ? buildWbsOutline(stripSharedWbsRoot(displayedActivities.map((a) => ({
      ...a,
      activity_id: a.activity_code,
      start_date: a.start,
      finish_date: a.finish
    }))))
    : [];
  const defaultCollapsedWbs = new Set(collectWbsPaths(activityOutline));
  const activityWbsCollapsed = activitySearchQ || chartFilterWeek
    ? new Set()
    : (collapsedActivityWbs || defaultCollapsedWbs);
  const visibleActivityRows = hasActivityWbs
    ? filterCollapsedRows(activityOutline, activityWbsCollapsed)
    : [];

  const clearActivitySlicer = () => {
    setSelectedActivityCodes([]);
    setActivitySearch('');
    setChartFilterWeek(null);
    setCollapsedActivityWbs(null);
  };

  const setPanelVisible = (id, on) => {
    setVisibleFilterPanels((prev) => {
      const next = { ...prev, [id]: on };
      saveVisiblePanels(next);
      return next;
    });
    if (on) {
      setSlicerLayout((prev) => {
        if (prev[id]) return prev;
        const next = { ...prev, [id]: { ...FALLBACK_SLICER } };
        saveSlicerLayout(next);
        return next;
      });
    }
  };
  const toggleActivityCode = (code) => {
    setSelectedActivityCodes((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
    );
  };
  const toggleActivityCodes = (codes) => {
    const list = (codes || []).filter(Boolean);
    if (!list.length) return;
    setSelectedActivityCodes((prev) => {
      const next = new Set(prev);
      const allOn = list.every((c) => next.has(c));
      list.forEach((c) => (allOn ? next.delete(c) : next.add(c)));
      return [...next];
    });
  };
  const toggleActivityWbsCollapse = (path) => {
    setCollapsedActivityWbs((prev) => {
      const next = new Set(prev || defaultCollapsedWbs);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  };

  const handleChartWeekClick = (data) => {
    const week = data?.payload?.date || data?.date || null;
    if (!week) return;
    // Highlight only — do not refetch / shrink the curves.
    setChartFilterWeek((prev) => (prev === week ? null : week));
  };

  const paintOpacity = (date, seriesKey) => {
    const base = seriesOpacity(seriesKey);
    if (!chartFilterWeek) return base;
    return date === chartFilterWeek ? base : Math.max(0.14, base * 0.18);
  };

  const paintedCells = (color, seriesKey) =>
    chartSeriesData.map((row) => (
      <Cell
        key={`${seriesKey}-${row.date}`}
        fill={color}
        fillOpacity={paintOpacity(row.date, seriesKey)}
      />
    ));

  const barValueLabel = (
    <LabelList
      position="top"
      offset={2}
      formatter={(v) => formatBarTotal(v, unitMode)}
      style={{ fontSize: 8, fill: '#475569', fontWeight: 600, pointerEvents: 'none' }}
    />
  );

  const stackCountLabel = (dataKey) => (
    showStaffCounts ? (
      <LabelList
        dataKey={dataKey}
        position="center"
        formatter={(v) => formatBarTotal(v, unitMode)}
        style={{ fontSize: 8, fill: '#ffffff', fontWeight: 800, pointerEvents: 'none' }}
      />
    ) : null
  );

  const paintedDot = (color, seriesKey) => (props) => {
    const { cx, cy, payload } = props;
    if (!chartFilterWeek || payload?.date !== chartFilterWeek || cx == null || cy == null) return null;
    return (
      <circle
        cx={cx}
        cy={cy}
        r={5}
        fill={color}
        fillOpacity={seriesOpacity(seriesKey)}
        stroke="#fff"
        strokeWidth={1.5}
      />
    );
  };

  const remainingFromWeek = dataDateYmd
    ? (() => {
      const d = parseChartDate(dataDateYmd);
      if (!d) return dataDateYmd;
      d.setDate(d.getDate() - d.getDay());
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    })()
    : null;

  const PERIOD_SERIES_KEYS = ['actual_period', 'earned_period', 'budget_period', 'remaining_period', 'shop_earned_period', 'field_earned_period'];
  const aggregatedSeries = aggregateSeriesByGroup(
    (displayChartData || []).filter((row) => {
      if (dateFrom && row.date < dateFrom) return false;
      if (dateTo && row.date > dateTo) return false;
      return true;
    }),
    groupBy,
    PERIOD_SERIES_KEYS,
    ['actual_cumulative', 'earned_cumulative', 'budget_cumulative', 'remaining_cumulative'],
    weekStart,
    dataDateYmd
  );
  // Man-weeks were converted per source row (before grouping), so a month
  // bucket holds summed man-weeks — rescale to man-months (÷ weeks in month).
  const grainScaledSeries = (unitMode === 'manweeks' && groupBy === 'month')
    ? aggregatedSeries.map((row) => {
      const f = grainWeeksOf(row.date);
      if (!f || f === 1) return row;
      const out = { ...row };
      for (const k of PERIOD_SERIES_KEYS) {
        if (out[k] != null) out[k] = Math.round(((Number(out[k]) || 0) / f) * 10) / 10;
      }
      return out;
    })
    : aggregatedSeries;
  const chartSeriesData = stopPerformanceLines(clipSeriesToVisibleData(grainScaledSeries, visibleSeries));

  const stackFilterOpts = {
    disciplines: selectedDisciplines,
    allDisciplines: allDisciplineIds,
    families: selectedFamilies,
    allFamilies: PBIX_FAMILIES.map((f) => f.id),
    shopField: selectedTypes.length === 1 ? selectedTypes[0] : null,
    scope: progressScopes?.length === 1 && progressScopes[0] !== 'all' ? progressScopes[0] : null,
    resourceIds: selectedResourceIds == null ? undefined : selectedResourceIds
  };
  const resourcesForStack = (curve) => Object.values(curve?.byResource || {})
    .filter((r) => resourceMatchesFilters(r, stackFilterOpts))
    .map((r) => {
      if (legendBy !== 'project' || (r.projId != null && r.projId !== '')) return r;
      const p = curve?.project;
      if (!p || p.kind === 'consolidated') return r;
      return {
        ...r,
        projId: p.projId,
        projLabel: r.projLabel || p.projShortName || p.projName || String(p.projId || '')
      };
    });
  // Budget stacks from the proposal / dump curve. Remaining stacks from live
  // remaining (P6 or the PBIX dump on Manual) — same slicer, never a P6 stand-in
  // for dump hours.
  // A P6-column legend uses the backend legend rebuild (legendStacks): buckets
  // keyed (legend value × resource) so every resource filter still applies.
  const p6LegendReady = !needsBackendLegend(legendBy)
    || (liveLegendStacks?.stackBy === p6StackByParam(legendBy));
  const remainingStackSource = needsBackendLegend(legendBy)
    ? (p6LegendReady ? liveLegendStacks.remaining : null)
    : liveRemaining;
  // A legend column with nothing on this copy collapses to one "(no value)"
  // bucket. Paint it the series colour (Budget blue, Remaining orange …) so the
  // bars match the Series swatches instead of a palette colour that means nothing.
  const seriesColorOf = (groupId) => CURVE_GROUPS.find((g) => g.id === groupId)?.color;
  const loneNoneColor = (items, item, groupId) => (
    items.length === 1 && String(item.label) === '(no value)'
      ? (seriesColorOf(groupId) || item.color)
      : item.color
  );
  const remainingStackItems = collectStackItems(resourcesForStack(remainingStackSource), legendBy)
    .map((s, _i, arr) => ({
      ...s,
      color: legendColor(legendColors, s.id, loneNoneColor(arr, s, 'remaining')),
      pattern: legendPattern(legendColors, s.id)
    }));
  const budgetStackCurve = needsBackendLegend(legendBy)
    ? (p6LegendReady
      ? pickBudgetStackCurve({
        proposal: liveLegendStacks.proposal,
        weeklyTarget: liveLegendStacks.weeklyTarget,
        remaining: liveLegendStacks.remaining
      })
      : null)
    : pickBudgetStackCurve({
      proposal: liveProposal,
      weeklyTarget: liveWeeklyTarget,
      remaining: liveRemaining
    });
  const budgetStackItems = collectStackItems(resourcesForStack(budgetStackCurve), legendBy)
    .map((s, _i, arr) => ({
      ...s,
      color: legendColor(legendColors, budgetStackKey(s.id), loneNoneColor(arr, s, 'budget')),
      pattern: legendPattern(legendColors, budgetStackKey(s.id))
    }));
  const earnedStackSource = needsBackendLegend(legendBy)
    ? (p6LegendReady ? liveLegendStacks?.p6Actual : null)
    : (isColumnLegend(legendBy) ? null : liveP6Actual);
  const earnedStackItems = collectStackItems(resourcesForStack(earnedStackSource), legendBy)
    .map((s, _i, arr) => ({
      ...s,
      color: legendColor(legendColors, earnedStackKey(s.id), loneNoneColor(arr, s, 'earned')),
      pattern: legendPattern(legendColors, earnedStackKey(s.id))
    }));
  const spentCurve = String(legendBy).startsWith('tt:')
    ? spentColumnCurve
    : spentCurveFromRows(liveMaestroActuals?.rows, legendBy);
  const spentFilterOpts = String(legendBy).startsWith('tt:')
    ? {}
    : { ...stackFilterOpts, resourceIds: undefined };
  const spentStackItems = collectStackItems(
    Object.values(spentCurve?.byResource || {}).filter((r) => resourceMatchesFilters(r, spentFilterOpts)),
    legendBy
  ).map((s) => ({
    ...s,
    color: legendColor(legendColors, spentStackKey(s.id), s.color),
    pattern: legendPattern(legendColors, spentStackKey(s.id))
  }));
  const spentOtherItem = {
    id: '__other__',
    label: '(other)',
    color: '#94a3b8',
    pattern: 'solid',
    resources: []
  };

  const seriesPaint = {
    budget: legendColor(legendColors, 'budget_period', '#7dd3fc'),
    earned: legendColor(legendColors, 'earned_period', '#15803d'),
    remaining: legendColor(legendColors, 'remaining_period', '#f59e0b'),
    spent: legendColor(legendColors, 'actual_period', '#dc2626')
  };
  const seriesPattern = {
    budget: legendPattern(legendColors, 'budget_period'),
    earned: legendPattern(legendColors, 'earned_period'),
    remaining: legendPattern(legendColors, 'remaining_period'),
    spent: legendPattern(legendColors, 'actual_period')
  };
  const barShape = (pattern) => (pattern && pattern !== 'solid' ? hatchedBarShape(pattern) : undefined);

  const hoursInBucket = (resources, bucket) => hoursOnWeek(resources, bucket);

  // Capacity of ONE person in this bucket, for THIS resource — from the
  // backend's weekHrCnt / calMonthHours, i.e. the resource's dominant TASK
  // calendar (what P6's FTE view divides by; resource-level calendars are
  // often copied from other estimates). Explicit what-if ranges still
  // override; the selected schedule calendar is only the last-resort fallback.
  const capacityHoursFor = (r, bucketYmd) => {
    const custom = rangesForDiscipline(r?.discipline);
    if (Array.isArray(custom) && custom.length) {
      const hit = hpwFromRanges(bucketYmd, custom);
      if (hit) return hit * grainWeeksOf(bucketYmd);
    }
    if (useGoodyearManualPbix) {
      const hit = hpwForDiscipline(bucketYmd, r?.discipline);
      if (hit) return hit * grainWeeksOf(bucketYmd);
    }
    if (groupBy === 'month') {
      const cap = Number(r?.calMonthHours?.[String(bucketYmd).slice(0, 7)]);
      if (cap > 0) return cap;
    }
    const wh = Number(r?.weekHrCnt);
    if (wh > 0) return wh * grainWeeksOf(bucketYmd);
    return (hpwForDiscipline(bucketYmd, r?.discipline) || baseWeekHrs || 40) * grainWeeksOf(bucketYmd);
  };

  // Man units for a stack block: convert per RESOURCE (each on its own
  // calendar) and then sum — never one hpw for the whole block.
  const manUnitsForResources = (resources, bucketYmd) => {
    let units = 0;
    (resources || []).forEach((r) => {
      const hrs = hoursInBucket([r], bucketYmd);
      if (!hrs) return;
      units += hrs / (capacityHoursFor(r, bucketYmd) || 40);
    });
    return Math.round(units * 10) / 10;
  };

  const stackValue = (resources, bucketYmd) => (
    unitMode === 'manweeks'
      ? manUnitsForResources(resources, bucketYmd)
      : Math.round(((hoursInBucket(resources, bucketYmd) || 0) + Number.EPSILON) * 100) / 100
  );

  // One colour (and a P6 legend still rebuilding) must show the SAME bar
  // heights as any stacked legend: convert per resource on its own calendar
  // and sum — never fall back to the flat hpw division. Otherwise switching
  // the legend changes the monthly totals (26-LG-0163: 198 vs 194.5 men).
  const unstackedRemainResources = (!remainingStackItems.length && unitMode === 'manweeks' && !goodyearManualPbix)
    ? resourcesForStack(liveRemaining)
    : null;
  const unstackedBudgetResources = (!budgetStackItems.length && unitMode === 'manweeks' && !goodyearManualPbix)
    ? resourcesForStack(pickBudgetStackCurve({
      proposal: liveProposal,
      weeklyTarget: liveWeeklyTarget,
      remaining: liveRemaining
    }))
    : null;

  const remainHourResources = remainingStackItems.length
    ? remainingStackItems.flatMap((s) => s.resources)
    : (unstackedRemainResources || resourcesForStack(liveRemaining));
  const budgetHourResources = budgetStackItems.length
    ? budgetStackItems.flatMap((s) => s.resources)
    : (unstackedBudgetResources || resourcesForStack(pickBudgetStackCurve({
      proposal: liveProposal,
      weeklyTarget: liveWeeklyTarget,
      remaining: liveRemaining
    })));
  const earnedHourResources = resourcesForStack(liveP6Actual);
  const spentHourResources = resourcesForStack(liveMaestroActuals);
  const canRebuildSCurve = remainHourResources.length
    || budgetHourResources.length
    || earnedHourResources.length
    || spentHourResources.length;

  const stackedPeriods = chartSeriesData.map((row) => {
    const next = { ...row };
    const beforeRemain = remainingFromWeek && earnedSource !== 'manual' && (
      groupBy === 'month'
        ? String(row.date).slice(0, 7) < String(remainingFromWeek).slice(0, 7)
        : row.date < remainingFromWeek
    );
    let rem = 0;
    remainingStackItems.forEach((item) => {
      if (beforeRemain) {
        next[item.id] = 0;
        return;
      }
      const val = stackValue(item.resources, row.date);
      next[item.id] = val;
      rem += val;
    });
    if (remainingStackItems.length) next.remaining_period = rem;
    else if (unstackedRemainResources?.length) {
      next.remaining_period = beforeRemain ? 0 : stackValue(unstackedRemainResources, row.date);
    }
    let bud = 0;
    budgetStackItems.forEach((item) => {
      const key = budgetStackKey(item.id);
      const val = stackValue(item.resources, row.date);
      next[key] = val;
      bud += val;
    });
    if (budgetStackItems.length) next.budget_period = bud;
    else if (unstackedBudgetResources?.length) {
      next.budget_period = stackValue(unstackedBudgetResources, row.date);
    }
    const afterEarn = remainingFromWeek && earnedSource !== 'manual' && (
      groupBy === 'month'
        ? String(row.date).slice(0, 7) > String(dataDateYmd || remainingFromWeek).slice(0, 7)
        : row.date > remainingFromWeek
    );
    if (earnedStackItems.length) {
      let earn = 0;
      earnedStackItems.forEach((item) => {
        const key = earnedStackKey(item.id);
        const val = afterEarn ? 0 : stackValue(item.resources, row.date);
        next[key] = val;
        earn += val;
      });
      next.earned_period = earn;
    } else if (earnedHourResources.length) {
      next.earned_period = afterEarn ? 0 : stackValue(earnedHourResources, row.date);
    }
    if (spentStackItems.length) {
      const slices = spentStackItems.map((item) => ({
        key: spentStackKey(item.id),
        raw: hoursInBucket(item.resources, row.date)
      }));
      spentStackItems.forEach((item) => { next[spentStackKey(item.id)] = 0; });
      next[spentStackKey(spentOtherItem.id)] = 0;
      const parts = apportionSlices(next.actual_period, slices);
      if (parts) parts.forEach((p) => { next[p.key] = p.value; });
      else if ((Number(next.actual_period) || 0) > 0) {
        next[spentStackKey(spentOtherItem.id)] = next.actual_period;
      }
    } else if (spentHourResources.length) {
      next.actual_period = stackValue(spentHourResources, row.date);
    }
    return next;
  });

  const rebuiltChart = canRebuildSCurve
    ? runningHourCumulatives(stackedPeriods, (row) => {
      const beforeRemain = remainingFromWeek && earnedSource !== 'manual' && (
        groupBy === 'month'
          ? String(row.date).slice(0, 7) < String(remainingFromWeek).slice(0, 7)
          : row.date < remainingFromWeek
      );
      const afterEarn = remainingFromWeek && earnedSource !== 'manual' && (
        groupBy === 'month'
          ? String(row.date).slice(0, 7) > String(dataDateYmd || remainingFromWeek).slice(0, 7)
          : row.date > remainingFromWeek
      );
      return {
        budget: hoursInBucket(budgetHourResources, row.date),
        earned: afterEarn ? 0 : hoursInBucket(earnedHourResources, row.date),
        // Maestro spent is job-wide (no byResource) — keep the API cumulative
        // instead of flattening Cum. Spent / CPI / CV to zero.
        actual: spentHourResources.length ? hoursInBucket(spentHourResources, row.date) : null,
        remaining: beforeRemain ? 0 : hoursInBucket(remainHourResources, row.date)
      };
    }, { remainingFromWeek, groupBy, dataDate: dataDateYmd })
    : stackedPeriods;
  const stackedChartData = stopPerformanceLines(rebuiltChart);
  const curveChartData = attachEvmIndices(stackedChartData);

  // Safety / supervision lines sit on the same Y as the bars. Period totals
  // on the raw series are job-wide; stacked remaining / budget already follow
  // the resource filter. Earned / spent use the filtered man-week helper.
  const histCrewPeak = (() => {
    let peak = 0;
    stackedChartData.forEach((r) => {
      peak = Math.max(
        peak,
        Number(r.budget_period) || 0,
        Number(r.remaining_period) || 0
      );
      const earnedMw = manWeeksOnDate(liveP6Actual, r.date);
      const spentMw = manWeeksOnDate(liveMaestroActuals, r.date);
      if (earnedMw != null) peak = Math.max(peak, earnedMw);
      if (spentMw != null) peak = Math.max(peak, spentMw);
    });
    return peak;
  })();

  // Same remaining stack the user counts on the histogram — not the Staffing
  // tab (hiring overrides / per-resource rounding live only there).
  const histogramSafetyCrew = stackedChartData.map((row) => {
    const date = String(row.date || '').slice(0, 10);
    const men = unitMode === 'manweeks'
      ? (Number(row.remaining_period) || 0)
      : manUnitsForResources(remainHourResources, row.date);
    let indirect = 0;
    if (unitMode === 'manweeks' && remainingStackItems.length) {
      remainingStackItems.forEach((item) => {
        const res = item.resources || [];
        const ind = res.filter((r) => familyFromResource(r) === 'indirect');
        if (!ind.length) return;
        if (ind.length === res.length) indirect += Number(row[item.id]) || 0;
        else indirect += manUnitsForResources(ind, row.date);
      });
    } else {
      const ind = (remainHourResources || []).filter((r) => familyFromResource(r) === 'indirect');
      if (ind.length) indirect += manUnitsForResources(ind, row.date);
    }
    return { date, men, indirect };
  });

  const histSafetyLevels = (() => {
    if (!safetyOn || unitMode !== 'manweeks') return [];
    const pts = safetyCrewValues(histogramSafetyCrew, {
      includeIndirect: true,
      from: '',
      to: ''
    });
    const peak = pts.reduce((m, p) => Math.max(m, p.men), 0);
    return safetyThresholdsUpTo(peak, safetyRule);
  })();

  const safetyPlanCrew = activeTab === 'staffing' ? staffingSafetyCrew : histogramSafetyCrew;

  const histSupervision = (() => {
    if (!supervisionOn || unitMode !== 'manweeks') {
      return { fm: [], gf: [], perFm: menPerFm, gfMen: menPerFm * fmPerGf };
    }
    const selected = (effectiveResourceIds || []).map((id) => (
      resourceCatalog?.byResource?.[id] || { resourceId: id }
    ));
    let overrides = {};
    try {
      const raw = JSON.parse(localStorage.getItem(`evm-supervision-cfg-v1-${jobNumber}`) || 'null');
      overrides = raw?.overrides && typeof raw.overrides === 'object' ? raw.overrides : {};
    } catch { /* ignore */ }
    const ratios = supervisionRatiosForResources(selected, menPerFm, fmPerGf, overrides);
    return supervisionThresholds(histCrewPeak, ratios.menPerFm, ratios.fmPerGf);
  })();
  const histGfSet = new Set(histSupervision.gf);

  const showBudgetStackPrefix = Boolean(
    showBars && visibleSeries.remaining_period && remainingStackItems.length
  );
  const stackedPeriodCount = [
    showBars && visibleSeries.remaining_period && remainingStackItems.length,
    showBars && visibleSeries.budget_period && budgetStackItems.length,
    showBars && visibleSeries.earned_period && earnedStackItems.length,
    showBars && visibleSeries.actual_period && spentStackItems.length
  ].filter(Boolean).length;
  const showEarnedStackPrefix = stackedPeriodCount > 1;
  const showSpentStackPrefix = stackedPeriodCount > 1;
  const spentLegendItems = spentStackItems.length
    ? [...spentStackItems, { ...spentOtherItem, color: legendColor(legendColors, spentStackKey(spentOtherItem.id), spentOtherItem.color) }]
    : [];
  const histogramLegendPayload = legendItemsWithData([
    ...(showBars && visibleSeries.remaining_period
      ? remainingStackItems.map((s) => ({ value: s.label, type: 'square', color: s.color, pattern: s.pattern, id: s.id, dataKey: s.id }))
      : []),
    ...(showBars && visibleSeries.budget_period && budgetStackItems.length
      ? budgetStackItems.map((s) => ({
        value: showBudgetStackPrefix ? `Budget · ${s.label}` : s.label,
        type: 'square',
        color: s.color,
        pattern: s.pattern,
        id: budgetStackKey(s.id),
        dataKey: budgetStackKey(s.id)
      }))
      : []),
    ...(showBars && visibleSeries.earned_period && earnedStackItems.length
      ? earnedStackItems.map((s) => ({
        value: showEarnedStackPrefix ? `Earned · ${s.label}` : s.label,
        type: 'square',
        color: s.color,
        pattern: s.pattern,
        id: earnedStackKey(s.id),
        dataKey: earnedStackKey(s.id)
      }))
      : []),
    ...(showBars && visibleSeries.actual_period && spentLegendItems.length
      ? spentLegendItems.map((s) => ({
        value: showSpentStackPrefix ? `Spent · ${s.label}` : s.label,
        type: 'square',
        color: s.color,
        pattern: s.pattern,
        id: spentStackKey(s.id),
        dataKey: spentStackKey(s.id)
      }))
      : []),
    showBars && visibleSeries.budget_period && !budgetStackItems.length && { value: 'Per. Budget', type: 'square', color: seriesPaint.budget, pattern: seriesPattern.budget, dataKey: 'budget_period' },
    showLines && visibleSeries.budget_cumulative && { value: 'Cum. Budget', type: 'line', color: seriesPaint.budget, dataKey: 'budget_cumulative' },
    showBars && visibleSeries.earned_period && !earnedStackItems.length && { value: 'Per. Earned', type: 'square', color: seriesPaint.earned, pattern: seriesPattern.earned, dataKey: 'earned_period' },
    showLines && visibleSeries.earned_cumulative && { value: 'Cum. Earned', type: 'line', color: seriesPaint.earned, dataKey: 'earned_cumulative' },
    !remainingStackItems.length && showBars && visibleSeries.remaining_period && { value: 'Per. Remaining', type: 'square', color: seriesPaint.remaining, pattern: seriesPattern.remaining, dataKey: 'remaining_period' },
    showLines && visibleSeries.remaining_cumulative && { value: 'Cum. Remaining', type: 'line', color: seriesPaint.remaining, dataKey: 'remaining_cumulative', dashed: true },
    showBars && visibleSeries.actual_period && !spentStackItems.length && { value: 'Per. Spent', type: 'square', color: seriesPaint.spent, pattern: seriesPattern.spent, dataKey: 'actual_period' },
    showLines && visibleSeries.actual_cumulative && { value: 'Cum. Spent', type: 'line', color: seriesPaint.spent, dataKey: 'actual_cumulative' }
  ].filter(Boolean), curveChartData);
  // Cumulative lines stay in hours so they meet the BAC / EAC pills.
  // Period bars are man-weeks when that unit is selected. The legend has to
  // say which is which — calling a 7,076-hour forecast "man-weeks" is not a
  // headcount.
  const cumLineKeys = new Set(['budget_cumulative', 'earned_cumulative', 'remaining_cumulative', 'actual_cumulative']);
  const legendHasHourLines = histogramLegendPayload.some((p) => cumLineKeys.has(p.dataKey));
  const legendHasBars = histogramLegendPayload.some((p) => p.dataKey && !cumLineKeys.has(p.dataKey));
  const histogramLegendTitle = legendHasHourLines && !legendHasBars
    ? 'Legend · Man-hours'
    : (legendHasHourLines && legendHasBars && unitMode === 'manweeks'
      ? 'Legend · bars man-weeks · lines hours'
      : `Legend · ${unitMode === 'manweeks' ? manUnitLabel : 'Man-hours'}`);

  const settingsBody = (
    <div style={{ fontSize: '0.72rem', color: '#334155' }}>
      <button
        type="button"
        onClick={() => {
          setShowSettings(false);
          setShowBudgetBaseline(true);
        }}
        title="Open the Budget Hours Baseline workbook"
        style={{
          ...chromeBtn,
          width: '100%',
          height: 28,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 6,
          fontWeight: 700
        }}
      >
        <FistIcon size={14} color="#475569" />
        Budget Hours Baseline
      </button>

      <div style={settingsRule}>
        <div style={itemTitle}>Counts on bars</div>
        <label style={FILTER_CHECK_ROW} title="Prints each colour’s count inside stacked Budget / Remaining bars.">
          <input
            type="checkbox"
            checked={showStaffCounts}
            onChange={(e) => {
              const on = e.target.checked;
              setShowStaffCounts(on);
              try { localStorage.setItem('evm-staffing-numbers-v1', on ? 'true' : 'false'); } catch { /* ignore */ }
            }}
            style={{ accentColor: '#0f766e' }}
          />
          Counts on bars
        </label>
      </div>

      <div style={settingsRule}>
        <div style={itemTitle}>Earned source</div>
        {[
          { id: 'as_is', label: 'As-is — P6 Resource Assignments' },
          { id: 'temp', label: 'Temp — weekly P6 minus previous' },
          { id: 'perm', label: 'Perm — stored financial periods' },
          { id: 'manual', label: 'Manual — PBIX leveled (presentations)' }
        ].map((opt) => (
          <label key={opt.id} style={FILTER_CHECK_ROW}>
            <input
              type="radio"
              name="earnedSource"
              checked={earnedSource === opt.id}
              onChange={() => {
                if (earnedSource === opt.id) return;
                setEarnedSource(opt.id);
                try { localStorage.setItem('evm-earned-source-v1', opt.id); } catch { /* ignore */ }
                if (opt.id === 'manual') applyManualPresentation();
              }}
              style={{ accentColor: '#64748b' }}
            />
            {opt.label}
          </label>
        ))}
      </div>

      <div style={settingsRule}>
        <div style={itemTitle}>FAC / ETC</div>
        {ETC_FORMULAS.map((f) => (
          <label key={f.id} style={FILTER_CHECK_ROW} title={f.hint || ''}>
            <input
              type="radio"
              name="facFormulaSettings"
              value={f.id}
              checked={etcFormula === f.id}
              onMouseDown={(e) => {
                e.preventDefault();
                if (etcFormula === f.id) return;
                setEtcFormula(f.id);
                saveEtcFormula(f.id);
              }}
              onChange={() => {
                setEtcFormula(f.id);
                saveEtcFormula(f.id);
              }}
              style={{ accentColor: '#0f766e' }}
            />
            {f.short} — {f.label}{f.id === DEFAULT_ETC_FORMULA ? ' (default)' : ''}
          </label>
        ))}
      </div>

      <div style={settingsRule}>
        <div style={itemTitle}>Assume spent</div>
        <label style={FILTER_CHECK_ROW} title="Maestro spend is usually one week late. Copy last week's hours onto this week so CPI / SPI use a full AC.">
          <input
            type="checkbox"
            checked={assumeLastWeekSpent}
            onChange={(e) => {
              const on = e.target.checked;
              setAssumeLastWeekSpent(on);
              try { localStorage.setItem(ASSUME_LAST_WEEK_KEY, on ? 'true' : 'false'); } catch { /* ignore */ }
            }}
            style={{ accentColor: '#64748b' }}
          />
          Assume this week = last week
        </label>
        {assumeLastWeekSpent && (assumedSpentMeta?.weeks?.length || assumedSpentMeta?.week) && (
          <div style={{ fontSize: '0.65rem', color: '#b45309', marginTop: 4 }}>
            {(assumedSpentMeta.weeks || [assumedSpentMeta.week]).map((w) => {
              const p = String(w).split('-');
              return `${Number(p[1])}/${Number(p[2])}`;
            }).join(', ')}
            {assumedSpentMeta.from ? ` ← ${Number(String(assumedSpentMeta.from).split('-')[1])}/${Number(String(assumedSpentMeta.from).split('-')[2])}` : ''}
          </div>
        )}
      </div>

      <div style={settingsRule}>
        <div style={itemTitle}>Fab view</div>
        {[
          { id: 'both', label: 'W Fab + WO Fab' },
          { id: 'with', label: 'With Fab only' },
          { id: 'without', label: 'Without Fab only' }
        ].map((opt) => (
          <label key={opt.id} style={FILTER_CHECK_ROW}>
            <input type="radio" name="fabMode" checked={fabMode === opt.id} onChange={() => setFabMode(opt.id)} style={{ accentColor: '#64748b' }} />
            {opt.label}
          </label>
        ))}
      </div>

      <div style={settingsRule}>
        <div style={itemTitle}>Budget source</div>
        <label style={FILTER_CHECK_ROW}>
          <input type="radio" name="budgetPhase" checked={earnedSource === 'manual' || selectedPhase === 'estimation'} onChange={() => setSelectedPhase('estimation')} style={{ accentColor: '#64748b' }} />
          Estimation
        </label>
        <label style={{ ...FILTER_CHECK_ROW, opacity: earnedSource === 'manual' || phase2CurveData.length === 0 ? 0.5 : 1 }}>
          <input
            type="radio"
            name="budgetPhase"
            checked={earnedSource !== 'manual' && selectedPhase === 'execution'}
            onChange={() => earnedSource !== 'manual' && phase2CurveData.length > 0 && setSelectedPhase('execution')}
            disabled={earnedSource === 'manual' || phase2CurveData.length === 0}
            style={{ accentColor: '#64748b' }}
          />
          Execution
        </label>
      </div>

      <div style={settingsRule}>
        <div style={itemTitle}>Calendar</div>
        <select
          value={selectedCalendarId}
          onChange={(e) => {
            calendarTouchedRef.current = true;
            setSelectedCalendarId(e.target.value);
          }}
          style={{ width: '100%', height: 26, fontSize: '0.7rem', borderRadius: 4, border: '1px solid #d1d5db' }}
        >
          {(p6Calendars || []).map((c) => (
            <option key={c.clndrId} value={c.clndrId}>{c.clndrName} ({c.weekHrCnt} h/wk)</option>
          ))}
          {(p6Calendars || []).length === 0 && <option value="">Loading…</option>}
        </select>
        {scenarioHpw && (
          <div style={{ fontSize: '0.62rem', color: '#0f766e', fontWeight: 700, marginTop: 6, lineHeight: 1.35 }}>
            {liveHistMeta?.scenarioHpwSource === 'p6_calendar'
              ? `Scenario ${liveHistMeta?.scenario} — men use ${scenarioHpw} h/person-week from the files’ own P6 calendar.`
              : `Scenario ${liveHistMeta?.scenario} — men use ${scenarioHpw} h/person-week from the scenario name.`}
          </div>
        )}
        {jobCalendarSeed && earnedSource !== 'manual' && (
          <label style={{ ...FILTER_CHECK_ROW, marginTop: 6 }}>
            <input type="checkbox" checked={useJobSeed} onChange={(e) => setUseJobSeed(e.target.checked)} style={{ accentColor: '#64748b' }} />
            PBIX 60→50 (Electrical →40)
          </label>
        )}
      </div>

      <div style={settingsRule}>
        <div style={itemTitle}>Spent feed</div>
        <select value={actualsSource} onChange={(e) => changeSpentFeed(e.target.value)} style={{ width: '100%', height: 26, fontSize: '0.7rem', borderRadius: 4, border: '1px solid #d1d5db' }}>
          <option value="auto">Auto</option>
          <option value="maestro_dump">Maestro</option>
          <option value="time_tracker">Time Tracker</option>
        </select>
      </div>

      <div style={settingsRule}>
        <div style={itemTitle}>What-if calendars</div>
        <button
          type="button"
          onClick={() => setShowDiscCalendars((v) => !v)}
          style={{ ...chromeBtn, width: '100%' }}
        >
          {showDiscCalendars ? 'Hide what-if ranges' : 'What-if calendars…'}
        </button>
        {showDiscCalendars && (
          <div style={{ maxHeight: 180, overflow: 'auto', marginTop: 6 }}>
            {whatIfDisciplines.length === 0 && (
              <div style={{ color: '#94a3b8', fontSize: '0.68rem' }}>Load a schedule — rows follow this job’s Discipline values.</div>
            )}
            {whatIfDisciplines.map((d) => {
              const ranges = disciplineRanges[d.id] || [];
              return (
                <div key={d.id} style={{ marginBottom: 8, paddingBottom: 6, borderBottom: '1px solid #f1f5f9' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span style={{ fontWeight: 700 }}>{d.name}</span>
                    <button type="button" onClick={() => addDiscRange(d.id)} style={{ border: '1px solid #e2e8f0', background: '#f8fafc', borderRadius: 4, fontSize: '0.68rem', cursor: 'pointer' }}>+ Range</button>
                  </div>
                  {ranges.length === 0 && <div style={{ color: '#94a3b8' }}>P6 default ({selectedCalWeekHrs} h/wk)</div>}
                  {ranges.map((r, idx) => (
                    <div key={idx} style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 4 }}>
                      <input type="date" value={r.from || ''} onChange={(e) => updateDiscRange(d.id, idx, 'from', e.target.value)} style={{ fontSize: '0.68rem', height: 24 }} />
                      <input type="date" value={r.to || ''} onChange={(e) => updateDiscRange(d.id, idx, 'to', e.target.value)} style={{ fontSize: '0.68rem', height: 24 }} />
                      <select value={String(r.hoursPerWeek || selectedCalWeekHrs)} onChange={(e) => updateDiscRange(d.id, idx, 'hoursPerWeek', e.target.value)} style={{ fontSize: '0.68rem', height: 24 }}>
                        {HPW_OPTIONS.map((h) => <option key={h} value={String(h)}>{hpwOptionLabel(h)}</option>)}
                      </select>
                      <button type="button" onClick={() => removeDiscRange(d.id, idx)} style={{ border: 'none', background: '#fee2e2', color: '#b91c1c', borderRadius: 4, fontSize: '0.65rem', cursor: 'pointer' }}>×</button>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div style={settingsRule}>
        <div style={itemTitle}>Safety</div>
        <label style={FILTER_CHECK_ROW}>
          <input
            type="checkbox"
            checked={safetyOn}
            onChange={(e) => {
              const on = e.target.checked;
              setSafetyOn(on);
              try { localStorage.setItem('evm-safety-lines-v1', on ? 'true' : 'false'); } catch { /* ignore */ }
            }}
            style={{ accentColor: '#dc2626' }}
          />
          Safety lines
        </label>
        <SafetyRulePicker
          name="safety-rule-settings"
          mode={safetyRuleMode}
          menPer={safetyMenPer}
          firstAt={safetyFirstAt}
          thenEvery={safetyThenEvery}
          finalEvery={safetyFinalEvery}
          onMode={changeSafetyRuleMode}
          onMenPer={changeSafetyMenPer}
          onFirstAt={changeSafetyFirstAt}
          onThenEvery={changeSafetyThenEvery}
          onFinalEvery={changeSafetyFinalEvery}
        />
        <button type="button" onClick={() => setSafetyPlanOpen(true)} style={{ ...chromeBtn, width: '100%' }}>
          Safety plan…
        </button>
        <button type="button" onClick={() => setExtrasPlanOpen(true)} style={{ ...chromeBtn, width: '100%', marginTop: 6, background: '#f5f3ff', color: '#5b21b6' }}>
          Indirect extras…
        </button>
      </div>

      <div style={settingsRule}>
        <div style={itemTitle}>Supervision</div>
        <label style={FILTER_CHECK_ROW}>
          <input
            type="checkbox"
            checked={supervisionOn}
            onChange={(e) => {
              const on = e.target.checked;
              setSupervisionOn(on);
              try { localStorage.setItem('evm-supervision-on-v1', on ? 'true' : 'false'); } catch { /* ignore */ }
            }}
            style={{ accentColor: '#d97706' }}
          />
          Supervision plan
        </label>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, margin: '4px 0' }}>
          1 FM per
          <input type="number" min={1} value={menPerFm} onChange={(e) => changeMenPerFm(e.target.value)} style={{ width: 40, fontSize: '0.72rem', border: '1px solid #d1d5db', borderRadius: 4, padding: '2px 4px' }} />
          men · 1 GF per
          <input type="number" min={1} value={fmPerGf} onChange={(e) => changeFmPerGf(e.target.value)} style={{ width: 40, fontSize: '0.72rem', border: '1px solid #d1d5db', borderRadius: 4, padding: '2px 4px' }} />
          FMs
        </div>
        <button type="button" onClick={() => setSupervisionPlanOpen(true)} style={{ ...chromeBtn, width: '100%', marginTop: 6, background: '#fffbeb', color: '#92400e' }}>
          Supervision plan…
        </button>
      </div>

      <div style={settingsRule}>
        <div style={itemTitle}>Staffing plan</div>
        <select
          value={staffingBaseline}
          onChange={(e) => changeStaffingBaseline(e.target.value)}
          style={{ width: '100%', fontSize: '0.7rem', borderRadius: 4, border: '1px solid #d1d5db', padding: '3px 4px' }}
        >
          <option value="current">Baseline: Current schedule</option>
          <option value="budget">Baseline: Budget / proposal XER</option>
        </select>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6 }}>
          Smooth
          <input type="range" min={0} max={5} value={staffingRadius} onChange={(e) => changeStaffingRadius(e.target.value)} style={{ flex: 1, accentColor: '#0f766e' }} />
          <span style={{ fontWeight: 700, color: '#0f766e', minWidth: 30 }}>{staffingRadius === 0 ? 'off' : `±${staffingRadius}`}</span>
        </label>
        <div style={{ display: 'flex', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
          {[['past', 'Past'], ['future', 'Future'], ['all', 'All']].map(([id, label]) => (
            <label key={id} style={{ display: 'flex', alignItems: 'center', gap: 3, cursor: 'pointer' }}>
              <input type="radio" name="staffHorizon" checked={staffingHorizon === id} onChange={() => changeStaffingHorizon(id)} style={{ accentColor: '#64748b' }} />
              {label}
            </label>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 4, flexWrap: 'wrap' }}>
          {[['curves', 'Curves'], ['crew', 'Crew stack']].map(([id, label]) => (
            <label key={id} style={{ display: 'flex', alignItems: 'center', gap: 3, cursor: 'pointer' }}>
              <input type="radio" name="staffView" checked={staffingView === id} onChange={() => changeStaffingView(id)} style={{ accentColor: '#64748b' }} />
              {label}
            </label>
          ))}
        </div>
        <div style={{ fontSize: '0.68rem', color: '#64748b', marginTop: 6 }}>
          ⬇ xlsx and ⬇ PDF are on the Staffing tab (and in Safety / Extras popups).
        </div>
      </div>
    </div>
  );

  const scrollBars = chartSpan === 'scroll' && !pdfChartHeight;
  const legendOnSide = legendPlace === 'left' || legendPlace === 'right';
  const legendBand = !legendOnSide && histogramLegendPayload.length ? 76 : 0;
  const plotH = Math.max(220, histogramChartH - legendBand);
  const scrollPlotW = Math.max(SCROLL_CATEGORY_PX, (stackedChartData.length || 1) * SCROLL_CATEGORY_PX);
  const leftDomainMax = stackedChartData.reduce((m, row) => {
    const bud = budgetStackItems.length
      ? budgetStackItems.reduce((s, it) => s + (Number(row[budgetStackKey(it.id)]) || 0), 0)
      : (Number(row.budget_period) || 0);
    const rem = remainingStackItems.length
      ? remainingStackItems.reduce((s, it) => s + (Number(row[it.id]) || 0), 0)
      : (Number(row.remaining_period) || 0);
    return Math.max(m, bud, rem, Number(row.earned_period) || 0, Number(row.actual_period) || 0);
  }, 0);
  const rightDomainMax = stackedChartData.reduce((m, row) => Math.max(
    m,
    Number(row.budget_cumulative) || 0,
    Number(row.earned_cumulative) || 0,
    Number(row.remaining_cumulative) || 0,
    Number(row.actual_cumulative) || 0
  ), 0);
  const leftDomain = [0, leftDomainMax > 0 ? leftDomainMax : 1];
  // EVM marks draw BAC / EAC as horizontal lines with pill labels above them —
  // the cumulative axis must reach them and leave room for the labels.
  const marksMax = showEvmIndices
    ? Math.max(rightDomainMax, Number(pmiBase?.bac) || 0, Number(pmiForecast?.fac) || 0) * 1.1
    : 0;
  const rightDomainTop = Math.max(rightDomainMax, marksMax);
  const rightDomain = [0, rightDomainTop > 0 ? rightDomainTop : 1];

  return (
    <div style={{ padding: '0.5rem 0.75rem 1.5rem', maxWidth: '100%', width: '100%', minWidth: 0, margin: 0, boxSizing: 'border-box', overflowX: 'hidden' }}>
      {/* Unified control bar — whitish / grayish, equal height */}
      <div style={{
        display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.5rem',
        marginBottom: '0.75rem', padding: '0.65rem 0.75rem',
        background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px'
      }}>
        {EVM_TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id === 'filters' ? 'histogram' : tab.id)}
            style={{
              ...btnBase,
              background: activeTab === tab.id ? '#eff6ff' : btnBase.background,
              borderColor: activeTab === tab.id ? '#93c5fd' : '#e2e8f0',
              color: activeTab === tab.id ? '#1d4ed8' : '#334155',
              fontWeight: activeTab === tab.id ? 800 : 600
            }}
          >
            {tab.label}
          </button>
        ))}
        <button
          type="button"
          onClick={() => {
            setShowFilters((v) => {
              const next = !v;
              try { localStorage.setItem('evm-show-filters-v2', next ? 'true' : 'false'); } catch { /* ignore */ }
              if (v) setShowFilterPicker(false);
              return next;
            });
          }}
          style={{
            ...btnBase,
            background: showFilters ? '#f1f5f9' : btnBase.background,
            fontWeight: showFilters ? 800 : 600
          }}
        >
          {showFilters ? '▾ Filters' : '▸ Filters'}
        </button>
        <button
          type="button"
          onClick={() => {
            setShowFigures((v) => {
              const next = !v;
              try { localStorage.setItem('evm-show-figures-v2', next ? 'true' : 'false'); } catch { /* ignore */ }
              return next;
            });
          }}
          style={{
            ...btnBase,
            background: showFigures ? '#f1f5f9' : btnBase.background,
            fontWeight: showFigures ? 800 : 600
          }}
        >
          {showFigures ? '▾ Figures' : '▸ Figures'}
        </button>
        <button
          type="button"
          onClick={() => setShowHelp(true)}
          title="Guide to Histogram, Staffing, KPIs, and Progress"
          style={btnBase}
        >
          Help
        </button>
        <div ref={settingsRef} style={{ position: 'relative' }}>
          <button
            type="button"
            onClick={() => setShowSettings((v) => !v)}
            title="Earned source, calendars, Budget Hours Baseline"
            style={{
              ...btnBase,
              background: showSettings || earnedSource !== 'as_is' ? '#f1f5f9' : btnBase.background,
              fontWeight: showSettings ? 800 : 600
            }}
          >
            {showSettings ? '▾ Settings' : (earnedSource === 'as_is' ? 'Settings' : `Settings · ${({ as_is: 'As-is', temp: 'Temp', perm: 'Perm', manual: 'Manual' })[earnedSource] || 'Settings'}`)}
          </button>
          {showSettings && (
            <div style={{
              position: 'absolute',
              zIndex: 70,
              top: 'calc(100% + 4px)',
              right: 0,
              width: 340,
              maxWidth: 'min(92vw, 420px)',
              maxHeight: 440,
              overflowY: 'auto',
              background: '#fff',
              border: '1px solid #cbd5e1',
              borderRadius: 8,
              boxShadow: '0 10px 28px rgba(15,23,42,0.15)',
              padding: 10
            }}
            >
              {settingsBody}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={handleCsvExport}
          style={btnBase}
          title={`Download CSV of the open tab (${activeTab})`}
        >
          ↓ CSV
        </button>
        <button
          type="button"
          onClick={() => setPdfOpen(true)}
          disabled={pdfBusy}
          style={{ ...btnBase, opacity: pdfBusy ? 0.55 : 1 }}
          title="Download PDF with LGL letterhead and footnote clauses"
        >
          {pdfBusy ? 'PDF…' : '↓ PDF'}
        </button>
        <button
          type="button"
          onClick={handleRefresh}
          title="Reload this job from P6 / Maestro"
          style={{
            ...btnBase,
            opacity: refreshBusy ? 0.75 : 1,
            cursor: 'pointer'
          }}
        >
          {refreshBusy ? 'Refreshing…' : 'Refresh'}
        </button>
        <div style={{ ...btnBase, gap: '0.35rem' }}>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', cursor: 'pointer', margin: 0 }}>
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              style={{ width: 14, height: 14, accentColor: '#64748b' }}
            />
            Auto
          </label>
          {autoRefresh && (
            <select
              value={refreshInterval}
              onChange={(e) => setRefreshInterval(Number(e.target.value))}
              style={{
                height: 26, border: '1px solid #d1d5db', borderRadius: 4,
                fontSize: '0.75rem', padding: '0 0.25rem', background: 'white', color: '#374151'
              }}
            >
              <option value={30}>30s</option>
              <option value={60}>1 min</option>
              <option value={300}>5 min</option>
              <option value={600}>10 min</option>
            </select>
          )}
        </div>
        {/* Spent feed lives in Settings — do not duplicate it on the toolbar. */}
        <input
          ref={maestroFileRef}
          type="file"
          accept=".xlsx,.xls,.csv"
          style={{ display: 'none' }}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) uploadMaestroActuals(f);
          }}
        />
        <button
          type="button"
          onClick={() => maestroFileRef.current?.click()}
          disabled={maestroUploading}
          title="Upload Maestro labour export. Premium rows are skipped."
          style={{ ...btnBase, opacity: maestroUploading ? 0.55 : 1, cursor: maestroUploading ? 'wait' : 'pointer' }}
        >
          {maestroUploading ? 'Uploading…' : 'Upload Maestro'}
        </button>
        <label style={{ ...btnBase, gap: '0.3rem', cursor: 'pointer' }} title="Uncheck to add June (or another slice) without wiping hours already imported">
          <input type="checkbox" checked={maestroReplace} onChange={(e) => setMaestroReplace(e.target.checked)} style={{ width: 14, height: 14, accentColor: '#64748b' }} />
          Replace
        </label>
        {maestroImportMeta?.imported_at && (
          <span style={{ fontSize: '0.75rem', color: '#94a3b8', height: btnH, display: 'inline-flex', alignItems: 'center' }} title={maestroImportMeta.filename || ''}>
            {Number(maestroImportMeta.total_hours || 0).toLocaleString()} h
          </span>
        )}
        {lastFetchedAt && (
          <span style={{ fontSize: '0.75rem', color: '#94a3b8', height: btnH, display: 'inline-flex', alignItems: 'center' }}>
            {lastFetchedAt.toLocaleTimeString()}
          </span>
        )}

      </div>

      {(pdfForceFigures == null ? showFigures : pdfForceFigures)
        && (analytics?.pmiSummary || summary?.pmiSummary) && (
        <div ref={figuresPanelRef} style={{ marginBottom: '0.75rem', marginTop: '0.5rem' }}>
          <div style={{
            fontSize: '0.72rem',
            color: '#64748b',
            letterSpacing: '0.02em',
            marginBottom: 6
          }}>
            All figures in man-hours
            {pmiBase?.data_date
              ? ` · data date ${pmiBase.data_date}`
              : ''}
            {pmiBase?.closed_week
              ? ` · EV / PV / AC through week of ${pmiBase.closed_week}`
              : ' · EV / PV / AC = curves at that date'}
            {analytics?.meta?.dataSources?.earned
              ? ` · earned ${analytics.meta.dataSources.earned}`
              : ''}
            {(() => {
              const em = analytics?.meta?.dataSources?.earnedSpreadMode;
              const rm = analytics?.meta?.dataSources?.remainingSpreadMode;
              if (!em && !rm) return '';
              const exact = (m) => m === 'p6_summarized_spread' || m === 'financial_periods' || m === 'weekly_delta' || m === 'pbi_earned_dump';
              if (exact(em) && (rm === 'p6_summarized_spread' || !rm)) {
                return <span style={{ color: '#059669', fontWeight: 700 }}>{' · P6 cells: exact (stored spread)'}</span>;
              }
              return (
                <span
                  style={{ color: '#b45309', fontWeight: 700 }}
                  title="The schedule changed after the last Tools → Summarize in P6 (or this XER was never summarized). Cells are rebuilt from calendars — close, not exact. Run Tools → Summarize in P6 on this project to match its Resource Assignments cells exactly."
                >
                  {' · P6 cells: rebuilt — run Summarize for exact'}
                </span>
              );
            })()}
            {(() => {
              const at = analytics?.meta?.p6ReadAt || liveHistMeta?.p6ReadAt;
              if (!at) return '';
              const d = new Date(at);
              if (Number.isNaN(d.getTime())) return '';
              const sameDay = d.toDateString() === new Date().toDateString();
              const stamp = sameDay
                ? d.toLocaleTimeString('en-CA', { hour: 'numeric', minute: '2-digit', second: '2-digit' })
                : d.toLocaleString('en-CA', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
              return (
                <span title="When the portal last read this job from the P6 database. Press Refresh to read it again now.">
                  {` · P6 read ${stamp}`}
                </span>
              );
            })()}
            {analytics?.meta?.stale || liveHistMeta?.stale ? ' · P6 catching up — last good' : ''}
            {analytics?.meta?.p6Retrying || liveHistMeta?.p6Retrying ? ' · reconnecting to P6' : ''}
            {pmiBase?.resource_filtered ? ' · checked resources only' : ''}
            {pmiForecast.formula?.label ? ` · FAC ${pmiForecast.formula.short || pmiForecast.formula.label}` : ''}
          </div>
          <div style={{ display: 'flex', flexWrap: 'nowrap', gap: 6, width: '100%', minWidth: 0 }}>
          {[
            { k: 'bac', label: 'BAC', tip: analytics?.meta?.scopeFilter ? `Budget at Completion (${analytics.meta.scopeFilter} only)` : 'Budget at Completion = baseline + approved extras (EX resources)' },
            { k: 'pv', label: 'PV', tip: 'Cum. Budget through the week before the XER Sunday (blue curve)' },
            { k: 'ev', label: 'EV', tip: 'Cum. Earned through the week before the XER Sunday — turn Earned on to see it' },
            { k: 'ac', label: 'AC', tip: 'Cum. Spent through the week before the XER Sunday (red curve)' },
            { k: 'sv', label: 'SV', tip: 'EV − PV' },
            { k: 'cv', label: 'CV', tip: 'EV − AC' },
            { k: 'spi', label: 'SPI', tip: 'EV / PV to data date (not EV / BAC)', fmt: 'idx' },
            { k: 'cpi', label: 'CPI', tip: 'EV / AC', fmt: 'idx' },
            { k: 'fac', label: 'FAC', tip: pmiForecast.formula?.hint || 'Forecast at Completion (≈ EAC)' },
            { k: 'etc', label: 'ETC', tip: pmiForecast.formula?.hint || 'Estimate to Complete' },
            { k: 'vac', label: 'VAC', tip: 'BAC − FAC' },
            { k: 'tcpi_bac', label: 'TCPI', tip: 'To-Complete PI (to BAC)', fmt: 'idx' },
            { k: 'pct_complete', label: '% Comp', tip: 'EV / BAC', fmt: 'pct' }
          ].map(({ k, label, tip, fmt }) => {
            const src = { ...(pmiBase || {}), fac: pmiForecast.fac, etc: pmiForecast.etc, vac: pmiForecast.vac, eac: pmiForecast.eac };
            const v = src[k];
            const color = fmt === 'idx'
              ? (v == null ? '#64748b' : v >= 1 ? '#16a34a' : v >= 0.9 ? '#ca8a04' : '#dc2626')
              : (k === 'sv' || k === 'cv' || k === 'vac')
                ? (v == null ? '#64748b' : v >= 0 ? '#16a34a' : '#dc2626')
                : '#0f172a';
            const text = v == null ? '—'
              : fmt === 'idx' ? Number(v).toFixed(2)
              : fmt === 'pct' ? `${Number(v).toFixed(1)}%`
              : Math.round(Number(v)).toLocaleString();
            return (
              <div key={k} title={tip} style={{
                flex: '1 1 0',
                minWidth: 0,
                background: 'white', border: '1px solid #e2e8f0', borderRadius: 10,
                padding: '0.4rem 0.35rem'
              }}>
                <div style={{ fontSize: 'clamp(0.58rem, 0.7vw, 0.68rem)', fontWeight: 700, color: '#64748b', letterSpacing: '0.02em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</div>
                <div style={{ fontSize: 'clamp(0.78rem, 1.35vw, 1.05rem)', fontWeight: 800, color, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{text}</div>
              </div>
            );
          })}
          </div>
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'stretch', minWidth: 0, width: '100%', position: 'relative' }}>
      <div ref={reportPrintRef} style={{ flex: '1 1 0%', minWidth: 0, maxWidth: '100%' }}>
      {activeTab === 'staffing' && (
        <StaffingPlanTab
          remaining={liveRemaining}
          actuals={liveP6Actual}
          baseline={staffingBaselineCurve}
          baselineLabel={staffingBaselineLabel}
          forecastFactor={staffingForecastFactor}
          forecastLabel={pmiForecast?.formula?.short || 'CPI'}
          dataDate={dataDateYmd}
          hpwForResource={staffingHpw}
          weekHoursDefault={baseWeekHrs}
          selectedDisciplines={selectedDisciplines}
          allDisciplines={allDisciplineIds}
          selectedFamilies={selectedFamilies}
          allFamilies={PBIX_FAMILIES.map((f) => f.id)}
          selectedTypes={selectedTypes}
          progressScopes={progressScopes}
          selectedResourceIds={selectedResourceIds}
          legendBy={(legendBy === 'total' || legendBy === 'project' || isP6LegendBy(legendBy) || isColumnLegend(legendBy)) ? 'resource' : legendBy}
          dateFrom={dateFrom}
          dateTo={dateTo}
          jobNumber={jobNumber}
          loading={loading}
          dataSource={dataSource}
          progressTable={analytics?.progressTable || null}
          safetyOn={safetyOn}
          safetyRule={safetyRule}
          onSafetyCrew={onStaffingSafetyCrew}
          supervisionOn={supervisionOn}
          menPerFm={menPerFm}
          fmPerGf={fmPerGf}
          onMenPerFm={changeMenPerFm}
          onFmPerGf={changeFmPerGf}
          superCfg={superCfg}
          onPatchCfg={patchSuperCfg}
          showBarCounts={showStaffCounts}
          legendColors={legendColors}
          onLegendColor={changeLegendColor}
          onLegendPattern={changeLegendPattern}
          radius={staffingRadius}
          horizon={staffingHorizon}
          view={staffingView}
          csvRef={staffingCsvRef}
          resetRef={staffingResetRef}
          onSummaryChange={onStaffingSummary}
        />
      )}

      {activeTab === 'histogram' && (
      <>
      <div style={{
        background: 'white',
        padding: '1rem 1.15rem',
        borderRadius: '10px',
        border: '1px solid #e2e8f0',
        minWidth: 0
      }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', marginBottom: '0.5rem' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10 }}>
            <h3 style={{ fontSize: '1.1rem', fontWeight: '600', color: '#1f2937', margin: 0 }}>
              Resource Histogram
            </h3>
            {(liveHistMeta?.stale || analytics?.meta?.stale) && (
              <span style={{
                fontSize: '0.68rem', fontWeight: 700, color: '#92400e',
                background: '#fef3c7', border: '1px solid #fcd34d',
                borderRadius: 6, padding: '2px 8px'
              }} title={liveHistMeta?.staleReason || analytics?.meta?.p6Error || 'P6 is catching up'}>
                P6 catching up — last good
              </span>
            )}
            {legendBusy && (
              <span style={{
                fontSize: '0.68rem', fontWeight: 700, color: '#92400e',
                background: '#fef3c7', border: '1px solid #fcd34d',
                borderRadius: 6, padding: '2px 8px'
              }}>
                Splitting legend…
              </span>
            )}
            {(liveHistMeta?.refreshing || liveHistMeta?.xerSelectionPending) && !liveHistMeta?.stale && (
              <span style={{
                fontSize: '0.68rem', fontWeight: 700, color: '#334155',
                background: '#e2e8f0', border: '1px solid #cbd5e1',
                borderRadius: 6, padding: '2px 8px'
              }}>
                {liveHistMeta?.xerSelectionPending ? 'Loading selected XERs…' : 'Updating from P6…'}
              </span>
            )}
            {(liveHistMeta?.p6Retrying || analytics?.meta?.p6Retrying) && !liveHistMeta?.stale && !analytics?.meta?.stale && (
              <span style={{
                fontSize: '0.68rem', fontWeight: 700, color: '#334155',
                background: '#e2e8f0', border: '1px solid #cbd5e1',
                borderRadius: 6, padding: '2px 8px'
              }}>
                Connecting to P6…
              </span>
            )}
            {earnedSource === 'manual' && (
              <span style={{
                fontSize: '0.68rem', fontWeight: 700, color: '#92400e',
                background: '#fef3c7', border: '1px solid #fcd34d',
                borderRadius: 6, padding: '2px 8px'
              }}>
                PBIX dump
                {analytics?.pmiSummary?.remaining_hours
                  ? ` · remain ${Math.round(Number(analytics.pmiSummary.remaining_hours)).toLocaleString()} h`
                  : ''}
                {analytics?.meta?.manual?.applied?.filter((x) => x !== 'p6_closed_week').length
                  ? ` · ${analytics.meta.manual.applied.filter((x) => x !== 'p6_closed_week').join(', ')}`
                  : ''}
                {analytics?.meta?.manual?.p6ClosedWeeks?.length
                  ? ` · P6 ${analytics.meta.manual.p6ClosedWeeks.map((w) => {
                    const p = String(w).split('-');
                    return `${Number(p[1])}/${Number(p[2])}`;
                  }).join(', ')}`
                  : ''}
              </span>
            )}
            {(liveHistMeta?.remainingSpreadMode || analytics?.meta?.dataSources?.remainingSpreadMode) === 'resource_assignments' && (
              <span
                style={{
                  fontSize: '0.68rem', fontWeight: 700, color: '#9a3412',
                  background: '#ffedd5', border: '1px solid #fdba74',
                  borderRadius: 6, padding: '2px 8px', cursor: 'help'
                }}
                title={'This XER has no stored Summarize cells, so the bars are rebuilt from each assignment\u2019s planned rate and calendar (P6 Linear: the qty is consumed at the rate and stops when it runs out). This tracks P6\u2019s Resource Usage Profile closely. For P6\u2019s exact stored cells: open the XER in P6, run Tools \u2192 Summarize, then Refresh here.'}
              >
                ≈ rebuilt P6 cells — Summarize in P6 for exact
              </span>
            )}
          </div>
          {(chartFilterWeek || selectedActivityCodes.length) ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.75rem', color: '#334155' }}>
              <span>
                {chartFilterWeek
                  ? `Week ${chartFilterWeek} highlighted · click again to clear`
                  : `${selectedActivityCodes.length} activit${selectedActivityCodes.length === 1 ? 'y' : 'ies'} selected`}
              </span>
              <button
                type="button"
                onClick={clearActivitySlicer}
                style={{ border: '1px solid #cbd5e1', background: '#f8fafc', borderRadius: 4, fontSize: '0.7rem', padding: '0.15rem 0.45rem', cursor: 'pointer' }}
              >
                Clear filter
              </button>
            </div>
          ) : null}
        </div>
        {loading && !histogramAxisRows.length ? (
          <div style={{ textAlign: 'center', padding: '3rem', color: '#6b7280' }}>
            Loading...
          </div>
        ) : (chartData.length === 0 && histogramAxisRows.length === 0) ? (
          <div style={{ textAlign: 'center', padding: '3rem', color: '#6b7280', maxWidth: 640, margin: '0 auto' }}>
            {loading || liveHistMeta?.p6Retrying || liveHistMeta?.stale ? (
              <>
                <div style={{ fontWeight: 600, marginBottom: 8, color: '#334155' }}>Loading the resource histogram…</div>
                Connecting to Primavera. When a last-good curve exists it stays on this page.
              </>
            ) : (
              <>
                No data available for this job — open Settings → Budget Hours Baseline to upload Maestro actuals if needed, then Refresh here.
              </>
            )}
          </div>
        ) : (
          <div ref={histogramChartRef} style={{
            display: 'flex',
            flexDirection: legendOnSide ? 'row' : 'column',
            gap: 6,
            alignItems: 'stretch',
            height: histogramChartH,
            minWidth: 0
          }}>
          {histogramLegendPayload.length > 0 && (legendPlace === 'left' || legendPlace === 'top') && (
            <div style={legendOnSide
              ? { width: 'auto', minWidth: 108, maxWidth: 168, flexShrink: 0, maxHeight: plotH, overflowY: 'auto', paddingRight: 2 }
              : { flexShrink: 0, maxHeight: legendBand, overflowY: 'auto', overflowX: 'hidden' }}>
              <HistogramLegend
                payload={histogramLegendPayload}
                spotlight={spotlightSeries}
                onSpotlight={setSpotlightSeries}
                onColorChange={changeLegendColor}
                onPatternChange={changeLegendPattern}
                hideColorPickers={Boolean(pdfBusy || pdfChartHeight)}
                hasColorOverrides={Object.keys(legendColors).length > 0}
                onResetColors={resetLegendColors}
                direction={legendOnSide ? 'column' : 'row'}
                title={histogramLegendTitle}
              />
            </div>
          )}
          <div style={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex', alignItems: 'stretch' }}>
          {scrollBars && (
            <div style={{ width: 42, flexShrink: 0 }}>
              <ScaleGutter side="left" width={42} height={plotH} domain={leftDomain} tickFormatter={formatPeriodTick} />
            </div>
          )}
          <div ref={plotBoxRef} style={{ flex: 1, minWidth: 0, overflowX: scrollBars ? 'auto' : 'hidden', overflowY: 'hidden' }}>
            <ComposedChart
              width={scrollBars ? scrollPlotW : Math.max(plotBoxW, 280)}
              height={plotH}
              data={curveChartData}
              barCategoryGap={2}
              barGap={0}
              margin={scrollBars
                ? { top: 22, right: 4, left: 4, bottom: 8 }
                : { top: 22, right: AXIS_RIGHT, left: AXIS_LEFT, bottom: 8 }}
              onClick={(state, event) => {
                const path = event?.nativeEvent?.composedPath?.() || [];
                if (path.some((n) => n?.classList?.contains?.('recharts-legend-wrapper'))) return;
                const week = state?.activeLabel;
                if (!week) return;
                handleChartWeekClick({ date: week });
              }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis
                dataKey="date"
                interval={0}
                height={52}
                tick={<HistogramTimeTick />}
                axisLine={{ stroke: '#94a3b8' }}
                tickLine={{ stroke: '#94a3b8' }}
              />
              <YAxis
                yAxisId="left"
                hide={scrollBars}
                width={scrollBars ? 0 : 36}
                domain={scrollBars ? leftDomain : [0, 'dataMax']}
                allowDataOverflow={true}
                tickFormatter={formatPeriodTick}
                tick={{ fontSize: 11 }}
                style={{ fontSize: '0.75rem' }}
                stroke="#6b7280"
              />
              <YAxis
                yAxisId="right"
                hide={scrollBars}
                width={scrollBars ? 0 : 40}
                orientation="right"
                domain={scrollBars || showEvmIndices ? rightDomain : [0, 'dataMax']}
                allowDataOverflow={true}
                tickFormatter={formatAxisTick}
                tick={{ fontSize: 11 }}
                style={{ fontSize: '0.75rem' }}
                stroke="#6b7280"
              />
              <Tooltip content={<CustomTooltip />} isAnimationActive={false} />
              {chartFilterWeek && (
                <ReferenceLine
                  yAxisId="left"
                  x={chartFilterWeek}
                  stroke="#334155"
                  strokeDasharray="4 3"
                />
              )}
              
              {showBars && visibleSeries.budget_period && budgetStackItems.length > 0 && budgetStackItems.map((s) => (
              <Bar
                key={budgetStackKey(s.id)}
                yAxisId="left"
                dataKey={budgetStackKey(s.id)}
                fill={s.color}
                shape={barShape(s.pattern)}
                name={showBudgetStackPrefix ? `Budget · ${s.label}` : s.label}
                stackId="budget"
                legendType="none"
                radius={0}
                isAnimationActive={false}
              >
                {paintedCells(s.color, budgetStackKey(s.id))}
                {stackCountLabel(budgetStackKey(s.id))}
              </Bar>
              ))}
              {showBars && visibleSeries.budget_period && budgetStackItems.length === 0 && (
              <Bar
                yAxisId="left"
                dataKey="budget_period"
                fill={seriesPaint.budget}
                shape={barShape(seriesPattern.budget)}
                name="Per. Budget"
                legendType="none"
                radius={0}
                isAnimationActive={false}
              >
                {paintedCells(seriesPaint.budget, 'budget_period')}
                {stackCountLabel('budget_period')}
                {barValueLabel}
              </Bar>
              )}
              {showBars && visibleSeries.budget_period && budgetStackItems.length > 0 && (
              <Line
                yAxisId="left"
                type="linear"
                dataKey="budget_period"
                name="_budgetTotal"
                stroke="none"
                strokeWidth={0}
                dot={false}
                legendType="none"
                isAnimationActive={false}
                connectNulls={false}
              >
                {barValueLabel}
              </Line>
              )}
              {showLines && visibleSeries.budget_cumulative && (
              <Line
                yAxisId="right"
                type="linear"
                dataKey="budget_cumulative"
                strokeOpacity={chartFilterWeek ? seriesOpacity('budget_cumulative') * 0.22 : seriesOpacity('budget_cumulative')}
                stroke={seriesPaint.budget}
                strokeWidth={3}
                name="Cum. Budget"
                legendType="none"
                dot={chartFilterWeek ? paintedDot(seriesPaint.budget, 'budget_cumulative') : false}
                isAnimationActive={false}
              />
              )}
              {showBars && visibleSeries.earned_period && earnedStackItems.length > 0 && earnedStackItems.map((s) => (
              <Bar
                key={earnedStackKey(s.id)}
                yAxisId="left"
                dataKey={earnedStackKey(s.id)}
                fill={s.color}
                shape={barShape(s.pattern)}
                name={showEarnedStackPrefix ? `Earned · ${s.label}` : s.label}
                stackId="earned"
                legendType="none"
                radius={0}
                isAnimationActive={false}
              >
                {paintedCells(s.color, earnedStackKey(s.id))}
                {stackCountLabel(earnedStackKey(s.id))}
              </Bar>
              ))}
              {showBars && visibleSeries.earned_period && earnedStackItems.length === 0 && (
              <Bar
                yAxisId="left"
                dataKey="earned_period"
                fill={seriesPaint.earned}
                shape={barShape(seriesPattern.earned)}
                name="Per. Earned"
                legendType="none"
                radius={0}
                isAnimationActive={false}
              >
                {paintedCells(seriesPaint.earned, 'earned_period')}
                {stackCountLabel('earned_period')}
                {barValueLabel}
              </Bar>
              )}
              {showBars && visibleSeries.earned_period && earnedStackItems.length > 0 && (
              <Line
                yAxisId="left"
                type="linear"
                dataKey="earned_period"
                name="_earnedTotal"
                stroke="none"
                strokeWidth={0}
                dot={false}
                legendType="none"
                isAnimationActive={false}
                connectNulls={false}
              >
                {barValueLabel}
              </Line>
              )}
              {showLines && visibleSeries.earned_cumulative && (
              <Line
                yAxisId="right"
                type="linear"
                dataKey="earned_cumulative"
                strokeOpacity={chartFilterWeek ? seriesOpacity('earned_cumulative') * 0.22 : seriesOpacity('earned_cumulative')}
                stroke={seriesPaint.earned}
                strokeWidth={3}
                name="Cum. Earned"
                legendType="none"
                dot={chartFilterWeek ? paintedDot(seriesPaint.earned, 'earned_cumulative') : false}
                connectNulls={false}
                isAnimationActive={false}
              />
              )}
              {showBars && visibleSeries.remaining_period && remainingStackItems.length > 0 && remainingStackItems.map((s) => (
              <Bar
                key={s.id}
                yAxisId="left"
                dataKey={s.id}
                fill={s.color}
                shape={barShape(s.pattern)}
                name={s.label}
                stackId="remaining"
                legendType="none"
                radius={0}
                isAnimationActive={false}
              >
                {paintedCells(s.color, s.id)}
                {stackCountLabel(s.id)}
              </Bar>
              ))}
              {showBars && visibleSeries.remaining_period && remainingStackItems.length === 0 && (
              <Bar
                yAxisId="left"
                dataKey="remaining_period"
                fill={seriesPaint.remaining}
                shape={barShape(seriesPattern.remaining)}
                name="Per. Remaining"
                legendType="none"
                radius={0}
                isAnimationActive={false}
              >
                {paintedCells(seriesPaint.remaining, 'remaining_period')}
                {stackCountLabel('remaining_period')}
              </Bar>
              )}
              {showBars && visibleSeries.remaining_period && (
              <Line
                yAxisId="left"
                type="linear"
                dataKey="remaining_period"
                name="_remainTotal"
                stroke="none"
                strokeWidth={0}
                dot={false}
                legendType="none"
                isAnimationActive={false}
                connectNulls={false}
              >
                {barValueLabel}
              </Line>
              )}
              {showLines && visibleSeries.remaining_cumulative && (
              <Line
                yAxisId="right"
                type="linear"
                dataKey="remaining_cumulative"
                strokeOpacity={chartFilterWeek ? seriesOpacity('remaining_cumulative') * 0.22 : seriesOpacity('remaining_cumulative')}
                stroke={seriesPaint.remaining}
                strokeWidth={3}
                strokeDasharray="5 4"
                name="Cum. Remaining"
                legendType="none"
                dot={chartFilterWeek ? paintedDot(seriesPaint.remaining, 'remaining_cumulative') : false}
                connectNulls
                isAnimationActive={false}
              />
              )}
              {showBars && visibleSeries.actual_period && spentStackItems.length > 0 && spentLegendItems.map((s) => (
              <Bar
                key={spentStackKey(s.id)}
                yAxisId="left"
                dataKey={spentStackKey(s.id)}
                fill={s.color}
                shape={barShape(s.pattern)}
                name={showSpentStackPrefix ? `Spent · ${s.label}` : s.label}
                stackId="spent"
                legendType="none"
                radius={0}
                isAnimationActive={false}
              >
                {paintedCells(s.color, spentStackKey(s.id))}
                {stackCountLabel(spentStackKey(s.id))}
              </Bar>
              ))}
              {showBars && visibleSeries.actual_period && spentStackItems.length === 0 && (
              <Bar
                yAxisId="left"
                dataKey="actual_period"
                fill={seriesPaint.spent}
                shape={barShape(seriesPattern.spent)}
                name="Per. Spent"
                legendType="none"
                radius={0}
                isAnimationActive={false}
              >
                {paintedCells(seriesPaint.spent, 'actual_period')}
                {stackCountLabel('actual_period')}
                {barValueLabel}
              </Bar>
              )}
              {showBars && visibleSeries.actual_period && spentStackItems.length > 0 && (
              <Line
                yAxisId="left"
                type="linear"
                dataKey="actual_period"
                name="_spentTotal"
                stroke="none"
                strokeWidth={0}
                dot={false}
                legendType="none"
                isAnimationActive={false}
                connectNulls={false}
              >
                {barValueLabel}
              </Line>
              )}
              {showLines && visibleSeries.actual_cumulative && (
              <Line
                yAxisId="right"
                type="linear"
                dataKey="actual_cumulative"
                strokeOpacity={chartFilterWeek ? seriesOpacity('actual_cumulative') * 0.22 : seriesOpacity('actual_cumulative')}
                stroke={seriesPaint.spent}
                strokeWidth={3}
                name="Cum. Spent"
                legendType="none"
                dot={chartFilterWeek ? paintedDot(seriesPaint.spent, 'actual_cumulative') : false}
                connectNulls={false}
                isAnimationActive={false}
              />
              )}
              {histSafetyLevels.map((y) => (
                <ReferenceLine
                  key={`safety-${y}`}
                  yAxisId="left"
                  y={y}
                  stroke="#dc2626"
                  strokeOpacity={0.6}
                  strokeDasharray="7 5"
                  label={{
                    value: `${y} men → ${safetyCountWord(safetyLineLabelCount(y, safetyRule))}`,
                    position: 'insideLeft',
                    fontSize: 9,
                    fill: '#b91c1c',
                    fontWeight: 700
                  }}
                />
              ))}
              {histSupervision.fm.filter((y) => !histGfSet.has(y)).map((y) => (
                <ReferenceLine
                  key={`fm-${y}`}
                  yAxisId="left"
                  y={y}
                  stroke="#d97706"
                  strokeOpacity={0.7}
                  strokeDasharray="4 4"
                  label={{
                    value: `${y} men → ${neededAt(y, histSupervision.perFm)} FM`,
                    position: 'insideRight',
                    fontSize: 9,
                    fill: '#b45309',
                    fontWeight: 700
                  }}
                />
              ))}
              {histSupervision.gf.map((y) => (
                <ReferenceLine
                  key={`gf-${y}`}
                  yAxisId="left"
                  y={y}
                  stroke="#92400e"
                  strokeOpacity={0.75}
                  strokeDasharray="2 3"
                  label={{
                    value: `${y} men → ${neededAt(y, histSupervision.perFm)} FM · ${gfNeededAt(y, histSupervision.gfMen)} GF`,
                    position: 'insideRight',
                    fontSize: 9,
                    fill: '#78350f',
                    fontWeight: 700
                  }}
                />
              ))}
              {/* SVG has no z-index; recharts 3 portals layers by zIndex so the
                  EVM balloons paint above bars, lines and value labels. */}
              {showEvmIndices && (
                <ZIndexLayer zIndex={DefaultZIndexes.label + 10}>
                  <EvmCurveMarks
                    rows={curveChartData}
                    bandScale={showBars}
                    dataDate={remainingFromWeek}
                    bac={pmiBase?.bac}
                    eac={pmiForecast?.fac}
                    etc={pmiForecast?.etc}
                    vac={pmiForecast?.vac}
                  />
                </ZIndexLayer>
              )}
            </ComposedChart>
          </div>
          {scrollBars && (
            <div style={{ width: 52, flexShrink: 0 }} title="Cumulative hours">
              <ScaleGutter side="right" width={52} height={plotH} domain={rightDomain} tickFormatter={formatAxisTick} />
            </div>
          )}
          </div>
          {histogramLegendPayload.length > 0 && (legendPlace === 'right' || legendPlace === 'bottom') && (
            <div style={legendOnSide
              ? { width: 'auto', minWidth: 108, maxWidth: 168, flexShrink: 0, maxHeight: plotH, overflowY: 'auto', paddingLeft: 2 }
              : { flexShrink: 0, maxHeight: legendBand, overflowY: 'auto', overflowX: 'hidden' }}>
              <HistogramLegend
                payload={histogramLegendPayload}
                spotlight={spotlightSeries}
                onSpotlight={setSpotlightSeries}
                onColorChange={changeLegendColor}
                onPatternChange={changeLegendPattern}
                hideColorPickers={Boolean(pdfBusy || pdfChartHeight)}
                hasColorOverrides={Object.keys(legendColors).length > 0}
                onResetColors={resetLegendColors}
                direction={legendOnSide ? 'column' : 'row'}
                title={histogramLegendTitle}
              />
            </div>
          )}
          </div>
        )}
      </div>
      </>
      )}

      {/* KPIs tab */}
      {activeTab === 'kpis' && (
      <>
      {analytics?.kpis && visibleKpiCharts.gauges !== false && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
          gap: '0.75rem',
          marginBottom: '1.5rem',
          width: '100%'
        }}>
          {analytics.kpis.filter((kpi) => selectedFamilies.includes(kpi.id)).map((kpi) => (
            <div key={kpi.id} style={{
              background: 'white',
              padding: '1rem 1.1rem',
              borderRadius: '12px',
              border: '1px solid #e5e7eb',
              boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
              minWidth: 0
            }}>
              <div style={{ fontWeight: 700, color: '#1f2937', marginBottom: '0.35rem', fontSize: '0.9rem' }}>{kpi.label}</div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', marginBottom: '0.15rem' }}>
                <span style={{ color: '#6b7280' }}>Overall Progress</span>
                <span style={{ fontWeight: 700 }}>{kpi.overall_progress_pct?.toFixed(1)}%</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', marginBottom: '0.35rem' }}>
                <span style={{ color: '#6b7280' }}>Cur. Week Progress</span>
                <span style={{ fontWeight: 600 }}>{kpi.current_week_progress_pct?.toFixed(1)}%</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.15rem' }}>
                <KpiGauge label="SPI" value={kpi.spi} colorKey={kpi.spi_color} />
                <KpiGauge label="CPI" value={kpi.cpi} colorKey={kpi.cpi_color} />
              </div>
            </div>
          ))}
          <div style={{ gridColumn: '1 / -1', fontSize: '0.75rem', color: '#6b7280' }}>
            Green: KPI &gt; 1 · Yellow: 0.9–1 · Red: &lt; 0.9
          </div>
        </div>
      )}

      {/* KPI Trends — collapsible; visibility chips below */}
      {analytics?.spiCpiTrend && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '1rem' }}>
          <div style={{
            display: 'flex', flexWrap: 'wrap', gap: '0.4rem', alignItems: 'center',
            padding: '0.55rem 0.65rem', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8
          }}>
            <span style={{ fontSize: '0.72rem', fontWeight: 700, color: '#475569', marginRight: 4 }}>Show KPIs:</span>
            {[
              { id: 'gauges', label: 'Gauges' },
              { id: 'spi_per', label: 'SPI Per' },
              { id: 'spi_cum', label: 'SPI Cum' },
              { id: 'cpi_per', label: 'CPI Per' },
              { id: 'cpi_cum', label: 'CPI Cum' },
              { id: 'sv', label: 'SV' },
              { id: 'cv', label: 'CV' },
              { id: 'forecast', label: 'Forecast' },
              { id: 'spentEarned', label: 'Spent/Earned' }
            ].map((c) => (
              <label key={c.id} style={{
                display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.72rem',
                padding: '0.2rem 0.45rem', borderRadius: 999, cursor: 'pointer',
                background: visibleKpiCharts[c.id] !== false ? '#e2e8f0' : 'white',
                border: '1px solid #cbd5e1', color: '#334155'
              }}>
                <input
                  type="checkbox"
                  checked={visibleKpiCharts[c.id] !== false}
                  onChange={() => setVisibleKpiCharts((prev) => ({ ...prev, [c.id]: prev[c.id] === false }))}
                  style={{ accentColor: '#64748b', width: 12, height: 12 }}
                />
                {c.label}
              </label>
            ))}
            <span style={{ fontSize: '0.68rem', color: '#64748b', marginLeft: 'auto' }}>
              Grouped by {groupBy} · {kpiTrendData.length} points
            </span>
          </div>
          <h3 style={{ margin: 0, fontSize: '0.95rem', fontWeight: 600, color: '#1f2937' }}>
            KPI Trends (PMI)
          </h3>
          {[
              {
                id: 'spi_per',
                title: 'SPI — Periodic',
                lines: [{ key: 'per_spi', name: 'Per. SPI', color: '#64748b', dash: '4 3' }],
                yRef: 1
              },
              {
                id: 'spi_cum',
                title: 'SPI — Cumulative',
                lines: [{ key: 'cum_spi', name: 'Cum. SPI', color: '#0f172a', dash: undefined }],
                yRef: 1
              },
              {
                id: 'cpi_per',
                title: 'CPI — Periodic',
                lines: [{ key: 'per_cpi', name: 'Per. CPI', color: '#93c5fd', dash: '4 3' }],
                yRef: 1
              },
              {
                id: 'cpi_cum',
                title: 'CPI — Cumulative',
                lines: [{ key: 'cum_cpi', name: 'Cum. CPI', color: '#1d4ed8', dash: undefined }],
                yRef: 1
              },
              {
                id: 'sv',
                title: 'Schedule Variance (SV hrs)',
                lines: [
                  { key: 'per_sv', name: 'Per. SV', color: '#94a3b8', dash: '4 3' },
                  { key: 'cum_sv', name: 'Cum. SV', color: '#475569', dash: undefined }
                ],
                yRef: 0
              },
              {
                id: 'cv',
                title: 'Cost Variance (CV hrs)',
                lines: [
                  { key: 'per_cv', name: 'Per. CV', color: '#94a3b8', dash: '4 3' },
                  { key: 'cum_cv', name: 'Cum. CV', color: '#475569', dash: undefined }
                ],
                yRef: 0
              }
            ].filter((chart) => visibleKpiCharts[chart.id] !== false).map((chart) => (
              <div key={chart.title} style={{
                background: 'white', padding: '0.75rem 1rem', borderRadius: '10px',
                border: '1px solid #e2e8f0'
              }}>
                <button
                  type="button"
                  onClick={() => setCollapsedKpi((prev) => ({ ...prev, [chart.id]: !prev[chart.id] }))}
                  style={{
                    all: 'unset', cursor: 'pointer', display: 'flex', width: '100%',
                    justifyContent: 'space-between', alignItems: 'center', marginBottom: collapsedKpi[chart.id] ? 0 : '0.5rem'
                  }}
                >
                  <h3 style={{ fontSize: '0.95rem', fontWeight: 600, margin: 0 }}>{chart.title}</h3>
                  <span style={{ fontSize: '0.75rem', color: '#64748b' }}>{collapsedKpi[chart.id] ? '▸ Show' : '▾ Hide'}</span>
                </button>
                {!collapsedKpi[chart.id] && (
                <ResponsiveContainer width="100%" height={320}>
                  <LineChart data={kpiTrendData} margin={{ top: 22, right: 16, left: 4, bottom: 52 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis
                      dataKey="date"
                      interval={0}
                      height={52}
                      tick={<HistogramTimeTick />}
                      axisLine={{ stroke: '#94a3b8' }}
                      tickLine={{ stroke: '#94a3b8' }}
                    />
                    <YAxis style={{ fontSize: '0.75rem' }} />
                    <Tooltip labelFormatter={formatDate} />
                    <Legend wrapperStyle={{ fontSize: '0.8rem' }} />
                    <ReferenceLine y={chart.yRef} stroke="#94a3b8" strokeDasharray="4 4" />
                    {chart.lines.map((ln) => (
                      <Line
                        key={ln.key}
                        type="monotone"
                        dataKey={ln.key}
                        stroke={ln.color}
                        strokeWidth={ln.dash ? 1.75 : 2.5}
                        strokeDasharray={ln.dash}
                        dot={{ r: 3, fill: ln.color, strokeWidth: 0 }}
                        name={ln.name}
                        connectNulls
                      >
                        <LabelList
                          dataKey={ln.key}
                          position="top"
                          offset={6}
                          formatter={(v) => (v == null || Number.isNaN(Number(v)) ? '' : Number(v).toFixed(2))}
                          style={{ fontSize: 9, fill: '#334155', fontWeight: 600 }}
                        />
                      </Line>
                    ))}
                  </LineChart>
                </ResponsiveContainer>
                )}
              </div>
            ))}
          {analytics.pmiSummary && visibleKpiCharts.forecast !== false && (
            <div style={{
              background: 'white', padding: '0.75rem 1rem', borderRadius: '10px',
              border: '1px solid #e2e8f0', fontSize: '0.82rem', color: '#475569'
            }}>
              <button type="button" onClick={() => setCollapsedKpi((prev) => ({ ...prev, forecast: !prev.forecast }))}
                style={{ all: 'unset', cursor: 'pointer', display: 'flex', width: '100%', justifyContent: 'space-between', marginBottom: collapsedKpi.forecast ? 0 : 6 }}>
                <strong style={{ color: '#0f172a' }}>Forecast variants (hours)</strong>
                <span style={{ fontSize: '0.75rem', color: '#64748b' }}>{collapsedKpi.forecast ? '▸ Show' : '▾ Hide'}</span>
              </button>
              {!collapsedKpi.forecast && (<>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', marginTop: '0.5rem' }}>
                <span>FAC (CPI): <b>{analytics.pmiSummary.fac_cpi != null ? Math.round(analytics.pmiSummary.fac_cpi).toLocaleString() : '—'}</b></span>
                <span>FAC (plan): <b>{analytics.pmiSummary.fac_plan != null ? Math.round(analytics.pmiSummary.fac_plan).toLocaleString() : '—'}</b></span>
                <span>FAC (CPI×SPI): <b>{analytics.pmiSummary.fac_combo != null ? Math.round(analytics.pmiSummary.fac_combo).toLocaleString() : '—'}</b></span>
                <span>TCPI→EAC: <b>{analytics.pmiSummary.tcpi_eac != null ? Number(analytics.pmiSummary.tcpi_eac).toFixed(2) : '—'}</b></span>
              </div>
              </>)}
            </div>
          )}
          {analytics.spentEarnedTable?.rows?.length > 0 && visibleKpiCharts.spentEarned !== false && (
            <div style={{
              background: 'white', padding: '0.75rem 1rem', borderRadius: '10px',
              border: '1px solid #e2e8f0', maxHeight: collapsedKpi.spentEarned ? 'auto' : 320, overflow: 'auto'
            }}>
              <button type="button" onClick={() => setCollapsedKpi((prev) => ({ ...prev, spentEarned: !prev.spentEarned }))}
                style={{ all: 'unset', cursor: 'pointer', display: 'flex', width: '100%', justifyContent: 'space-between', marginBottom: collapsedKpi.spentEarned ? 0 : 6 }}>
                <h3 style={{ fontSize: '0.95rem', fontWeight: 600, margin: 0 }}>Per. Spent / Per. Earned</h3>
                <span style={{ fontSize: '0.75rem', color: '#64748b' }}>{collapsedKpi.spentEarned ? '▸ Show' : '▾ Hide'}</span>
              </button>
              {!collapsedKpi.spentEarned && (
              <>
              <div style={{ fontSize: '0.72rem', color: '#64748b', marginBottom: '0.5rem' }}>
                Hours burned (Maestro Spent) vs hours completed (P6 Earned) per period — same basis as the histogram bars. Grouped by {groupBy}.
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                <thead>
                  <tr style={{ background: '#f3f4f6', textAlign: 'left' }}>
                    <th style={{ padding: '0.4rem' }}>Date</th>
                    <th style={{ padding: '0.4rem' }}>Per. Spent</th>
                    <th style={{ padding: '0.4rem' }}>Per. Earned</th>
                  </tr>
                </thead>
                <tbody>
                  {(kpiTrendData || []).map((row) => (
                    <tr key={row.date} style={{ borderTop: '1px solid #e5e7eb' }}>
                      <td style={{ padding: '0.35rem' }}>{formatDate(row.date)}</td>
                      <td style={{ padding: '0.35rem' }}>{Number(row.per_spent || 0).toLocaleString()}</td>
                      <td style={{ padding: '0.35rem' }}>{Number(row.per_earned || 0).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ borderTop: '2px solid #d1d5db', fontWeight: 700 }}>
                    <td style={{ padding: '0.4rem' }}>Total</td>
                    <td style={{ padding: '0.4rem' }}>{Number(analytics.spentEarnedTable.total_spent).toLocaleString()}</td>
                    <td style={{ padding: '0.4rem' }}>{Number(analytics.spentEarnedTable.total_earned).toLocaleString()}</td>
                  </tr>
                </tfoot>
              </table>
              </>
              )}
            </div>
          )}
        </div>
      )}
      {!analytics?.kpis && !analytics?.spiCpiTrend && (
        <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>
          No KPI analytics yet for this job. Refresh after Maestro / P6 data is available.
        </div>
      )}
      </>
      )}

      {activeTab === 'progress' && analytics?.progressTable && (
        <div style={{
          background: 'white', padding: '1.25rem', borderRadius: '12px',
          border: '1px solid #e5e7eb', marginBottom: '1.5rem',
          minWidth: 0, maxWidth: '100%', overflow: 'hidden'
        }}>
          <h3 style={{ fontSize: '1rem', fontWeight: 600, margin: '0 0 0.75rem' }}>Progress by XER week</h3>
          {(() => {
            const weeks = (analytics.progressTable.weeks || [])
              .filter((w) => (!dateFrom || w >= dateFrom) && (!dateTo || w <= dateTo));
            const rows = analytics.progressTable.rows
              .filter((row) => progressScopes.includes(row.scope))
              .flatMap((row) => progressMetricKeys.map(([label, key], idx) => ({
                key: `${row.scope}-${key}`,
                label: `${row.label} · ${label}`,
                metricKey: key,
                row,
                idx
              })));
            const cellH = 28;
            const cellBox = { height: cellH, boxSizing: 'border-box' };
            return (
              <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                <table style={{
                  borderCollapse: 'separate', borderSpacing: 0, fontSize: '0.72rem',
                  width: 220, flex: 'none', background: '#fff', zIndex: 2,
                  boxShadow: '8px 0 12px rgba(15,23,42,0.10)'
                }}>
                  <thead>
                    <tr>
                      <th style={{
                        padding: '0.35rem 0.5rem', background: '#dbeafe', textAlign: 'left', ...cellBox
                      }}>Metric</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.key}>
                        <td style={{
                          padding: '0.3rem 0.5rem', fontWeight: 600, whiteSpace: 'nowrap',
                          background: r.idx % 2 ? '#f8fafc' : '#ffffff', ...cellBox
                        }}>
                          {r.label}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div style={{ overflowX: 'auto', flex: 1, minWidth: 0 }}>
                  <table style={{
                    borderCollapse: 'separate', borderSpacing: 0, fontSize: '0.72rem',
                    minWidth: '100%'
                  }}>
                    <thead>
                      <tr>
                        {weeks.map((w) => (
                          <th key={w} style={{
                            padding: '0.35rem', whiteSpace: 'nowrap', background: '#dbeafe', ...cellBox
                          }}>{w}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r) => (
                        <tr key={r.key}>
                          {weeks.map((w) => (
                            <td key={w} style={{
                              padding: '0.3rem', textAlign: 'right', ...cellBox,
                              background: r.idx % 2 ? '#f8fafc' : '#ffffff'
                            }}>
                              {r.row.weeks?.[w]?.[r.metricKey] != null
                                ? `${r.row.weeks[w][r.metricKey].toFixed(2)}%`
                                : '—'}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {activeTab === 'progress' && !analytics?.progressTable && (
        <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>
          No progress table for this job yet.
        </div>
      )}

      </div>{/* reportPrintRef */}
      <aside
        ref={filtersPanelRef}
        aria-hidden={!showFilters}
        style={{
          flex: `0 0 ${showFilters ? FILTER_PANE_W : 0}px`,
          width: showFilters ? FILTER_PANE_W : 0,
          minWidth: 0,
          maxHeight: pdfCaptureFilters ? 'none' : 'calc(100vh - 140px)',
          marginLeft: showFilters ? 8 : 0,
          marginTop: 6,
          opacity: showFilters ? 1 : 0,
          pointerEvents: showFilters ? 'auto' : 'none',
          overflow: pdfCaptureFilters ? 'visible' : 'hidden',
          position: 'sticky',
          top: 8,
          alignSelf: 'flex-start',
          overflowAnchor: 'none',
          boxSizing: 'border-box',
          transition: pdfCaptureFilters
            ? 'none'
            : 'flex-basis 220ms cubic-bezier(0.22, 1, 0.36, 1), width 220ms cubic-bezier(0.22, 1, 0.36, 1), margin-left 200ms ease, opacity 180ms ease'
        }}
      >
        <div
          ref={filtersPaneInnerRef}
          style={{
          width: FILTER_PANE_W,
          minWidth: FILTER_PANE_W,
          height: pdfCaptureFilters ? 'auto' : '100%',
          maxHeight: pdfCaptureFilters ? 'none' : 'calc(100vh - 140px)',
          display: 'flex',
          flexDirection: 'column',
          background: '#f8fafc',
          border: '1px solid #e2e8f0',
          borderRadius: 8,
          boxSizing: 'border-box'
        }}>
        {(() => {
          const panelOn = (id) => {
            if (SETTINGS_SLICER_IDS.includes(id)) return false;
            return isSlicerVisible(
              effFilterPanels,
              id,
              DEFAULT_TOOLBAR_ON.includes(id) || DEFAULT_ACTUALS_ON.includes(id)
            );
          };
          const chromeBtn = {
            padding: '0.15rem 0.45rem',
            background: 'white',
            color: '#374151',
            border: '1px solid #d1d5db',
            borderRadius: '5px',
            fontSize: '0.68rem',
            fontWeight: 600,
            cursor: 'pointer'
          };
          const paneChipBtn = {
            height: 24,
            padding: '0 8px',
            background: '#fff',
            color: '#334155',
            border: '1px solid #d1d5db',
            borderRadius: 5,
            fontSize: '0.7rem',
            fontWeight: 600,
            cursor: 'pointer',
            boxSizing: 'border-box',
            lineHeight: 1,
            whiteSpace: 'nowrap'
          };
          const sectionTitle = {
            fontSize: '0.62rem',
            fontWeight: 800,
            color: '#64748b',
            letterSpacing: 0.4,
            textTransform: 'uppercase',
            margin: '8px 0 4px'
          };
          const onSeries = CURVE_GROUPS.filter((g) => curveGroupOn(g.id));
          const seriesNames = onSeries.length === CURVE_GROUPS.length
            ? 'All series'
            : (onSeries.map((g) => g.label).join(', ') || 'None');
          const shapeDefault = showBars && showLines && !showEvmIndices;
          const shapeBits = [
            showBars ? 'Bars' : null,
            showLines ? 'Lines' : null,
            showEvmIndices ? 'EVM marks' : null
          ].filter(Boolean);
          const seriesSummary = shapeDefault
            ? seriesNames
            : `${shapeBits.join(' + ') || 'None'} · ${seriesNames}`;
          const groupSummary = groupBy === 'week'
            ? `Week · ${weekStart === 'monday' ? 'Mon' : 'Sun'}`
            : (GROUP_BY_OPTIONS.find((o) => o.id === groupBy)?.name || 'Week');
          const legendBySummary = legendBy === 'total'
            ? 'One colour'
            : legendBy === 'discipline'
              ? 'Discipline'
              : legendBy === 'resource'
                ? 'Resource'
                : legendBy === 'project'
                  ? 'Project ID'
                  : String(legendBy).startsWith('acct:')
                    ? (ACCT_LEGEND_FIELDS.find((f) => `acct:${f.id}` === legendBy)?.label || 'Accounting')
                    : String(legendBy).startsWith('tt:')
                      ? (slicerFields.find((f) => `tt:${f.id}` === legendBy)?.label
                        || actualsSlicers.find((f) => `tt:${f.id}` === legendBy)?.label
                        || 'Actuals column')
                      : (legendFields.find((f) => `p6:${f.id}` === legendBy)?.label || 'P6 column');
          const shopSummary = selectedTypes.length === TYPE_ITEMS.length
            ? 'Fab + Field'
            : (TYPE_ITEMS.filter((t) => selectedTypes.includes(t.id)).map((t) => t.name).join(', ') || 'None');
          const discSummary = pickCountSummary(
            selectedDisciplines,
            allDisciplineIds,
            disciplineOptions.find((d) => d.id === selectedDisciplines[0])?.name
          );
          const resSummary = allResourcesOn
            ? 'All'
            : pickCountSummary(
              effectiveResourceIds,
              allResourceIds,
              resourceOptions.find((r) => r.id === effectiveResourceIds[0])?.label
            );
          const rsrcNameSummary = allResourceNamesOn
            ? 'All'
            : pickCountSummary(
              selectedResourceNames,
              resourceNameOptions.map((n) => n.id),
              selectedResourceNames[0]
            );
          const actSummary = selectedActivityCodes.length
            ? `${selectedActivityCodes.length} selected`
            : (chartFilterWeek ? 'Week' : 'All');
          const familySummary = pickCountSummary(
            selectedFamilies,
            PBIX_FAMILIES.map((f) => f.id),
            PBIX_FAMILIES.find((f) => f.id === selectedFamilies[0])?.label
          );
          const scopeSummary = pickCountSummary(
            progressScopes,
            PROGRESS_SCOPES.map((s) => s.id),
            PROGRESS_SCOPES.find((s) => s.id === progressScopes[0])?.label
          );
          const xerPickSummary = (ids, autoLabel) => {
            if (!ids?.length) return autoLabel;
            if (ids.length === 1) {
              const p = (p6Projects || []).find((x) => String(x.projId) === String(ids[0]));
              return p ? p6ProjectLabel(p) : '1 XER';
            }
            return `${ids.length} XERs`;
          };

          const paneFilters = [];
          const addFilter = (on, label, node) => {
            if (!on) return;
            paneFilters.push({ label, node });
          };

          addFilter(panelOn('p6schedules'), 'Budget', (
            <PaneFilter
              key="budget"
              label="Budget"
              summary={xerPickSummary(selectedProposalProjIds, 'Auto — latest budget')}
              active={selectedProposalProjIds.length > 0}
              onOpen={refreshP6Projects}
              popoverWidth={WIDE_PANE_POPOVER}
            >
              <P6WeeklyPicker
                embedded
                projects={p6Projects}
                selectedIds={selectedProposalProjIds}
                onChange={setSelectedProposalProjIds}
                autoSummary="Auto — latest budget"
                autoOptionLabel="Auto — latest budget (proposal baseline)"
              />
            </PaneFilter>
          ));
          addFilter(panelOn('p6schedules'), 'Current', (
            <PaneFilter
              key="current"
              label="Current"
              summary={xerPickSummary(selectedWeeklyProjIds, 'Auto — latest weekly')}
              active={selectedWeeklyProjIds.length > 0}
              onOpen={refreshP6Projects}
              popoverWidth={WIDE_PANE_POPOVER}
            >
              <P6WeeklyPicker
                embedded
                projects={p6Projects}
                selectedIds={selectedWeeklyProjIds}
                onChange={setSelectedWeeklyProjIds}
              />
            </PaneFilter>
          ));
          addFilter(panelOn('series'), 'Curve', (
            <PaneFilter key="curve" label="Curve" summary={seriesSummary} active={!shapeDefault || onSeries.length !== CURVE_GROUPS.length}>
              <div style={{ fontSize: '0.62rem', fontWeight: 700, color: '#64748b', letterSpacing: 0.3, textTransform: 'uppercase', marginBottom: 4 }}>
                Shape
              </div>
              <label style={FILTER_CHECK_ROW} title="Weekly amounts — Budget, Earned, Remaining, and Spent.">
                <input
                  type="checkbox"
                  checked={showBars}
                  onChange={() => setCurveMarks((prev) => ({ ...prev, bars: !prev.bars }))}
                  style={{ accentColor: '#64748b' }}
                />
                Bars
              </label>
              <label style={FILTER_CHECK_ROW} title="Running totals — the S-curves.">
                <input
                  type="checkbox"
                  checked={showLines}
                  onChange={() => setCurveMarks((prev) => ({ ...prev, lines: !prev.lines }))}
                  style={{ accentColor: '#64748b' }}
                />
                Lines
              </label>
              <label
                style={FILTER_CHECK_ROW}
                title="Classic EVM S-curve marks: Data Date line, BAC and EAC lines, SV (Earned vs Budget) and CV (Earned vs Spent) brackets where the curves stop, ETC from Spent up to EAC, Work remaining (BAC − EV) and VAC at the end, Funds remaining (BAC − AC) at the start. EAC follows the FAC formula; when that formula gives nonsense (no spent hours yet) the forecast curve's end is used instead. Hover a week for SPI, CPI, SV, and CV at that week."
              >
                <input
                  type="checkbox"
                  checked={showEvmIndices}
                  onChange={() => setShowEvmIndices((on) => !on)}
                  style={{ accentColor: '#0e7490' }}
                />
                EVM marks (SV, CV, ETC, VAC, BAC, EAC)
              </label>
              <div style={{ fontSize: '0.62rem', fontWeight: 700, color: '#64748b', letterSpacing: 0.3, textTransform: 'uppercase', margin: '8px 0 4px' }}>
                Series
              </div>
              {CURVE_GROUPS.map((g) => (
                <label key={g.id} style={FILTER_CHECK_ROW}>
                  <input type="checkbox" checked={curveGroupOn(g.id)} onChange={() => toggleCurveGroup(g.id)} style={{ accentColor: legendColor(legendColors, g.keys[0], g.color) }} />
                  <span style={{ width: 7, height: 7, borderRadius: 2, background: legendColor(legendColors, g.keys[0], g.color), display: 'inline-block' }} />
                  {g.label}
                </label>
              ))}
            </PaneFilter>
          ));
          addFilter(panelOn('units'), 'Units', (
            <PaneFilter key="units" label="Units" summary={unitMode === 'hours' ? 'Man-hours' : manUnitLabel}>
              {[['hours', 'Man-hours'], ['manweeks', manUnitLabel]].map(([id, label]) => (
                <label key={id} style={FILTER_CHECK_ROW}>
                  <input type="radio" name="unitMode" checked={unitMode === id} onChange={() => setUnitMode(id)} style={{ accentColor: '#64748b' }} />
                  {label}
                </label>
              ))}
            </PaneFilter>
          ));
          addFilter(panelOn('groupby'), 'Group', (
            <PaneFilter key="group" label="Group" summary={groupSummary}>
              {GROUP_BY_OPTIONS.map((option) => (
                <label key={option.id} style={FILTER_CHECK_ROW}>
                  <input type="radio" name="groupByTop" checked={groupBy === option.id} onChange={() => setGroupBy(option.id)} style={{ accentColor: '#64748b' }} />
                  {option.name}
                </label>
              ))}
              {groupBy === 'week' && (
                <>
                  <div style={{ fontSize: '0.62rem', fontWeight: 700, color: '#64748b', margin: '8px 0 4px' }}>Week starts</div>
                  {[['sunday', 'Sunday 00:00'], ['monday', 'Monday']].map(([id, label]) => (
                    <label key={id} style={FILTER_CHECK_ROW}>
                      <input
                        type="radio"
                        name="weekStartTop"
                        checked={weekStart === id}
                        onChange={() => {
                          setWeekStart(id);
                          try { localStorage.setItem(WEEK_START_KEY, id); } catch { /* ignore */ }
                        }}
                        style={{ accentColor: '#64748b' }}
                      />
                      {label}
                    </label>
                  ))}
                </>
              )}
            </PaneFilter>
          ));
          addFilter(panelOn('chartspan'), 'Presentation', (
            <PaneFilter key="chartspan" label="Presentation" summary={chartSpan === 'scroll' ? 'Scroll' : 'Fit'} active={chartSpan !== 'fit'}>
              {[
                ['fit', 'Fit', 'Squeeze the whole job onto this screen'],
                ['scroll', 'Scroll', 'Keep bar width fixed and scroll sideways along the job']
              ].map(([id, label, tip]) => (
                <label key={id} style={FILTER_CHECK_ROW} title={tip}>
                  <input type="radio" name="chartSpan" checked={chartSpan === id} onChange={() => setChartSpan(id)} style={{ accentColor: '#64748b' }} />
                  {label}
                </label>
              ))}
            </PaneFilter>
          ));
          addFilter(panelOn('legendby'), 'Legend', (
            <PaneFilter
              key="legend"
              label="Legend"
              summary={`${legendBySummary} · ${LEGEND_PLACES.find((p) => p.id === legendPlace)?.label || 'Left'}`}
              popoverMaxHeight={360}
              active={legendBy !== DEFAULT_LEGEND_BY || legendPlace !== 'left'}
            >
              <div style={{ fontSize: '0.62rem', fontWeight: 700, color: '#64748b', letterSpacing: 0.3, textTransform: 'uppercase', marginBottom: 4 }}>
                Place
              </div>
              {LEGEND_PLACES.map((p) => (
                <label key={p.id} style={FILTER_CHECK_ROW}>
                  <input type="radio" name="legendPlace" checked={legendPlace === p.id} onChange={() => setLegendPlace(p.id)} style={{ accentColor: '#64748b' }} />
                  {p.label}
                </label>
              ))}
              <div style={{ fontSize: '0.62rem', fontWeight: 700, color: '#64748b', letterSpacing: 0.3, textTransform: 'uppercase', margin: '8px 0 4px' }}>
                Colour by
              </div>
              <FilterRadioList
                name="legendBy"
                value={legendBy}
                onChange={changeLegendBy}
                search
                groups={[
                  {
                    label: 'Portal',
                    items: [
                      { id: 'total', label: 'One colour' },
                      { id: 'discipline', label: 'Discipline' },
                      { id: 'resource', label: 'Resource' },
                      { id: 'project', label: 'Project ID' }
                    ]
                  },
                  ...[...new Set(legendFields.map((f) => f.group))].map((group) => ({
                    label: group,
                    items: legendFields.filter((f) => f.group === group).map((f) => ({
                      id: `p6:${f.id}`,
                      label: f.label
                    }))
                  })),
                  {
                    label: 'Accounting',
                    items: ACCT_LEGEND_FIELDS.map((f) => ({ id: `acct:${f.id}`, label: f.label }))
                  },
                  ...[...new Set(columnLegendFields(slicerFields).map((f) => f.group || 'Time Tracker / Actuals'))].map((group) => ({
                    label: group,
                    items: columnLegendFields(slicerFields)
                      .filter((f) => (f.group || 'Time Tracker / Actuals') === group)
                      .map((f) => ({ id: `tt:${f.id}`, label: f.label }))
                  }))
                ]}
              />
              {((needsBackendLegend(legendBy) && !p6LegendReady) || (String(legendBy).startsWith('tt:') && spentLegendBusy)) && (
                <div style={{ fontSize: '0.65rem', color: '#b45309', marginTop: 6 }}>
                  {(legendBusy || spentLegendBusy) ? 'Splitting bars…' : 'Updating legend…'}
                </div>
              )}
            </PaneFilter>
          ));
          addFilter(panelOn('shopfield'), 'Fab / Field', (
            <PaneFilter key="shopfield" label="Fab / Field" summary={shopSummary} active={selectedTypes.length !== TYPE_ITEMS.length}>
              {TYPE_ITEMS.map((item) => (
                <label key={item.id} style={{ ...FILTER_CHECK_ROW, ...slicerPaint(!weekTypes || weekTypes.has(item.id)) }}>
                  <input type="checkbox" checked={selectedTypes.includes(item.id)} onChange={() => toggleType(item.id)} style={{ accentColor: '#64748b' }} />
                  {item.name}
                </label>
              ))}
            </PaneFilter>
          ));
          addFilter(panelOn('discipline'), 'Discipline', (
            <PaneFilter
              key="discipline"
              label="Discipline"
              summary={discSummary}
              active={allDisciplineIds.length > 0 && selectedDisciplines.length !== allDisciplineIds.length}
            >
              <FilterCheckList
                items={disciplineOptions.map((d) => ({ id: d.id, label: d.name, color: d.color }))}
                selectedIds={selectedDisciplines}
                onToggle={toggleDiscipline}
                onSelectAll={selectAllDisciplines}
                paint={(item) => slicerPaint(!weekDisc || weekDisc.has(item.id))}
                empty="No Discipline values on this XER. In P6 that is the Discipline activity code, or a Discipline UDF — not a portal list."
              />
            </PaneFilter>
          ));
          addFilter(panelOn('resources'), 'Resource ID', (
            <PaneFilter key="resources" label="Resource ID" summary={resSummary} active={!allResourcesOn} title="P6 Resource ID (rsrc_short_name)" popoverWidth={WIDE_PANE_POPOVER}>
              <FilterCheckList
                items={resourceOptions.map((r) => ({
                  id: r.id,
                  label: r.id,
                  color: r.color,
                  title: r.p6Name && r.p6Name !== r.id ? `${r.id} — ${r.p6Name}` : r.id
                }))}
                selectedIds={effectiveResourceIds}
                onToggle={toggleResourceId}
                onSelectAll={selectAllResources}
                empty="No resources on this remaining curve yet."
              />
            </PaneFilter>
          ));
          addFilter(panelOn('rsrcname'), 'Resource Name', (
            <PaneFilter key="rsrcname" label="Resource Name" summary={rsrcNameSummary} active={!allResourceNamesOn} title="P6 Resource Name (rsrc_name)" popoverWidth={WIDE_PANE_POPOVER}>
              <FilterCheckList
                items={resourceNameOptions.map((n) => ({
                  id: n.id,
                  label: n.label,
                  color: n.color,
                  title: n.resourceIds.join(', ')
                }))}
                selectedIds={selectedResourceNames}
                onToggle={toggleResourceName}
                onSelectAll={selectAllResources}
                empty="No resource names on this remaining curve yet."
              />
            </PaneFilter>
          ));
          addFilter(panelOn('activities'), 'Activities', (
            <PaneFilter
              key="activities"
              label="Activities"
              summary={actSummary}
              popoverWidth={WIDE_PANE_POPOVER}
              popoverMaxHeight={420}
              active={!!selectedActivityCodes.length || !!chartFilterWeek}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6, flexWrap: 'wrap' }}>
                <input
                  type="search"
                  value={activitySearch}
                  onChange={(e) => setActivitySearch(e.target.value)}
                  placeholder={hasActivityWbs ? 'Search WBS or activity…' : 'Search activities…'}
                  style={{
                    flex: 1,
                    minWidth: 140,
                    height: 24,
                    boxSizing: 'border-box',
                    border: '1px solid #cbd5e1',
                    borderRadius: 4,
                    padding: '0 8px',
                    fontSize: '0.7rem'
                  }}
                />
                {hasActivityWbs && (
                  <>
                    <button type="button" onClick={() => setCollapsedActivityWbs(new Set())} style={chromeBtn}>Expand</button>
                    <button type="button" onClick={() => setCollapsedActivityWbs(new Set(collectWbsPaths(activityOutline)))} style={chromeBtn}>Collapse</button>
                  </>
                )}
                <button type="button" onClick={clearActivitySlicer} style={chromeBtn}>Clear</button>
              </div>
              <div style={{ fontSize: '0.68rem', color: '#64748b', marginBottom: 6 }}>
                {chartFilterWeek
                  ? 'Showing the clicked week. Click the bar again to clear.'
                  : hasActivityWbs
                    ? 'Check a WBS band to take that whole branch. Expand to pick activities.'
                    : 'Check rows to limit the curve.'}
              </div>
              {allActivities.length === 0 ? (
                <div style={{ fontSize: '0.68rem', color: '#94a3b8' }}>No activities yet.</div>
              ) : hasActivityWbs ? (
                <ActivityWbsTree
                  rows={visibleActivityRows}
                  outline={activityOutline}
                  selectedCodes={selectedActivityCodes}
                  collapsedPaths={activityWbsCollapsed}
                  onToggleCode={toggleActivityCode}
                  onToggleCodes={toggleActivityCodes}
                  onToggleCollapse={toggleActivityWbsCollapse}
                  formatNumber={formatNumber}
                />
              ) : displayedActivities.length === 0 ? (
                <div style={{ fontSize: '0.68rem', color: '#94a3b8' }}>
                  No match{chartFilterWeek ? ' for this week' : ''}.
                </div>
              ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.65rem', tableLayout: 'fixed' }}>
                <colgroup>
                  <col style={{ width: 18 }} />
                  <col style={{ width: '22%' }} />
                  <col style={{ width: '28%' }} />
                  <col style={{ width: '13%' }} />
                  <col style={{ width: '13%' }} />
                  <col style={{ width: '8%' }} />
                  <col style={{ width: '8%' }} />
                  <col style={{ width: '8%' }} />
                </colgroup>
                <thead>
                  <tr style={{ color: '#64748b', textAlign: 'left' }}>
                    <th style={{ fontWeight: 600, padding: '1px 2px' }} />
                    <th style={{ fontWeight: 600, padding: '1px 2px' }}>Activity ID</th>
                    <th style={{ fontWeight: 600, padding: '1px 2px' }}>Activity name</th>
                    <th style={{ fontWeight: 600, padding: '1px 2px' }}>Start</th>
                    <th style={{ fontWeight: 600, padding: '1px 2px' }}>Finish</th>
                    <th style={{ fontWeight: 600, padding: '1px 2px', textAlign: 'right' }}>Bud</th>
                    <th style={{ fontWeight: 600, padding: '1px 2px', textAlign: 'right' }}>Act</th>
                    <th style={{ fontWeight: 600, padding: '1px 2px', textAlign: 'right' }}>Rem</th>
                  </tr>
                </thead>
                <tbody>
                  {displayedActivities.map((a) => {
                    const on = selectedActivityCodes.includes(a.activity_code);
                    return (
                      <tr
                        key={a.activity_code}
                        onClick={() => toggleActivityCode(a.activity_code)}
                        style={{
                          cursor: 'pointer',
                          background: on ? '#e0f2fe' : 'transparent',
                          color: on ? '#0f172a' : '#334155'
                        }}
                      >
                        <td style={{ padding: '1px 2px' }} onClick={(e) => e.stopPropagation()}>
                          <input type="checkbox" checked={on} onChange={() => toggleActivityCode(a.activity_code)} style={{ accentColor: '#0284c7', width: 11, height: 11 }} />
                        </td>
                        <td style={{ padding: '1px 2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={a.activity_code}>{a.activity_code}</td>
                        <td style={{ padding: '1px 2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={a.activity_name || ''}>{a.activity_name && a.activity_name !== a.activity_code ? a.activity_name : '—'}</td>
                        <td style={{ padding: '1px 2px', whiteSpace: 'nowrap' }}>{a.start || '—'}</td>
                        <td style={{ padding: '1px 2px', whiteSpace: 'nowrap' }}>{a.finish || '—'}</td>
                        <td style={{ padding: '1px 2px', textAlign: 'right' }}>{formatNumber(a.budget_hours)}</td>
                        <td style={{ padding: '1px 2px', textAlign: 'right' }}>{formatNumber(a.actual_hours)}</td>
                        <td style={{ padding: '1px 2px', textAlign: 'right' }}>{formatNumber(a.remaining_hours ?? a.hours)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              )}
            </PaneFilter>
          ));
          addFilter(panelOn('family'), 'KPI family', (
            <PaneFilter key="family" label="KPI family" summary={familySummary} active={selectedFamilies.length !== PBIX_FAMILIES.length}>
              <FilterCheckList
                items={PBIX_FAMILIES.map((f) => ({ id: f.id, label: f.label }))}
                selectedIds={selectedFamilies}
                onToggle={(id) => setSelectedFamilies((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id])}
                onSelectAll={() => setSelectedFamilies((prev) => prev.length === PBIX_FAMILIES.length ? [] : PBIX_FAMILIES.map((f) => f.id))}
                paint={(item) => slicerPaint(!weekFamilies || weekFamilies.has(item.id))}
              />
            </PaneFilter>
          ));
          addFilter(panelOn('scope'), SCOPE_SLICER_LABEL, (
            <PaneFilter key="scope" label={SCOPE_SLICER_LABEL} summary={scopeSummary} active={progressScopes.length !== PROGRESS_SCOPES.length}>
              <FilterCheckList
                items={PROGRESS_SCOPES.map((s) => ({ id: s.id, label: s.label }))}
                selectedIds={progressScopes}
                onToggle={(id) => setProgressScopes((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id])}
                search={false}
              />
            </PaneFilter>
          ));
          actualsSlicers.filter((s) => panelOn(s.id)).forEach((box) => {
            const opts = filterOptions[box.id] || [];
            const selected = selectedFilters[box.id] || [];
            addFilter(true, box.label, (
              <PaneFilter
                key={box.id}
                label={box.label}
                summary={pickCountSummary(selected, opts, selected[0])}
                active={selected.length > 0 && selected.length !== opts.length}
              >
                <FilterCheckList
                  items={opts.map((opt) => ({ id: opt, label: opt }))}
                  selectedIds={selected}
                  onToggle={(id) => toggleActualsFilter(box.id, id)}
                  onSelectAll={() => selectAllActualsFilter(box.id, opts)}
                  empty="No values on this job yet."
                />
              </PaneFilter>
            ));
          });

          return (
            <>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          padding: '8px 10px',
          borderBottom: '1px solid #e2e8f0',
          flexShrink: 0
        }}>
          <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.4, textTransform: 'uppercase', color: '#334155' }}>
            Filters
          </div>
          <span style={{ flex: 1 }} />
          {!pdfPanelsOverride && (
            <>
              <div ref={filterPickerRef}>
                <button
                  type="button"
                  onClick={() => setShowFilterPicker((v) => !v)}
                  style={{ ...paneChipBtn, background: showFilterPicker ? '#e2e8f0' : '#fff' }}
                >
                  + Add slicer {showFilterPicker ? '▴' : '▾'}
                </button>
                <AnchoredPopover
                  open={showFilterPicker}
                  anchorRef={filterPickerRef}
                  width={WIDE_PANE_POPOVER}
                  align="right"
                  maxHeight={380}
                  zIndex={90}
                  onClose={() => setShowFilterPicker(false)}
                >
                  <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#0f172a', marginBottom: 2 }}>
                    Add a slicer
                  </div>
                  <div style={{ fontSize: '0.62rem', color: '#64748b', marginBottom: 6, lineHeight: 1.35 }}>
                    Earned, FAC, calendars, safety, and spent stay under Settings.
                    Check a row to pin it on this drawer. Uncheck to hide it.
                  </div>
                  <input
                    type="search"
                    value={slicerPickerSearch}
                    onChange={(e) => setSlicerPickerSearch(e.target.value)}
                    placeholder="Search columns / UDFs…"
                    style={{
                      width: '100%', boxSizing: 'border-box', height: 26, marginBottom: 8,
                      border: '1px solid #cbd5e1', borderRadius: 4, padding: '0 8px', fontSize: '0.72rem'
                    }}
                  />
                  {PICKER_GROUPS.map((group) => {
                    const q = slicerPickerSearch.trim().toLowerCase();
                    const rows = FILTER_PANEL_DEFS.filter((f) => f.group === group).filter((f) => {
                      if (SETTINGS_SLICER_IDS.includes(f.id)) return false;
                      if (!q) return true;
                      return [f.label, f.id, f.column].filter(Boolean)
                        .some((x) => String(x).toLowerCase().includes(q));
                    }).sort((a, b) => String(a.label).localeCompare(String(b.label), undefined, { sensitivity: 'base' }));
                    if (!rows.length) return null;
                    return (
                    <div key={group} style={{ marginBottom: 8 }}>
                      <div style={{ fontSize: '0.62rem', fontWeight: 700, color: '#64748b', letterSpacing: '0.04em', marginBottom: 3 }}>
                        {group.toUpperCase()}
                        <span style={{ fontWeight: 500, marginLeft: 6, letterSpacing: 0 }}>{rows.length}</span>
                      </div>
                      {rows.map((f) => (
                        <label key={f.id} style={{
                          display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.75rem',
                          padding: '0.22rem 0.15rem', cursor: 'pointer', color: '#334155'
                        }}>
                          <input
                            type="checkbox"
                            checked={isSlicerVisible(visibleFilterPanels, f.id, pickerDefaultOn(f))}
                            onChange={() => {
                              setPanelVisible(f.id, !isSlicerVisible(visibleFilterPanels, f.id, pickerDefaultOn(f)));
                            }}
                            style={{ accentColor: '#475569', width: 13, height: 13 }}
                          />
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={f.id}>
                            {f.label}
                          </span>
                        </label>
                      ))}
                    </div>
                    );
                  })}
                </AnchoredPopover>
              </div>
            </>
          )}
          <button
            type="button"
            onClick={() => {
              setShowFilters(false);
              setShowFilterPicker(false);
              try { localStorage.setItem('evm-show-filters-v2', 'false'); } catch { /* ignore */ }
            }}
            style={paneChipBtn}
          >
            Close
          </button>
        </div>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 0,
          minWidth: 0,
          padding: '8px 10px 12px',
          overflowY: 'auto',
          overflowX: 'hidden',
          flex: 1
        }}>
          {sortFiltersByLabel(paneFilters).map((row) => row.node)}
          {!pdfPanelsOverride && (
            <button type="button" onClick={clearFilters} style={{ ...paneChipBtn, marginTop: 8, alignSelf: 'flex-start' }}>
              Clear
            </button>
          )}
        </div>
            </>
          );
        })()}
        </div>
      </aside>
      {!showFilters && (
      <button
        type="button"
        onClick={() => { setShowFilters(true); try { localStorage.setItem('evm-show-filters-v2', 'true'); } catch { /* ignore */ } }}
        title="Open filters"
        style={{
          position: 'absolute',
          right: 0,
          top: 12,
          zIndex: 20,
          width: 28,
          border: '1px solid #cbd5e1',
          background: '#f8fafc',
          borderRadius: '6px 0 0 6px',
          cursor: 'pointer',
          fontSize: 11,
          fontWeight: 800,
          color: '#334155',
          letterSpacing: 0.6,
          writingMode: 'vertical-rl',
          transform: 'rotate(180deg)',
          padding: '10px 4px'
        }}
      >
        Filters
      </button>
      )}
      </div>

      {/* Help (LGL red) + Test your knowledge */}
      {showHelp && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Resource Control Help"
          style={{
            position: 'fixed', inset: 0, zIndex: 1300,
            background: 'rgba(15, 23, 42, 0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '1.25rem'
          }}
          onClick={() => setShowHelp(false)}
        >
          <div
            style={{
              background: '#ffffff',
              borderRadius: '16px', width: 'min(920px, 100%)',
              maxHeight: '90vh', overflow: 'auto',
              boxShadow: '0 30px 60px rgba(0,0,0,0.35)',
              border: '2px solid #dc2626'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '1rem 1.35rem',
              background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
              color: 'white', position: 'sticky', top: 0, zIndex: 1
            }}>
              <div>
                <div style={{ fontWeight: 800, fontSize: '1.15rem', letterSpacing: '0.01em' }}>
                  Resource Control
                </div>
                <div style={{ fontSize: '0.78rem', opacity: 0.95 }}>Histogram · Staffing · KPIs · Progress — one guide</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <ListenButton compact guideId="evm-report-help" title="Listen" accent="#b91c1c" dark />
                <button
                  type="button"
                  onClick={() => setShowHelp(false)}
                  style={{
                    border: '2px solid rgba(255,255,255,0.5)', background: 'rgba(0,0,0,0.15)', color: 'white',
                    borderRadius: '999px', padding: '0.4rem 0.9rem', fontWeight: 800, cursor: 'pointer'
                  }}
                >
                  Done ✓
                </button>
              </div>
            </div>
            <div style={{ padding: '1.25rem 1.35rem 1.5rem' }}>
              <HelpWithVideoTour guideId="evm-report-help" title="Resource Control Help" accent="#b91c1c">
              <div style={{
                display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '0.75rem'
              }}>
                {[
                  {
                    badge: '1', color: '#1d4ed8', title: 'Read the curves',
                    body: 'Histogram = Spent (Maestro) · Earned (P6) · Proposal (BAC) · Forecast (earned + remaining after data date). Under Curve, tick Bars, Lines, or both. Tick EVM marks for the Data Date, BAC, EAC, SV, CV, ETC, and VAC on this chart.'
                  },
                  {
                    badge: '2', color: '#0d9488', title: 'Unlock Budget Baseline',
                    body: 'Top bar Settings → Budget Hours Baseline for shop/field hours by discipline. Estimation vs Execution — no separate menu.'
                  },
                  {
                    badge: '3', color: '#7c3aed', title: 'KPI power-ups',
                    body: 'Cards show PMI classics: BAC, PV, EV, AC, SV, CV, SPI, CPI, FAC, ETC, VAC, TCPI. KPI family (Overall / Mech / Pipe / Elec / Indirect / Pipe Fab) is the old Power BI Resource Control slicer — it only slices these cards, not every histogram trade. HVAC / civil / structural stay under Overall. Leave all families ticked unless you are matching an old PBIX slide.'
                  },
                  {
                    badge: '4', color: '#ea580c', title: 'Man-weeks = hours ÷ calendar',
                    body: 'Default view is man-weeks. Each resource divides by its OWN P6 calendar. What-if ranges (Settings) answer “what if Electrical works 4x10 for the rest of the job?” — hours stay the same; men change. Electrical Extra inherits Electrical unless you set it separately. Whole-program 4x10 vs 5x10 is a new P6 scenario file, not this tool. Goodyear Manual only: PBIX calendars 60 then 50 from 12 Apr; Electrical 40 from 18 May (remove when 75998 is done).'
                  },
                  {
                    badge: '5', color: '#0369a1', title: 'Maestro Saturday → P6 Sunday',
                    body: 'Spent Date is week-ending (almost always Saturday). The plot is P6 week-starting Sunday. Saturday 8/1 hours sit on Sunday 8/2. Saturday 8/8 sits on Sunday 8/9. Tooltip shows both.'
                  },
                  {
                    badge: '6', color: '#0f766e', title: 'Staffing = P6 extracts + your hiring plan',
                    body: 'The Staffing tab is the chart and the six tables. Peaks, data date, and controls live under Filters → Staffing plan. Method lives in Help. Blue earned and green remaining meet on the data-date week. Manpower = Hrs ÷ each resource’s own P6 calendar.'
                  },
                  {
                    badge: '7', color: '#0369a1', title: 'Progress is one column per XER week',
                    body: 'Each column is that week’s own XER (earned ÷ that file’s budget). This week = this cumulative minus last week. Past Sundays stay frozen; Current only moves the latest week. Pick a dated XER and the table is as-of that file only.'
                  }
                ].map((m) => (
                  <div key={m.badge} style={{
                    background: 'white', borderRadius: 16, padding: '1rem',
                    border: `2px solid ${m.color}33`,
                    boxShadow: `0 4px 14px ${m.color}22`,
                    position: 'relative'
                  }}>
                    <div style={{
                      position: 'absolute', top: -10, left: 14,
                      width: 28, height: 28, borderRadius: '50%',
                      background: m.color, color: 'white',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontWeight: 900, fontSize: '0.85rem',
                      boxShadow: '0 2px 6px rgba(0,0,0,0.2)'
                    }}>
                      {m.badge}
                    </div>
                    <div style={{ fontWeight: 800, color: m.color, marginTop: 6, marginBottom: 6, fontSize: '0.95rem' }}>
                      {m.title}
                    </div>
                    <div style={{ fontSize: '0.84rem', color: '#475569', lineHeight: 1.45 }}>{m.body}</div>
                  </div>
                ))}
              </div>
              <div style={{
                marginTop: '1rem', padding: '0.85rem 1rem', borderRadius: 12,
                background: 'linear-gradient(90deg, #ecfdf5, #eff6ff)',
                border: '1px dashed #94a3b8', fontSize: '0.8rem', color: '#334155'
              }}>
                <strong>The top row is the whole menu.</strong> Histogram · Staffing · KPIs · Progress, plus Filters and
                Figures (the KPI cards). Everything except that row and the curve folds away. Legend click spotlights a series.
              </div>

              <div style={{
                marginTop: '1rem', padding: '1rem', borderRadius: 14,
                background: '#f0fdfa', border: '2px solid #99f6e4'
              }}>
                <div style={{ fontWeight: 800, color: '#0f766e', marginBottom: '0.5rem' }}>📡 Where the numbers come from</div>
                <div style={{ fontSize: '0.84rem', color: '#115e59', lineHeight: 1.5 }}>
                  <div><strong>Spent (AC):</strong> Maestro labour actuals (hours burned). Date is week-ending Saturday — plotted on the next P6 Sunday.</div>
                  <div><strong>Earned (EV):</strong> pick the mode in the filter pane — full breakdown in the Earned source box below.
                    {analytics?.meta?.earnedSource ? ` Active: ${analytics.meta.earnedSource}` : ''}
                    {analytics?.meta?.dataSources?.earned ? ` — ${analytics.meta.dataSources.earned}` : ''}.</div>
                  <div><strong>Budget (PV / BAC):</strong> the blue curve is always the baseline plan (proposal XER) — extras never draw budget bars. The KPIs budget whatever scope you filtered: All = baseline + extras priced from Current P6, Baseline = proposal only, Extra = Current EX target hours. Extra is not a P6 flag: EX token on the resource ID (25LG0311-PI-EX), or Extra / Additional Scope in the name. Fab vs Field is not a P6 column either: Fab in the resource name = fab, everything else = field.
                    {analytics?.meta?.dataSources?.plannedKpi ? ` — KPI budget: ${analytics.meta.dataSources.plannedKpi}` : (analytics?.meta?.dataSources?.planned ? ` — ${analytics.meta.dataSources.planned}` : '')}.</div>
                  <div><strong>Remaining / Forecast:</strong> remaining work from the Current P6 pick — Auto is the latest weekly with activities; check two or more XERs to add their hours (empty shells stay in the list). A file with no Discipline activity codes still adds — suffix/name classify, so the other file’s Discipline list cannot hide it. Bars are P6 Resource Assignments as-is at the Group By grain: Day / Week / Month are the same daily cells (qty_per_hr × calendar). Month is a calendar month, not a sum of Sunday weeks. Stored Summarize cells win when fresh; otherwise the cells are rebuilt from the planned rate and calendar. Forecast = earned to data date + that remaining. The data date is P6’s own Data Date for the picked copy (the Projects layout column), not the date in the file name. <strong>Legend by</strong> (Filters): one dropdown — <em>One colour</em>, <em>Discipline</em>, <em>Resource</em>, any <em>P6 column</em>, the Accounting columns (op code, description, coding group, cost type), or any Time Tracker / employee column that is already a slicer. Budget, Remaining, and Earned split on the P6 columns — Earned is the same Resource Assignments as Remaining. Spent splits on those when the operation code maps through the Accounting coding structure, and splits directly on an Accounting or Time Tracker column. A column a curve does not carry stays one colour. The split never changes the week total. Week total always on top. Tick <em>Counts on bars</em> under Filters → Units. Earned source, calendars, and staffing plans live under Filters → Settings. Legend: click the name to spotlight, click the square to change colour; hide a trade in Filters → Resources.</div>
                  <div><strong>Programs / what-if scenarios:</strong> name P6 files <code>{'{estimate}'}-A-4X10</code> (package letter + crew calendar, fits P6's 20-char ID) or end the project <em>name</em> with a tag like <code>A4x10</code> — the picker groups by package, shows the calendar instead of a date, and the <em>Scenario</em> chips check that calendar for every package in one click. One calendar per package (double-checks warn — hours would double-count). Real weekly updates keep <code>{'{estimate}'}-YYMMDD</code>; with no weekly, Auto assembles the base program (lowest calendar every package has). <strong>Manpower follows the real P6 calendar:</strong> weekly hours stay the same across calendars, but men = hours ÷ the crew's person-week, read from each file's <em>own P6 calendar day patterns</em> (a file named 6x10 on a real 4x10 calendar divides by 40). P6's week_hr_cnt setting is ignored — it is a display conversion, not the working week. The scenario name only fills in when the calendar has no usable pattern. Explicit What-if ranges still win. Full convention: Admin → Settings → Nomenclature.</div>
                  <div><strong>Staffing Plan:</strong> P6 extracts per resource — Baseline vs Earned + Remaining, hours and manpower (÷ each resource’s P6 calendar), overridable Hiring/layoff. Blue and green share the data-date week so the crew line is continuous.</div>
                  <div style={{ marginTop: 8 }}><strong>P6 resource names (standard from now on):</strong> ID = <code>{'{estimateKey}'}-{'{TRADE}'}[-FAB][-EX]</code>. Name = trade words + Fabrication when it is shop + Extra or Additional Scope when it is extra. Tokens: PI piping · PF pipe fab · EI electrical · MW mechanical · ST structural · HV HVAC · DU ducts · CV civil · BM boilermaker · TK tank · IN / IN-S / IN-F indirect. Discipline UDF should use the same words as the name. Suffix map is only the fallback when that UDF is missing.</div>
                </div>
              </div>

              <div style={{
                marginTop: '1rem', padding: '1rem', borderRadius: 14,
                background: '#f0fdfa', border: '2px solid #5eead4'
              }}>
                <div style={{ fontWeight: 800, color: '#0f766e', marginBottom: '0.65rem' }}>
                  Staffing Plan
                </div>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 0, marginBottom: 12,
                  background: 'white', border: '1px solid #99f6e4', borderRadius: 12, padding: '0.7rem 0.9rem'
                }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ height: 8, background: 'linear-gradient(90deg, #93c5fd, #2563eb)', borderRadius: '8px 0 0 8px' }} />
                    <div style={{ fontSize: '0.68rem', fontWeight: 800, color: '#2563eb', marginTop: 4 }}>Blue · earned</div>
                  </div>
                  <div style={{
                    width: 18, height: 18, borderRadius: 99, background: '#eab308',
                    border: '3px solid #fff', boxShadow: '0 0 0 2px #eab308', flex: 'none', zIndex: 1
                  }} title="Data date" />
                  <div style={{ flex: 1 }}>
                    <div style={{ height: 8, background: 'linear-gradient(90deg, #16a34a, #86efac)', borderRadius: '0 8px 8px 0' }} />
                    <div style={{ fontSize: '0.68rem', fontWeight: 800, color: '#16a34a', marginTop: 4, textAlign: 'right' }}>Green · remaining</div>
                  </div>
                </div>
                <div style={{ fontSize: '0.8rem', color: '#115e59', marginBottom: 10, lineHeight: 1.45 }}>
                  They meet on the yellow data-date week — same crew count, then green continues the forecast.
                  A drop at that week is a real layoff (red bar underneath), not a broken line.
                </div>
                <StaffingHelpBody compact />
              </div>

              <div style={{
                marginTop: '1rem', padding: '1rem', borderRadius: 14,
                background: '#fffbeb', border: '2px solid #fcd34d'
              }}>
                <div style={{ fontWeight: 800, color: '#92400e', marginBottom: '0.5rem' }}>
                  ⚖️ Earned source — the four modes (filter pane)
                </div>
                <div style={{ display: 'grid', gap: '0.5rem' }}>
                  {[
                    {
                      mode: 'As-is', color: '#475569',
                      what: 'P6\u2019s own Resource Assignments cells at Day / Week / Month (same daily spread — month is a calendar month, not summed weeks). When you run Tools \u2192 Summarize in P6, the portal reads P6\u2019s stored spread directly. If the schedule changed since the last Summarize, it rebuilds qty_per_hr \u00D7 calendar (planned rate, not an even dump). Run Summarize after each weekly update to stay exact.'
                    },
                    {
                      mode: 'Temp', color: '#b45309',
                      what: 'Weekly delta: this week\u2019s dated P6 Actual Units total minus the previous weekly XER (e.g. 20260816 minus 20260809). Current is the last point after the newest dated XER. Needs a weekly XER upload to stay accurate.'
                    },
                    {
                      mode: 'Perm', color: '#6d28d9',
                      what: 'Stored financial periods (TRSRCFIN) — P6\u2019s own \u201CStore Period Performance\u201D history. Only as fresh as the last stored period, so run Store Period Performance in P6 every week to keep it current.'
                    },
                    {
                      mode: 'Manual', color: '#0f766e',
                      what: 'The leveled Power BI Resource Histogram dump used on Goodyear presentations (job 75998). Dump weeks stay as stored. Closed weeks after the last dump Sunday come from the selected P6 (Current as of this Sunday \u2192 earned on the Sunday before). Remaining after those closed weeks stays the dump. Other jobs stay on As-is unless someone uploads a dump. Never the default.'
                    }
                  ].map((r) => (
                    <div key={r.mode} style={{
                      display: 'flex', gap: '0.6rem', alignItems: 'flex-start',
                      background: 'white', borderRadius: 10, padding: '0.6rem 0.75rem',
                      border: '1px solid #fde68a'
                    }}>
                      <span style={{
                        flex: 'none', fontWeight: 900, fontSize: '0.74rem', color: 'white',
                        background: r.color, borderRadius: 999, padding: '0.2rem 0.6rem', marginTop: 1
                      }}>
                        {r.mode}
                      </span>
                      <span style={{ fontSize: '0.8rem', color: '#44403c', lineHeight: 1.5 }}>{r.what}</span>
                    </div>
                  ))}
                </div>
                <div style={{ fontSize: '0.76rem', color: '#78716c', marginTop: '0.6rem', lineHeight: 1.5 }}>
                  <strong>Tools → Summarize:</strong> P6 stores its Resource Assignments spread (earned, remaining
                  and targets) only when Summarize runs. P6 Professional has no background service for it, so it is
                  a manual step — once a week, ~30 seconds, right after the weekly update <em>and after any
                  reschedule</em>: import XER → schedule → Store Period Performance → Summarize. Miss it and nothing
                  breaks: the portal compares the stored cells with the live units. Earned must match live actuals
                  exactly. For remaining and budget, if the stored total differs from live units and anything in the
                  project was edited <em>after</em> the last Summarize, the stored cells are stale and the portal
                  rebuilds from the live units instead — so a scheduler changing units without summarizing never
                  leaves the old number on screen. If Summarize ran after the last edit, the stored cells stand even
                  when they differ (P6 Effort there often follows the assigned baseline — 256k on 71940 — not live
                  At Completion). The header line shows which one you are
                  looking at: <span style={{ color: '#059669', fontWeight: 700 }}>P6 cells: exact</span> vs{' '}
                  <span style={{ color: '#b45309', fontWeight: 700 }}>rebuilt — run Summarize for exact</span>, and
                  <strong> P6 read</strong> is the time the portal last read this job from P6 — press Refresh to read
                  it again now. Summarize the proposal XER once too and the blue Budget curve becomes P6-exact as well.
                </div>
              </div>

              <div style={{ marginTop: '1.15rem' }}>
                <div style={{ fontWeight: 900, fontSize: '1rem', color: '#1e3a8a', marginBottom: '0.65rem' }}>
                  📘 KPI pocket guide (for a new PM)
                </div>
                <div style={{ display: 'grid', gap: '0.55rem' }}>
                  {[
                    { t: 'BAC — Budget at Completion', d: 'Budget hours for the scope you filtered. All (default) = proposal baseline + extras priced from Current P6 (EV earns extras, so BAC must budget them). Baseline-only = proposal. Extra-only = Current EX target hours. The blue Budget curve on the chart always stays baseline-only — extras never add budget bars. An XER Sunday is the remaining week; EV, PV, and AC are the curve totals through the Sunday before (Cum. Earned / Cum. Budget / Cum. Spent). Turn Earned on to see EV on the histogram.' },
                    { t: 'PV — Planned Value', d: 'How many of those budget hours we planned to have earned by today (the S-curve to the data date).' },
                    { t: 'EV — Earned Value', d: 'Hours of work actually completed / credited so far. Progress converted into hours.' },
                    { t: 'AC — Actual Cost (Spent)', d: 'Hours really burned in the field/shop (Maestro). Money/hours already spent.' },
                    { t: 'SV — Schedule Variance (EV − PV)', d: 'Positive = ahead of the plan; negative = behind. Measured in hours of work, not calendar days.' },
                    { t: 'CV — Cost Variance (EV − AC)', d: 'Positive = more progress than hours spent (efficient); negative = burning faster than progress.' },
                    { t: 'SPI — Schedule Performance Index (EV ÷ PV)', d: '1.0 = on plan. Above 1 = ahead; below 1 = behind. Cum = whole job to date; Per = that week only.' },
                    { t: 'CPI — Cost Performance Index (EV ÷ AC)', d: '1.0 = getting one hour of progress per hour spent. Below 1 = overrunning labour.' },
                    { t: 'FAC / EAC — Forecast at Completion', d: 'Pick the formula in the header. Default is typical CPI (BAC ÷ CPI). Other options: atypical AC+(BAC−EV), CPI×SPI, SPI only, P6 remaining, P6 remaining÷CPI, earned+P6 remaining, or on-plan FAC=BAC.' },
                    { t: 'ETC — Estimate to Complete', d: 'Hours still needed from now to finish (FAC − AC), using the same formula you selected.' },
                    { t: 'VAC — Variance at Completion (BAC − FAC)', d: 'Positive = expect to underrun the budget; negative = expect to overrun.' },
                    { t: 'TCPI — To-Complete Performance Index', d: 'How efficient remaining work must be to still hit BAC. Much above 1 = recovery is hard — talk about it early.' },
                    { t: '% Complete', d: 'EV ÷ BAC. Share of the budgeted scope finished so far.' }
                  ].map((row) => (
                    <div key={row.t} style={{
                      background: 'white', borderRadius: 12, padding: '0.7rem 0.85rem',
                      border: '1px solid #e2e8f0'
                    }}>
                      <div style={{ fontWeight: 800, color: '#0f172a', fontSize: '0.84rem' }}>{row.t}</div>
                      <div style={{ fontSize: '0.8rem', color: '#475569', marginTop: 3, lineHeight: 1.45 }}>{row.d}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div style={{
                marginTop: '1.35rem', padding: '1rem', borderRadius: 12,
                background: '#fafafa', border: '1px solid #e5e7eb'
              }}>
                <div style={{ fontWeight: 800, fontSize: '1rem', color: '#b91c1c', marginBottom: '0.35rem' }}>
                  Test your knowledge
                </div>
                <div style={{
                  fontSize: '0.78rem', color: '#64748b', marginBottom: '0.85rem', lineHeight: 1.45,
                  padding: '0.55rem 0.7rem', background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 8
                }}>
                  <strong>Just for fun.</strong> Points only motivate learning about the portal.
                  They do <em>not</em> affect your role, permissions, pay, or performance reviews.
                  You will also see them on your profile — same disclaimer there.
                </div>
                {activeQuiz.map((item, qi) => (
                  <div key={item.id} style={{ marginBottom: '0.85rem' }}>
                    <div style={{ fontWeight: 600, fontSize: '0.84rem', color: '#0f172a', marginBottom: 6 }}>
                      {qi + 1}. {item.q}
                    </div>
                    {item.choices.map((c, ci) => (
                      <label key={ci} style={{
                        display: 'flex', alignItems: 'flex-start', gap: '0.45rem',
                        fontSize: '0.8rem', color: '#334155', marginBottom: 4, cursor: 'pointer'
                      }}>
                        <input
                          type="radio"
                          name={item.id}
                          checked={Number(quizAnswers[item.id]) === ci}
                          onChange={() => setQuizAnswers((prev) => ({ ...prev, [item.id]: ci }))}
                          style={{ accentColor: '#dc2626', marginTop: 2 }}
                        />
                        {c}
                      </label>
                    ))}
                  </div>
                ))}
                <button
                  type="button"
                  disabled={quizSubmitting || Object.keys(quizAnswers).length < activeQuiz.length}
                  onClick={submitEvmQuiz}
                  style={{
                    ...btnBase, background: '#dc2626', color: 'white', border: 'none',
                    height: 36, fontWeight: 700,
                    opacity: (quizSubmitting || Object.keys(quizAnswers).length < activeQuiz.length) ? 0.5 : 1
                  }}
                >
                  {quizSubmitting ? 'Saving…' : 'Check answers & earn fun points'}
                </button>
                <button
                  type="button"
                  onClick={refreshQuiz}
                  style={{ ...btnBase, height: 36, marginLeft: 8, fontSize: '0.78rem' }}
                >
                  New questions
                </button>
                {quizResult && (
                  <div style={{ marginTop: '0.75rem', fontSize: '0.84rem', color: '#0f172a' }}>
                    Score: <strong>{quizResult.score}/{quizResult.maxScore}</strong>
                    {quizResult.awarded != null ? ` · +${quizResult.awarded} fun points` : ''}
                    <div style={{ marginTop: 4, fontSize: '0.75rem', color: '#64748b' }}>{quizResult.disclaimer}</div>
                    {quizResult.error && <div style={{ color: '#b91c1c', marginTop: 4 }}>{quizResult.error}</div>}
                  </div>
                )}
              </div>
              </HelpWithVideoTour>
            </div>
          </div>
        </div>
      )}

      <SafetyPlanModal
        open={safetyPlanOpen}
        onClose={() => setSafetyPlanOpen(false)}
        crew={safetyPlanCrew}
        safetyRule={safetyRule}
        onSafetyRule={(patch) => {
          if (patch.mode) changeSafetyRuleMode(patch.mode);
          if (patch.menPer != null) changeSafetyMenPer(patch.menPer);
          if (patch.firstAt != null) changeSafetyFirstAt(patch.firstAt);
          if (patch.thenEvery != null) changeSafetyThenEvery(patch.thenEvery);
          if (patch.finalEvery != null) changeSafetyFinalEvery(patch.finalEvery);
        }}
        source={activeTab === 'staffing' ? 'staffing' : 'histogram'}
        cfg={safetyCfg}
        onCfg={patchSafetyCfg}
        jobNumber={jobNumber}
      />

      <SupervisionPlanModal
        open={supervisionPlanOpen}
        onClose={() => setSupervisionPlanOpen(false)}
        weeks={supervisionStaffingModel?.weeks || []}
        resources={supervisionStaffingModel?.resources || []}
        menByResource={supervisionMenBy}
        jobNumber={jobNumber}
        menPerFm={menPerFm}
        fmPerGf={fmPerGf}
        onMenPerFm={changeMenPerFm}
        onFmPerGf={changeFmPerGf}
        cfg={superCfg}
        onPatchCfg={patchSuperCfg}
      />

      <IndirectExtrasModal
        open={extrasPlanOpen}
        onClose={() => setExtrasPlanOpen(false)}
        jobNumber={jobNumber}
        cfg={extrasCfg}
        onCfg={setExtrasCfg}
        projectMonths={extrasMonths.length}
        months={extrasMonths}
      />

      <HistogramPdfModal
        open={pdfOpen}
        jobNumber={jobNumber}
        defaultTitle={pdfTitle}
        cardOptions={pdfCardOptions}
        options={pdfOptions}
        busy={pdfBusy}
        onCancel={() => setPdfOpen(false)}
        onDownload={(next) => {
          setPdfOptions(next);
          handleDownloadPdf(next);
        }}
      />

      {/* Budget Hours Baseline popup (former Budget Report) */}
      {showBudgetBaseline && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Budget Hours Baseline"
          style={{
            position: 'fixed', inset: 0, zIndex: 1200,
            background: 'rgba(15, 23, 42, 0.55)',
            display: 'flex', alignItems: 'stretch', justifyContent: 'center',
            padding: '1.25rem'
          }}
          onClick={() => setShowBudgetBaseline(false)}
        >
          <div
            style={{
              background: 'white', borderRadius: '14px', width: 'min(1200px, 100%)',
              maxHeight: '100%', overflow: 'auto', boxShadow: '0 25px 50px rgba(0,0,0,0.35)',
              display: 'flex', flexDirection: 'column'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              position: 'sticky', top: 0, zIndex: 2,
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '0.85rem 1.25rem',
              background: 'linear-gradient(135deg, #1e3a8a 0%, #0369a1 100%)',
              color: 'white'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem' }}>
                <span style={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  width: 36, height: 36, borderRadius: 10,
                  background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.25)'
                }}>
                  <FistIcon size={22} color="#fff" />
                </span>
                <div>
                  <div style={{ fontWeight: 800, fontSize: '1.05rem' }}>Budget Hours Baseline</div>
                  <div style={{ fontSize: '0.78rem', opacity: 0.9 }}>
                    Estimation &amp; Execution hours by discipline — opened from EVM (no separate menu)
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <ListenButton compact guideId="budget-report-help" title="Budget Baseline Help" accent="#fff" dark />
                <button
                  type="button"
                  onClick={() => setShowBudgetBaseline(false)}
                  style={{
                    border: 'none', background: 'rgba(255,255,255,0.2)', color: 'white',
                    borderRadius: '8px', padding: '0.4rem 0.75rem', fontWeight: 700, cursor: 'pointer'
                  }}
                >
                  Close
                </button>
              </div>
            </div>
            <div style={{ padding: '0.5rem 0.75rem 1.5rem' }}>
              <BudgetReport jobNumber={jobNumber} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EVMReport;
